'use client'

import ConfirmModal from '@/components/confirm-modal'
import { profitMargin } from '@/lib/billing/financials'
import { TussSelector } from '@/components/tuss/TussSelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { SupplierSelector } from '@/components/SupplierSelector'
import ProductCategorySelector from '@/components/catalog/product-category-selector'
import {
    PRODUCT_DIMENSIONS,
    PRODUCT_ORIGINS,
    PRODUCT_UNITS,
} from '@/lib/catalog/product-fields'
import { ClientSelector } from '@/components/client/ClientSelector'
import { createOrder } from '@/lib/api/orders/order-api'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { HospitalSearchSelector } from '@/components/HospitalSearchSelector'
import {
    addCatalogExam,
    addCatalogMedicalSupply,
    addCatalogPartner,
    addCatalogProfessional,
    removeCatalogVariation,
    getCatalogVariations,
    Catalog,
    CATALOG_CATEGORY_LABELS,
    CatalogCategory,
    CatalogExam,
    CatalogMedicalSupply,
    CatalogPackageItem,
    type ComponentKind,
    COMPONENT_KIND_LABELS,
    PAYOUT_RECIPIENT_LABELS,
    addCatalogPackageItem,
    getCatalogPackageItems,
    getCatalogPackageCost,
    updateCatalogPackageItem,
    removeCatalogPackageItem,
    CatalogType,
    createCatalog,
    UpdateCatalogPartnerData,
    UpdateCatalogProfessionalData,
    createCatalogType,
    DEFAULT_DURATIONS,
    getCatalog,
    getCatalogByTuss,
    getCatalogsByCompany,
    getCatalogTypesByOrganization,
    removeCatalogExam,
    removeCatalogMedicalSupply,
    removeCatalogPartner,
    removeCatalogProfessional,
    updateCatalog,
    updateCatalogExam,
    updateCatalogMedicalSupply,
    updateCatalogPartner,
    updateCatalogProfessional,
    catalogCompanyPrice,
    catalogBasePrice,
    type CatalogCompanyPrice,
} from '@/lib/api/catalog/catalog-api'
import { Partner } from '@/lib/api/common/interfaces/partner.interface'
import { Professional } from '@/lib/api/common/interfaces/professional.interface'
import { Specialty } from '@/lib/api/common/interfaces/specialty.interface'
import {
    MEDICAL_SUPPLY_TYPE_LABELS,
    MedicalSupply,
    searchMedicalSupplies,
} from '@/lib/api/medical-supplies/medical-supplies-api'
import {
    createSpecialty,
    getSpecialtiesByOrganization,
} from '@/lib/api/specialties/specialties-api'
import { getSettingByName } from '@/lib/api/utils/settings-api'
import { getCatalogCostPolicy } from '@/lib/api/financial/financial-settings-api'
import { mask, unMask } from '@/lib/form-state/util'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useCatalogAccess } from '@/lib/hooks/use-catalog-access'
import {
    useCatalogCategoryLabels,
    useServiceLabel,
} from '@/lib/hooks/use-service-label'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import {
    getStoreConfigByCompany,
    getStoresByProduct,
    syncProductStores,
    type StoreConfig,
} from '@/lib/api/commerce/store-config'
import { useAppSelector, useAuth } from '@/store/hooks'
import { useCallback, useEffect, useRef, useState } from 'react'
import VariationCombinationsModal from './variation-combinations-modal'
import CatalogPromotionSection from './catalog-promotion-section'
import {
    MdAdd,
    MdArrowBack,
    MdAutoAwesome,
    MdCategory,
    MdDelete,
    MdInfo,
    MdPerson,
    MdSave,
    MdSell,
    MdLocationOn,
    MdScience,
    MdSearch,
    MdStorefront,
    MdCalculate,
    MdClose,
} from 'react-icons/md'
import { CostCalculatorModal } from '@/components/pricing/cost-calculator-modal'
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom'
import { toast } from 'react-toastify'
import RichTextEditor from '@/components/rich-text-editor'
import CatalogMediaSection, {
    type CatalogMediaSectionHandle,
} from '@/components/catalog/catalog-media-section'

// ==================== Utilitários de moeda ====================

const currencyFormat = (
    value: string | number | null | undefined,
    allowEmpty = false
) => {
    if (value === null || value === undefined) value = '0'
    const digits = `${value}`.replace(/[^0-9]+/g, '')
    if (
        allowEmpty &&
        (!digits || digits === '0' || digits === '00' || digits === '000')
    )
        return ''
    return mask(digits, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}

const parseCurrencyToCents = (value: string): number => {
    if (!value.trim()) return 0
    const unmaskedValue = unMask(value, 'currency')
    return parseInt(unmaskedValue)
}

const formatCentsToDisplay = (cents: number | null | undefined): string => {
    if (cents === null || cents === undefined || cents === 0) return ''
    return currencyFormat(cents.toString())
}

// ==================== Tipos auxiliares ====================

interface PartnerPricing {
    partnerId: number
    partnerName: string
    partnerLocation?: string
    price: string
    sellingPrice: string
    markupPercentage: string
    maxDiscount: string
    observations: string
    active: boolean
    savedId?: number // ID do CatalogPartner existente
}

interface ProfessionalPricing {
    professionalId: number
    professionalName: string
    price: string
    sellingPrice: string
    markupPercentage: string
    maxDiscount: string
    active: boolean
    savedId?: number // ID do CatalogProfessional existente
}

// Variação dinâmica do item de catálogo (precificação própria, igual ao item base)
interface VariationForm {
    savedId?: number // ID da CatalogVariation persistida
    name: string
    tussCode: string
    active: boolean
    sortOrder: number
    price: string
    sellingPrice: string
    markupPercentage: string
    maxDiscount: string
}

/**
 * Materiais de coleta sugeridos no cadastro do exame.
 *
 * É sugestão (`<datalist>`), não lista fechada: a relação real cresce por
 * especialidade — líquor, medula, lavado brônquico — e o médico precisa poder digitar
 * o que falta sem esperar um deploy. Ordenados pelo que mais aparece num pedido de
 * rotina, para o acerto ser no primeiro item da lista.
 */
const COLLECTION_MATERIALS = [
    'Sangue',
    'Urina',
    'Fezes',
    'Escarro',
    'Secreção',
    'Swab',
    'Saliva',
    'Líquor',
]

const CATEGORIES: CatalogCategory[] = [
    'CONSULTATION',
    //'TELECONSULTATION',
    'EXAM',
    'PROCEDURE',
    'PACKAGE',
    'PRODUCT',
]

// ==================== Componente principal ====================

// Props opcionais para reuso como MODAL (ex.: criar/editar uma VARIAÇÃO como item
// filho). Quando ausentes, o componente opera como PÁGINA (rota). Ver
// docs/catalog-variation-as-item-redesign.md §6.
export interface CatalogFormProps {
    modalId?: number // id do item em edição (modo modal)
    modalCategory?: CatalogCategory // categoria fixa (herdada do pai na variação)
    parentId?: number // quando presente, salva como variação-filha deste pai
    // Cria um item RAIZ novo em modo modal (não variação). Usado pelas abas
    // "Catálogo do Parceiro/Profissional" para atrelar um item novo à entidade.
    modalCreate?: boolean
    // Entidade pré-vinculada e TRAVADA (não pode trocar nem remover): já estamos
    // dentro dela. A seção correspondente fica sem seletor/remover, só o preço.
    lockedPartner?: { id: number; name: string; location?: string }
    lockedProfessional?: { id: number; name: string }
    // Recebe o id do item salvo, para o chamador poder destacá-lo/recarregá-lo.
    onSuccess?: (catalogId?: number) => void
    onCancel?: () => void
}

export default function CatalogFormPage(props: CatalogFormProps = {}) {
    const { user } = useAuth()
    const { canCreate, canUpdate } = useResourcePermissions(
        'registrations',
        'catalog'
    )
    const {
        allowedCategories,
        isCategoryAllowed,
        canSeeProfessionals,
        canSeePartners,
    } = useCatalogAccess()
    // Campos que só fazem sentido na saúde: código TUSS (Tabela 22 da ANS),
    // hospitais padrão e instruções do exame. O catálogo é agnóstico de segmento
    // — um salão cadastra o mesmo item que a clínica —, e esses três só poluiriam
    // o formulário. Todos são opcionais no backend.
    const isHealth = user?.company?.segment === 'HEALTH'
    const {
        Order: OrderLabel,
        Exam: ExamLabel,
        itemNamePlaceholder,
        variationNamePlaceholder,
        packageNamePlaceholder,
        variationHint,
        exam,
        exams,
        consultation,
        consultations,
        order,
        orders,
    } = useServiceLabel()
    const { clientLabel, ClientLabel } = useClientLabel()
    // Rótulo da categoria já traduzido (Consulta→Avaliação, Procedimento→Serviço).
    const categoryLabels = useCatalogCategoryLabels()
    const navigate = useNavigate()
    const location = useLocation()
    const { id: routeId } = useParams()
    const [searchParams] = useSearchParams()
    // Modo modal: props sobrepõem os hooks de rota. NUNCA cair no routeId em modo
    // modal (o routeId é o id do PAI, pois o modal é renderizado dentro da página
    // dele) — senão o modal carregaria o item pai em vez da variação.
    const isModal =
        props.parentId != null || props.modalId != null || !!props.modalCreate
    // TUSS duplicado: ao selecionar no modo criação um TUSS que já existe, o form
    // "vira" edição do item existente (editingExistingId sobrepõe o id de rota/modal).
    const [editingExistingId, setEditingExistingId] = useState<number | null>(
        null
    )
    // Banner "este item já existe" quando entramos em edição por duplicidade de TUSS.
    const [duplicateNotice, setDuplicateNotice] = useState(false)
    const id =
        editingExistingId != null
            ? String(editingExistingId)
            : isModal
              ? props.modalId != null
                  ? String(props.modalId)
                  : undefined
              : routeId
    const isEdit = !!id
    const isVariationMode = props.parentId != null
    // Categoria fixa quando herdada (variação) ou imposta pelo botão da aba de
    // entidade (modalCreate). Também travada em edição.
    const categoryLocked = isEdit || props.modalCategory != null
    // Seções de entidade travadas: a entidade dona da aba não pode ser trocada
    // nem removida (o item está sendo criado JÁ atrelado a ela).
    const lockPartnerSection = !!props.lockedPartner
    const lockProfessionalSection = !!props.lockedProfessional

    const initialCategory =
        props.modalCategory ||
        (searchParams.get('category') as CatalogCategory) ||
        'CONSULTATION'

    const [loading, setLoading] = useState(false)
    /** Calculadora do custo por atendimento, ao lado do campo de custo. */
    const [showCostCalculator, setShowCostCalculator] = useState(false)
    const [loadingData, setLoadingData] = useState(isEdit)
    const [savingBudgetOnly, setSavingBudgetOnly] = useState(false)
    const [category, setCategory] = useState<CatalogCategory>(initialCategory)

    // Categoria que o usuário não vê no menu (a padrão é Consulta, ou veio na URL)
    // cai na primeira liberada. Categoria travada (edição, variação) fica como está.
    useEffect(() => {
        if (categoryLocked || isCategoryAllowed(category)) return
        const firstAllowed = CATEGORIES.find(c => allowedCategories.includes(c))
        if (firstAllowed) setCategory(firstAllowed)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryLocked, category, allowedCategories])

    // Dados do formulário
    const [formData, setFormData] = useState({
        name: '',
        tussCode: '',
        description: '',
        typeId: null as number | null,
        price: '',
        sellingPrice: '',
        markupPercentage: '',
        maxDiscount: '',
        defaultDuration: '',
        collectionMaterial: '',
        active: true,
        // Apenas orçamento: bloqueia o agendamento direto do item.
        budgetOnly: false,
        // Procedimento cirúrgico. Só vale em PROCEDURE; a variação herda do pai.
        isSurgical: false,
        /**
         * Aparece no seletor do agendamento.
         *
         * Pacote NOVO nasce `false`: ele é vendido aqui mesmo, com data por componente —
         * aparecer na busca do agendamento levaria o operador ao formulário errado. Os
         * demais tipos nascem `true`. Ver `Catalog.schedulable`.
         */
        schedulable: true,
        // Consulta / Teleconsulta
        specialtyIds: [] as number[],
        // Hospitais padrão
        hospitalIds: [] as number[],
        // Exame
        instructions: '',
        // Procedimento
        preInstructions: '',
        postInstructions: '',
        // Procedimento/Exame
        anesthetistFee: '',
        // Teleconsulta
        maxTeleDuration: '',
        enableChat: false,
        enableScreenShare: false,
        recordingEnabled: false,
        // Produto (category = PRODUCT). Preço, parceiro e profissional NÃO entram
        // aqui: o produto usa o mesmo modelo de preço do serviço — é o que permite
        // revenda, dropshipping e produto de terceiro com margem própria.
        supplierId: null as number | null,
        productCategoryId: null as number | null,
        productSubcategoryId: null as number | null,
        sku: '',
        barcode: '',
        brand: '',
        unit: 'UNIT',
        productTags: '',
        ncm: '',
        cest: '',
        cfop: '',
        productOrigin: '',
        weight: '',
        width: '',
        height: '',
        depth: '',
        warranty: '',
        // Características ([{ key, value }]). Na variação, são os eixos da escolha na
        // loja: "Cor = Azul" e "Tamanho = M" viram dois seletores em vez de uma lista.
        // `color`: bolinha de cor da opção na loja (só nas variações). Ex.: Cor = Azul, #1E40AF.
        attributes: [] as { key: string; value: string; color?: string }[],
        stockEnabled: false,
        stockQuantity: '',
        minStock: '',
    })

    // Listas auxiliares
    const [catalogTypes, setCatalogTypes] = useState<CatalogType[]>([])
    const [specialties, setSpecialties] = useState<Specialty[]>([])

    // Type dropdown
    const [typeInput, setTypeInput] = useState('')
    const [showTypeDropdown, setShowTypeDropdown] = useState(false)

    // Specialty dropdown
    const [specialtySearch, setSpecialtySearch] = useState('')
    const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false)

    // Parceiros e profissionais vinculados
    const [partnerPricings, setPartnerPricings] = useState<PartnerPricing[]>([])
    const [professionalPricings, setProfessionalPricings] = useState<
        ProfessionalPricing[]
    >([])

    // Variações do item (novo modelo: filhas Catalog editadas via MODAL, que reusa
    // este próprio form). `variations` guarda a lista para exibição.
    const [variations, setVariations] = useState<VariationForm[]>([])
    // Modal de variação (reuso do CatalogForm em modo variação-filha).
    const [variationModalOpen, setVariationModalOpen] = useState(false)
    // "Adicionar variação" num item ainda não salvo: a variação é um item filho e
    // precisa do id do pai. O botão salva o item pelo mesmo submit do formulário
    // (validações iguais às do "Salvar") e marca este ref; o submit, em vez de voltar
    // para a lista, abre a edição do item recém-criado com o modal de variação aberto.
    const formRef = useRef<HTMLFormElement>(null)
    const openVariationAfterSaveRef = useRef(false)
    const [combinationsModalOpen, setCombinationsModalOpen] = useState(false)
    /**
     * Linha de preço GRAVADA da filial (venda e promoção). A promoção grava à parte e
     * confere contra o preço de venda salvo, não contra o que está digitado no formulário.
     */
    const [savedCompanyPrice, setSavedCompanyPrice] = useState<CatalogCompanyPrice | null>(
        null
    )
    const [editingVariationId, setEditingVariationId] = useState<number | null>(
        null
    )

    // Recarrega a lista de variações-filhas do item (após salvar no modal).
    const reloadVariations = async () => {
        if (!id) return
        const res = await getCatalogVariations(parseInt(id))
        if (res.success && res.result) {
            setVariations(
                (res.result as any[]).map(v => {
                    const cp = (v.companyPrices || []).find(
                        (p: any) => p.companyId === user?.companyId
                    )
                    return {
                        savedId: v.id,
                        name: v.name,
                        tussCode: v.tussCode || '',
                        active: v.active,
                        sortOrder: v.sortOrder ?? 0,
                        price: formatCentsToDisplay(cp?.price),
                        sellingPrice: formatCentsToDisplay(cp?.sellingPrice),
                        markupPercentage:
                            cp?.markupPercentage?.toString() || '',
                        maxDiscount: cp?.maxDiscount?.toString() || '',
                    }
                })
            )
        }
    }

    // Materiais médicos vinculados
    const [catalogMedicalSupplies, setCatalogMedicalSupplies] = useState<
        CatalogMedicalSupply[]
    >([])
    const [medicalSupplySearch, setMedicalSupplySearch] = useState('')
    const [medicalSupplyResults, setMedicalSupplyResults] = useState<
        MedicalSupply[]
    >([])
    const [showMedicalSupplyDropdown, setShowMedicalSupplyDropdown] =
        useState(false)
    const [loadingMedicalSupplies, setLoadingMedicalSupplies] = useState(false)

    // Exames relacionados (somente para PROCEDURE)
    const [catalogExams, setCatalogExams] = useState<CatalogExam[]>([])
    const [examSearch, setExamSearch] = useState('')
    const [examSearchResults, setExamSearchResults] = useState<Catalog[]>([])
    const [showExamDropdown, setShowExamDropdown] = useState(false)
    const [loadingExamSearch, setLoadingExamSearch] = useState(false)

    // Componentes do pacote (somente para PACKAGE). Cada componente é um item
    // de catálogo com TUSS (exame/procedimento/consulta). Ver docs/catalog-packages.md §6.
    const [packageItems, setPackageItems] = useState<CatalogPackageItem[]>([])
    const [packageSearch, setPackageSearch] = useState('')
    const [packageSearchResults, setPackageSearchResults] = useState<Catalog[]>(
        []
    )
    const [showPackageDropdown, setShowPackageDropdown] = useState(false)
    const [loadingPackageSearch, setLoadingPackageSearch] = useState(false)

    // Componente cuja EXECUÇÃO está aberta para edição (parceiro, profissional e valor do
    // profissional). Uma linha por vez: os três campos não cabem na tabela sem espremer as
    // colunas que o operador lê o tempo todo.
    /**
     * Veio para VENDER (botão da listagem), não para editar.
     *
     * Muda o título e rola até o bloco de venda: quem clicou em "Vender" não deveria
     * precisar procurar o formulário no meio do cadastro.
     */
    const sellingMode = searchParams.get('sell') === '1'

    useEffect(() => {
        if (!sellingMode) return
        // Espera o formulário terminar de carregar: rolar antes só sobe para o topo.
        const timer = setTimeout(() => {
            document
                .getElementById('sale-block')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 600)
        return () => clearTimeout(timer)
    }, [sellingMode])

    // === VENDA DO PACOTE ===
    // O pacote é vendido do próprio cadastro: não passa pelo formulário de agendamento.
    const [saleClientId, setSaleClientId] = useState<number | null>(null)
    const [saleDiscount, setSaleDiscount] = useState('')
    const [saleOverridePrice, setSaleOverridePrice] = useState('')
    /** Markup da venda: outro jeito de dizer o valor de venda, a partir do custo. */
    const [saleMarkup, setSaleMarkup] = useState('')

    const [selling, setSelling] = useState(false)
    /** Data/sala escolhidas por componente, chaveadas pelo id do vínculo. */
    const [componentSchedules, setComponentSchedules] = useState<
        Record<number, { scheduledAt?: string; arrivalOrder?: boolean }>
    >({})

    /**
     * Cria a venda e leva para a cobrança.
     *
     * Chama o MESMO service do agendamento (`POST /orders`), de propósito: é ele que
     * dispara notificação, e-mail, evento de agenda, deal no funil e as automações do
     * WhatsApp. Um caminho próprio para a venda seria uma cópia de todos esses fluxos, que
     * envelheceria em silêncio a cada um novo.
     */
    const handleSellPackage = async () => {
        if (!saleClientId || !id || !user?.companyId) return
        setSelling(true)
        try {
            const res = await createOrder({
                clientId: saleClientId,
                companyId: user.companyId,
                catalogId: parseInt(id),
                // Nasce orçamento: a cobrança é criada na aprovação.
                status: 'BUDGET',
                discount: saleDiscount ? parseInt(saleDiscount) : undefined,
                overridePrice: saleOverridePrice
                    ? parseCurrencyToCents(saleOverridePrice)
                    : undefined,
                componentSchedules: packageItems
                    .filter(
                        pi => pi.item && componentSchedules[pi.id]?.scheduledAt
                    )
                    .map(pi => ({
                        componentId: pi.id,
                        scheduledAt: componentSchedules[pi.id].scheduledAt,
                        arrivalOrder: componentSchedules[pi.id].arrivalOrder,
                    })),
            } as any)

            if (!res.success || !res.result) {
                toast.error(res.error || 'Erro ao vender o pacote')
                return
            }
            toast.success('Orçamento do pacote gerado')
            // O pacote nasce como ORÇAMENTO e vai para o detalhe do atendimento, onde
            // ficam "Aprovar" e "Aprovar flex". A cobrança sai na APROVAÇÃO — cheia no
            // fluxo normal, ou nenhuma no flex (o paciente gera cada pagamento no
            // portal). Emitir a cobrança já na venda tirava do operador a chance de
            // revisar com o paciente antes de mandar o boleto, e tornava o flex
            // inalcançável para pacote.
            navigate(`/bookings/orders/${res.result.id}`)
        } finally {
            setSelling(false)
        }
    }

    /** Tipo do componente que o operador está prestes a adicionar. */
    const [componentKind, setComponentKind] = useState<ComponentKind>('ITEM')
    /** Valor (custo) do componente novo — material, hospital ou anestesista. */
    const [newComponentAmount, setNewComponentAmount] = useState('')
    const [newComponentRecipientKind, setNewComponentRecipientKind] = useState<
        'SUPPLIER' | 'HOSPITAL' | 'PROFESSIONAL' | 'PARTNER' | null
    >(null)
    const [newComponentRecipientId, setNewComponentRecipientId] = useState<
        number | null
    >(null)
    const [newSupply, setNewSupply] = useState<{
        id: number
        name: string
        costPrice: number
    } | null>(null)

    /**
     * Acrescenta um componente de material, hospital ou anestesista.
     *
     * Em edição grava na hora; na criação fica no estado e é gravado no submit, como já
     * acontece com item e produto. O `label` é só para a lista enxergar um nome antes de o
     * backend devolver a linha com as relações.
     */
    const addPackageComponent = async (input: {
        kind: 'SUPPLY' | 'HOSPITAL' | 'ANESTHETIST'
        label: string
        medicalSupplyId?: number
        amount?: number
        recipientKind?: string
        recipientId?: number
    }) => {
        const payload = {
            kind: input.kind,
            medicalSupplyId: input.medicalSupplyId,
            amount: input.amount,
            recipientKind: input.recipientKind as any,
            recipientId: input.recipientId,
            quantity: 1,
            sortOrder: packageItems.length,
        }

        if (isEdit && id) {
            const res = await addCatalogPackageItem(parseInt(id), payload)
            if (!res.success) {
                toast.error(res.error || 'Erro ao adicionar componente')
                return
            }
            setPackageItems(prev => [...prev, res.result])
            toast.success('Componente adicionado')
        } else {
            setPackageItems(prev => [
                ...prev,
                {
                    id: 0,
                    packageId: 0,
                    kind: input.kind,
                    medicalSupplyId: input.medicalSupplyId ?? null,
                    medicalSupply: input.medicalSupplyId
                        ? { id: input.medicalSupplyId, name: input.label }
                        : null,
                    amount: input.amount ?? null,
                    recipientKind: (input.recipientKind ?? null) as any,
                    recipientId: input.recipientId ?? null,
                    quantity: 1,
                    complimentary: false,
                    sortOrder: prev.length,
                    createdAt: '',
                    updatedAt: '',
                } as CatalogPackageItem,
            ])
        }

        setNewComponentAmount('')
        setNewComponentRecipientKind(null)
        setNewComponentRecipientId(null)
        setNewSupply(null)
    }

    // Componente cuja EXECUÇÃO está aberta, pela POSIÇÃO na lista — não pelo id: na criação
    // do pacote os componentes ainda não têm id (nascem 0), e todos colidiriam num só.
    const [executionOpenIndex, setExecutionOpenIndex] = useState<number | null>(
        null
    )

    // Destinatário PADRÃO da taxa do anestesista, herdado pelo agendamento.
    const [anesthetistRecipientKind, setAnesthetistRecipientKind] = useState<
        'HOSPITAL' | 'PROFESSIONAL' | 'PARTNER' | null
    >(null)
    const [anesthetistRecipientId, setAnesthetistRecipientId] = useState<
        number | null
    >(null)

    // Custo composto pelos componentes: o que a clínica vai PAGAR de repasse ao vender este
    // pacote (parceiro + profissional + produto). Exibido no bloco de componentes, ao lado da
    // composição — NÃO se confunde com "Preço próprio", que é o preço da empresa que não
    // trabalha com parceiro. Ver docs/catalog-package-pricing.md §5.
    const [packageComputedCost, setPackageComputedCost] = useState<
        number | null
    >(null)

    /**
     * Sequência da última consulta de custo disparada.
     *
     * O custo é lido do servidor, e a tela dispara mais de uma leitura por gesto (a
     * lista muda na hora, e a gravação do componente termina depois). Sem a sequência,
     * a resposta da consulta ANTIGA — feita quando o parceiro ainda não estava salvo —
     * chegava por último e sobrescrevia o custo novo pelo zero anterior.
     */
    const packageCostRequestSeq = useRef(0)

    /**
     * Relê o custo dos componentes no servidor.
     *
     * É o servidor quem soma (`GET catalog/:id/package-cost`), então o número só muda
     * depois que a alteração do componente foi GRAVADA — daí esta função ser chamada
     * também no fim de cada patch, e não só quando a lista muda na tela.
     */
    const refreshPackageCost = useCallback(async () => {
        if (!id) return
        const seq = ++packageCostRequestSeq.current
        const res = await getCatalogPackageCost(
            parseInt(id),
            user?.companyId ?? undefined
        )
        if (seq !== packageCostRequestSeq.current) return
        if (res.success && res.result) setPackageComputedCost(res.result.cost)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, user?.companyId])

    useEffect(() => {
        // Abre com o preço de venda do pacote: é a proposta padrão, e o operador ajusta.
        // Só semeia uma vez — sobrescrever depois apagaria o que ele digitou.
        if (saleOverridePrice || !formData.sellingPrice) return
        setSaleOverridePrice(formData.sellingPrice)
        // ⚠️ O markup GRAVADO manda. Derivá-lo do custo dos componentes deixava o campo
        // vazio quando esse custo é zero — o valor estava salvo no banco e a tela mostrava
        // em branco, parecendo que não tinha salvado. Só calcula quando não há nada
        // gravado E existe custo de onde tirar.
        setSaleMarkup(
            formData.markupPercentage ||
                (packageComputedCost
                    ? recalcMarkupFromPrices(
                          packageComputedCost,
                          parseCurrencyToCents(formData.sellingPrice)
                      )
                    : '')
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.sellingPrice, formData.markupPercentage, packageComputedCost])

    /**
     * Altera um componente do pacote na tela e, em edição, persiste no mesmo passo.
     *
     * O componente é endereçado pelo id da LINHA do vínculo — `itemId` não identifica mais
     * nada desde que um componente pode ser produto (dois componentes do mesmo pacote podem
     * ter `itemId` nulo).
     */
    /**
     * Edições de componente ainda NÃO gravadas, por id da linha.
     *
     * O valor do componente grava ao SAIR do campo — a cada tecla seria uma requisição por
     * dígito. Só que "sair do campo" e "clicar em Salvar e sair" acontecem no mesmo gesto:
     * o clique no botão tira o foco, a requisição parte e a navegação seguinte a cancela.
     * O operador digita o custo, salva, e o valor não está lá.
     *
     * Guardando o que está pendente, o submit consegue esperar antes de navegar.
     */
    const pendingItemPatches = useRef<Map<number, Partial<CatalogPackageItem>>>(
        new Map()
    )

    /**
     * Campos que a API aceita no componente. O resto do patch é só da TELA.
     *
     * O painel de execução manda também o objeto da entidade (`partner`,
     * `professional`) para a lista exibir o nome sem recarregar. Mandar isso para a API
     * derruba a requisição inteira: a validação é `forbidNonWhitelisted`, e a resposta é
     * um 400 seco — "property partner should not exist" — que na tela vira "erro ao
     * salvar" sem dizer o quê.
     */
    const PACKAGE_ITEM_API_FIELDS = [
        'quantity',
        'complimentary',
        'sortOrder',
        'notes',
        'amount',
        'recipientKind',
        'recipientId',
        'partnerId',
        'professionalId',
        'professionalAmount',
    ] as const

    const patchPackageItem = async (
        idx: number,
        patch: Partial<CatalogPackageItem>
    ) => {
        const target = packageItems[idx]
        setPackageItems(prev =>
            prev.map((it, i) => (i === idx ? { ...it, ...patch } : it))
        )
        if (!isEdit || !id || !target?.id) return
        pendingItemPatches.current.delete(target.id)

        const apiPatch: Record<string, unknown> = {}
        for (const campo of PACKAGE_ITEM_API_FIELDS) {
            if (campo in patch) apiPatch[campo] = (patch as any)[campo]
        }
        if (Object.keys(apiPatch).length === 0) return

        const res = await updateCatalogPackageItem(
            parseInt(id),
            target.id,
            apiPatch as any
        )
        if (!res.success) {
            toast.error(res.error || 'Erro ao salvar a execução do componente')
            return
        }

        // Só AGORA o servidor sabe do parceiro (ou do valor) recém-escolhido. A lista
        // já tinha mudado na tela e disparado uma consulta de custo, mas aquela partiu
        // antes da gravação e voltava com o total antigo — o operador escolhia o
        // parceiro, o custo continuava zerado, e só aparecia depois de salvar, sair e
        // recarregar a tela.
        await refreshPackageCost()
    }

    /** Grava o que ficou pendente. Chamado pelo submit, antes de sair da tela. */
    const flushPendingItemPatches = async () => {
        if (!isEdit || !id || pendingItemPatches.current.size === 0) return
        const pendentes = Array.from(pendingItemPatches.current.entries())
        pendingItemPatches.current.clear()
        const results = await Promise.all(
            pendentes.map(([componentId, patch]) =>
                updateCatalogPackageItem(
                    parseInt(id),
                    componentId,
                    patch as any
                )
            )
        )
        if (results.some(r => !r.success)) {
            toast.error('Erro ao salvar o valor de um componente')
            return
        }
        await refreshPackageCost()
    }

    // Componente-PRODUTO do pacote. Busca separada da de serviços de propósito: os dois são
    // item de catálogo, mas produto não tem TUSS nem categoria clínica, e misturá-los no
    // mesmo dropdown obrigaria o operador a adivinhar o que cada linha é.
    const [packageProductSearch, setPackageProductSearch] = useState('')
    const [packageProductResults, setPackageProductResults] = useState<
        Array<{
            id: number
            name: string
            companyPrices?: CatalogCompanyPrice[]
        }>
    >([])
    const [showPackageProductDropdown, setShowPackageProductDropdown] =
        useState(false)
    const [loadingPackageProductSearch, setLoadingPackageProductSearch] =
        useState(false)

    // Mesmo preço em todas as filiais da organização.
    //
    // Nasce MARCADO na criação: o item é org-global, o preço é por filial, e o operador
    // cadastra logado numa unidade só — o resultado era o item existir nas outras sem
    // preço, e na loja delas aparecer a R$ 0,00. Na EDIÇÃO nasce desmarcado, porque ali
    // marcar significa reescrever o preço das outras filiais.
    const [applyPriceToAllCompanies, setApplyPriceToAllCompanies] = useState(!isEdit)
    // As filiais que ESTE usuário acessa — é o mesmo conjunto do seletor de unidade. Só
    // faz sentido oferecer a opção a quem tem mais de uma.
    const availableCompanies = useAppSelector(
        state => state.auth.accessibleCompanies
    )

    // Lojas em que a MERCADORIA aparece (`StoreProduct`). A vitrine lista a partir
    // desse vínculo: produto sem loja marcada existe no catálogo e não aparece em
    // loja nenhuma. Só faz sentido em `category = PRODUCT`.
    const [availableStores, setAvailableStores] = useState<StoreConfig[]>([])
    const [selectedStoreIds, setSelectedStoreIds] = useState<number[]>([])

    // Configuração de preços do catálogo
    const [catalogPricingMode, setCatalogPricingMode] =
        useState<string>('priority')
    // Item com venda e custo zero (Configurações Financeiras > Precificação), da
    // organização. Desligado, o backend recusa venda sem custo; ligado, o campo de
    // custo aceita o zero digitado.
    const [allowZeroCost, setAllowZeroCost] = useState(false)

    // Confirmação de remoção
    const [confirmRemove, setConfirmRemove] = useState<{
        type:
            | 'partner'
            | 'professional'
            | 'variation'
            // Ações em massa do modal de variação: `index` é ignorado.
            | 'all-partners'
            | 'all-professionals'
        index: number
        name: string
    } | null>(null)
    const [removingItem, setRemovingItem] = useState(false)

    // ==================== Cálculos de preço ====================

    const recalcSellingPriceFromMarkup = (
        priceCents: number,
        markup: number
    ): string => {
        if (priceCents <= 0 || isNaN(markup)) return ''
        const sellingCents = Math.round(
            priceCents + (priceCents * markup) / 100
        )
        return formatCentsToDisplay(sellingCents)
    }

    const recalcMarkupFromPrices = (
        priceCents: number,
        sellingCents: number
    ): string => {
        if (priceCents <= 0) return ''
        const markup = ((sellingCents - priceCents) / priceCents) * 100
        return markup.toFixed(2)
    }

    const handlePriceChange = (value: string) => {
        // Com item sem custo permitido, o "0" digitado no campo vazio vira "R$ 0,00"
        // (o `allowEmpty` o apagava). Apagando, o campo volta a ficar vazio.
        const typedZero =
            allowZeroCost &&
            !formData.price &&
            /^0+$/.test(value.replace(/\D/g, ''))
        const formatted = currencyFormat(value, !typedZero)
        const priceCents = parseCurrencyToCents(formatted)
        const updated = { ...formData, price: formatted }
        if (formData.markupPercentage) {
            const markup = parseFloat(formData.markupPercentage)
            if (!isNaN(markup)) {
                updated.sellingPrice = recalcSellingPriceFromMarkup(
                    priceCents,
                    markup
                )
            }
        } else if (formData.sellingPrice) {
            const sellingCents = parseCurrencyToCents(formData.sellingPrice)
            if (sellingCents > 0) {
                updated.markupPercentage = recalcMarkupFromPrices(
                    priceCents,
                    sellingCents
                )
            }
        }
        setFormData(updated)
    }

    const handleSellingPriceChange = (value: string) => {
        const formatted = currencyFormat(value, true)
        const sellingCents = parseCurrencyToCents(formatted)
        const priceCents = parseCurrencyToCents(formData.price)
        setFormData(prev => ({
            ...prev,
            sellingPrice: formatted,
            markupPercentage: recalcMarkupFromPrices(priceCents, sellingCents),
        }))
    }

    const handleMarkupChange = (value: string) => {
        const markup = parseFloat(value)
        const priceCents = parseCurrencyToCents(formData.price)
        setFormData(prev => ({
            ...prev,
            markupPercentage: value,
            sellingPrice: !isNaN(markup)
                ? recalcSellingPriceFromMarkup(priceCents, markup)
                : '',
        }))
    }

    // Pricing helpers para parceiros/profissionais
    const handleSubPriceChange = (
        list: PartnerPricing[] | ProfessionalPricing[],
        setList: React.Dispatch<React.SetStateAction<any[]>>,
        index: number,
        field: 'price' | 'sellingPrice' | 'markupPercentage',
        value: string
    ) => {
        const updated = [...list]
        const item = { ...updated[index] }

        if (field === 'price') {
            item.price = currencyFormat(value, true)
            const priceCents = parseCurrencyToCents(item.price)
            if (item.markupPercentage) {
                const markup = parseFloat(item.markupPercentage)
                if (!isNaN(markup)) {
                    item.sellingPrice = recalcSellingPriceFromMarkup(
                        priceCents,
                        markup
                    )
                }
            } else if (item.sellingPrice) {
                const sellingCents = parseCurrencyToCents(item.sellingPrice)
                if (sellingCents > 0) {
                    item.markupPercentage = recalcMarkupFromPrices(
                        priceCents,
                        sellingCents
                    )
                }
            }
        } else if (field === 'sellingPrice') {
            item.sellingPrice = currencyFormat(value, true)
            const sellingCents = parseCurrencyToCents(item.sellingPrice)
            const priceCents = parseCurrencyToCents(item.price)
            item.markupPercentage = recalcMarkupFromPrices(
                priceCents,
                sellingCents
            )
        } else if (field === 'markupPercentage') {
            item.markupPercentage = value
            const markup = parseFloat(value)
            const priceCents = parseCurrencyToCents(item.price)
            item.sellingPrice = !isNaN(markup)
                ? recalcSellingPriceFromMarkup(priceCents, markup)
                : ''
        }

        updated[index] = item
        setList(updated)
    }

    // ==================== Variações ====================
    // Variação-como-item: criadas/editadas via MODAL (reuso deste form em modo
    // variação-filha). Ver a seção de Variações no render e reloadVariations().

    // ==================== Carregamento de dados ====================

    // Chegada pelo "Adicionar variação" de um item que acabou de ser criado: abre o
    // modal da variação e limpa o state, para recarregar a página não reabri-lo.
    useEffect(() => {
        if (isModal || !id) return
        if (!(location.state as { openVariation?: boolean } | null)?.openVariation)
            return
        setEditingVariationId(null)
        setVariationModalOpen(true)
        navigate(`${location.pathname}${location.search}`, {
            replace: true,
            state: null,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state])

    useEffect(() => {
        if (user?.companyId) {
            loadSpecialties()
            getSettingByName('catalog_pricing_mode', user.companyId).then(
                res => {
                    if (res.success && res.result) {
                        setCatalogPricingMode(res.result.value)
                    }
                }
            )
            getCatalogCostPolicy()
                .then(res =>
                    setAllowZeroCost(!!res.success && res.result?.allowZeroCost === true)
                )
                .catch(() => setAllowZeroCost(false))
        }
    }, [user?.companyId])

    useEffect(() => {
        if (user?.companyId) {
            loadCatalogTypes()
        }
    }, [user?.companyId, category])

    // Lojas da empresa + as que já exibem este produto. Só em PRODUCT: serviço não vai
    // para a vitrine. A carga é separada da do item porque a lista de lojas não depende
    // dele — e na CRIAÇÃO já precisa estar na tela.
    useEffect(() => {
        if (category !== 'PRODUCT' || !user?.companyId) return
        getStoreConfigByCompany(user.companyId).then(res => {
            if (res.success) {
                setAvailableStores(
                    (Array.isArray(res.result) ? res.result : [res.result]).filter(
                        Boolean
                    ) as StoreConfig[]
                )
            }
        })
    }, [category, user?.companyId])

    useEffect(() => {
        if (category !== 'PRODUCT' || !isEdit || !id) return
        getStoresByProduct(parseInt(id)).then(res => {
            if (res.success) {
                setSelectedStoreIds((res.result || []).map(l => l.storeConfigId))
            }
        })
    }, [category, isEdit, id])

    useEffect(() => {
        if (isEdit && id) {
            // Só restringe à entidade travada quando caímos aqui por duplicidade
            // de TUSS DENTRO do modal de parceiro/profissional (editingExistingId).
            const restrictLockedEntity =
                editingExistingId != null &&
                (lockPartnerSection || lockProfessionalSection)
            loadCatalog(parseInt(id), { restrictLockedEntity })
        }
    }, [id, isEdit])

    // Variação NOVA: herda os vínculos do PAI como default (parceiros,
    // profissionais e hospitais), livres para editar/adicionar/remover.
    useEffect(() => {
        if (isVariationMode && !isEdit && props.parentId != null) {
            loadParentDefaults(props.parentId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // modalCreate: pré-vincula a entidade dona da aba (parceiro/profissional),
    // sem savedId → será criada como vínculo novo no submit (addCatalog*).
    useEffect(() => {
        if (!props.modalCreate || isEdit) return
        if (props.lockedPartner) {
            setPartnerPricings([
                {
                    partnerId: props.lockedPartner.id,
                    partnerName: props.lockedPartner.name,
                    partnerLocation: props.lockedPartner.location || '',
                    price: '',
                    sellingPrice: '',
                    markupPercentage: '',
                    maxDiscount: '',
                    observations: '',
                    active: true,
                },
            ])
        }
        if (props.lockedProfessional) {
            setProfessionalPricings([
                {
                    professionalId: props.lockedProfessional.id,
                    professionalName: props.lockedProfessional.name,
                    price: '',
                    sellingPrice: '',
                    markupPercentage: '',
                    maxDiscount: '',
                    active: true,
                },
            ])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Popula os vínculos do pai numa variação nova (SEM savedId → criados na filha).
    const loadParentDefaults = async (parentId: number) => {
        const response = await getCatalog(parentId)
        if (!response.success || !response.result) return
        const parent: Catalog = response.result
        setFormData(prev => ({
            ...prev,
            hospitalIds: parent.hospitals?.length
                ? parent.hospitals
                      .map((ch: any) => ch.hospitalId ?? ch.hospital?.id)
                      .filter((v: any) => v != null)
                : [],
        }))
        if (parent.partners?.length) {
            setPartnerPricings(
                parent.partners.map(cp => ({
                    partnerId: cp.partnerId,
                    partnerName:
                        cp.partner?.name ||
                        cp.partner?.tradingName ||
                        `Parceiro #${cp.partnerId}`,
                    partnerLocation: [cp.partner?.city, cp.partner?.state]
                        .filter(Boolean)
                        .join(' - '),
                    price: formatCentsToDisplay(cp.price),
                    sellingPrice: formatCentsToDisplay(cp.sellingPrice),
                    markupPercentage: cp.markupPercentage?.toString() || '',
                    maxDiscount: cp.maxDiscount?.toString() || '',
                    observations: cp.observations || '',
                    active: cp.active,
                    // sem savedId → será criado como vínculo novo na filha
                }))
            )
        }
        if (parent.professionals?.length) {
            setProfessionalPricings(
                parent.professionals.map(cp => ({
                    professionalId: cp.professionalId,
                    professionalName:
                        cp.professional?.name ||
                        `Profissional #${cp.professionalId}`,
                    price: formatCentsToDisplay(cp.price),
                    sellingPrice: formatCentsToDisplay(cp.sellingPrice),
                    markupPercentage: cp.markupPercentage?.toString() || '',
                    maxDiscount: cp.maxDiscount?.toString() || '',
                    active: cp.active,
                    // sem savedId → será criado como vínculo novo na filha
                }))
            )
        }
    }

    const loadCatalogTypes = async () => {
        if (!user?.organizationId) return
        try {
            const response = await getCatalogTypesByOrganization(
                user.organizationId,
                { category }
            )
            if (response.success) {
                setCatalogTypes(response.result)
            }
        } catch {
            // silently fail
        }
    }

    const loadSpecialties = async () => {
        if (!user?.companyId) return
        try {
            const response = await getSpecialtiesByOrganization(
                user.organizationId
            )
            if (response.success) {
                setSpecialties(response.result)
            }
        } catch {
            // silently fail
        }
    }

    const loadCatalog = async (
        catalogId: number,
        opts?: { restrictLockedEntity?: boolean }
    ) => {
        setLoadingData(true)
        try {
            const response = await getCatalog(catalogId)
            if (response.success) {
                const catalog: Catalog = response.result
                setCategory(catalog.category)
                setAnesthetistRecipientKind(
                    catalog.anesthetistRecipientKind ?? null
                )
                setAnesthetistRecipientId(
                    catalog.anesthetistRecipientId ?? null
                )
                const currentStock = (catalog.companyStocks || []).find(
                    stock => stock.companyId === user?.company?.id
                )
                setSavedCompanyPrice(
                    (catalog.companyPrices || []).find(
                        row => row.companyId === user?.companyId
                    ) ?? null
                )
                setFormData({
                    name: catalog.name,
                    tussCode: catalog.tussCode || '',
                    description: catalog.description || '',
                    typeId: catalog.typeId || null,
                    price: formatCentsToDisplay(
                        catalogCompanyPrice(catalog, user?.companyId).price
                    ),
                    sellingPrice: formatCentsToDisplay(
                        catalogCompanyPrice(catalog, user?.companyId)
                            .sellingPrice
                    ),
                    markupPercentage:
                        catalogCompanyPrice(
                            catalog,
                            user?.companyId
                        ).markupPercentage?.toString() || '',
                    maxDiscount:
                        catalogCompanyPrice(
                            catalog,
                            user?.companyId
                        ).maxDiscount?.toString() || '',
                    defaultDuration: catalog.defaultDuration?.toString() || '',
                    collectionMaterial: catalog.collectionMaterial || '',
                    active: catalog.active,
                    budgetOnly: catalog.budgetOnly === true,
                    isSurgical: catalog.isSurgical === true,
                    schedulable: catalog.schedulable !== false,
                    specialtyIds: catalog.specialties?.length
                        ? catalog.specialties.map((cs: any) => cs.specialty.id)
                        : catalog.specialtyId
                          ? [catalog.specialtyId]
                          : [],
                    hospitalIds: catalog.hospitals?.length
                        ? catalog.hospitals
                              .map(
                                  (ch: any) => ch.hospitalId ?? ch.hospital?.id
                              )
                              .filter((v: any) => v != null)
                        : [],
                    instructions: catalog.instructions || '',
                    preInstructions: catalog.preInstructions || '',
                    postInstructions: catalog.postInstructions || '',
                    anesthetistFee: formatCentsToDisplay(
                        catalog.anesthetistFee
                    ),
                    maxTeleDuration: catalog.maxTeleDuration?.toString() || '',
                    enableChat: catalog.enableChat || false,
                    enableScreenShare: catalog.enableScreenShare || false,
                    recordingEnabled: catalog.recordingEnabled || false,
                    // Produto: cadastro (satélite) e estoque DESTA filial. O item é da
                    // organização; a mercadoria é da unidade — por isso o estoque é
                    // procurado pelo companyId do operador, e não o primeiro da lista.
                    supplierId: catalog.supplierId ?? null,
                    productCategoryId: catalog.productCategoryId ?? null,
                    productSubcategoryId: catalog.productSubcategoryId ?? null,
                    sku: catalog.productDetails?.sku || '',
                    barcode: catalog.productDetails?.barcode || '',
                    brand: catalog.productDetails?.brand || '',
                    unit: catalog.productDetails?.unit || 'UNIT',
                    productTags: (catalog.productDetails?.tags || []).join(', '),
                    ncm: catalog.productDetails?.ncm || '',
                    cest: catalog.productDetails?.cest || '',
                    cfop: catalog.productDetails?.cfop || '',
                    productOrigin: catalog.productDetails?.origin || '',
                    weight: catalog.productDetails?.weight?.toString() || '',
                    width: catalog.productDetails?.width?.toString() || '',
                    height: catalog.productDetails?.height?.toString() || '',
                    depth: catalog.productDetails?.depth?.toString() || '',
                    warranty: catalog.productDetails?.warranty || '',
                    attributes: Array.isArray(catalog.productDetails?.attributes)
                        ? (catalog.productDetails.attributes as any[])
                              .filter(attr => attr && typeof attr.key === 'string')
                              .map(attr => ({
                                  key: String(attr.key),
                                  value: String(attr.value ?? ''),
                                  ...(typeof attr.color === 'string' && attr.color ? { color: attr.color } : {}),
                              }))
                        : [],
                    stockEnabled: currentStock?.stockEnabled ?? false,
                    stockQuantity: currentStock?.stockQuantity?.toString() || '',
                    minStock: currentStock?.minStock?.toString() || '',
                })

                if (catalog.catalogType) {
                    setTypeInput(catalog.catalogType.name)
                }

                // specialties são carregadas via specialtyIds no formData

                // Restrição do modal de entidade: ao editar um item existente
                // (duplicidade de TUSS) dentro do modal de parceiro/profissional,
                // mostra APENAS a entidade travada e oculta as demais (itens podem
                // ter muitos vínculos). O vínculo existente entra com savedId (update);
                // se ainda não existe, entra sem savedId (será criado ao salvar).
                if (opts?.restrictLockedEntity && props.lockedPartner) {
                    const existing = catalog.partners?.find(
                        cp => cp.partnerId === props.lockedPartner!.id
                    )
                    setPartnerPricings([
                        existing
                            ? {
                                  partnerId: existing.partnerId,
                                  partnerName:
                                      existing.partner?.name ||
                                      existing.partner?.tradingName ||
                                      props.lockedPartner.name,
                                  partnerLocation: [
                                      existing.partner?.city,
                                      existing.partner?.state,
                                  ]
                                      .filter(Boolean)
                                      .join(' - '),
                                  price: formatCentsToDisplay(existing.price),
                                  sellingPrice: formatCentsToDisplay(
                                      existing.sellingPrice
                                  ),
                                  markupPercentage:
                                      existing.markupPercentage?.toString() ||
                                      '',
                                  maxDiscount:
                                      existing.maxDiscount?.toString() || '',
                                  observations: existing.observations || '',
                                  active: existing.active,
                                  savedId: existing.id,
                              }
                            : {
                                  partnerId: props.lockedPartner.id,
                                  partnerName: props.lockedPartner.name,
                                  partnerLocation:
                                      props.lockedPartner.location || '',
                                  price: '',
                                  sellingPrice: '',
                                  markupPercentage: '',
                                  maxDiscount: '',
                                  observations: '',
                                  active: true,
                              },
                    ])
                    // Foco no atual: oculta profissionais deste item (não são tocados
                    // no submit por não estarem no estado).
                    setProfessionalPricings([])
                } else if (
                    opts?.restrictLockedEntity &&
                    props.lockedProfessional
                ) {
                    const existing = catalog.professionals?.find(
                        cp => cp.professionalId === props.lockedProfessional!.id
                    )
                    setProfessionalPricings([
                        existing
                            ? {
                                  professionalId: existing.professionalId,
                                  professionalName:
                                      existing.professional?.name ||
                                      props.lockedProfessional.name,
                                  price: formatCentsToDisplay(existing.price),
                                  sellingPrice: formatCentsToDisplay(
                                      existing.sellingPrice
                                  ),
                                  markupPercentage:
                                      existing.markupPercentage?.toString() ||
                                      '',
                                  maxDiscount:
                                      existing.maxDiscount?.toString() || '',
                                  active: existing.active,
                                  savedId: existing.id,
                              }
                            : {
                                  professionalId: props.lockedProfessional.id,
                                  professionalName:
                                      props.lockedProfessional.name,
                                  price: '',
                                  sellingPrice: '',
                                  markupPercentage: '',
                                  maxDiscount: '',
                                  active: true,
                              },
                    ])
                    setPartnerPricings([])
                } else {
                    // Carregar parceiros vinculados
                    if (catalog.partners?.length) {
                        setPartnerPricings(
                            catalog.partners.map(cp => ({
                                partnerId: cp.partnerId,
                                partnerName:
                                    cp.partner?.name ||
                                    cp.partner?.tradingName ||
                                    `Parceiro #${cp.partnerId}`,
                                partnerLocation: [
                                    cp.partner?.city,
                                    cp.partner?.state,
                                ]
                                    .filter(Boolean)
                                    .join(' - '),
                                price: formatCentsToDisplay(cp.price),
                                sellingPrice: formatCentsToDisplay(
                                    cp.sellingPrice
                                ),
                                markupPercentage:
                                    cp.markupPercentage?.toString() || '',
                                maxDiscount: cp.maxDiscount?.toString() || '',
                                observations: cp.observations || '',
                                active: cp.active,
                                savedId: cp.id,
                            }))
                        )
                    }

                    // Carregar profissionais vinculados
                    if (catalog.professionals?.length) {
                        setProfessionalPricings(
                            catalog.professionals.map(cp => ({
                                professionalId: cp.professionalId,
                                professionalName:
                                    cp.professional?.name ||
                                    `Profissional #${cp.professionalId}`,
                                price: formatCentsToDisplay(cp.price),
                                sellingPrice: formatCentsToDisplay(
                                    cp.sellingPrice
                                ),
                                markupPercentage:
                                    cp.markupPercentage?.toString() || '',
                                maxDiscount: cp.maxDiscount?.toString() || '',
                                active: cp.active,
                                savedId: cp.id,
                            }))
                        )
                    }
                }

                // Carregar variações (novo modelo: filhas Catalog isVariation).
                if (catalog.variationChildren?.length) {
                    setVariations(
                        catalog.variationChildren.map(v => {
                            const cp = (v.companyPrices || []).find(
                                p => p.companyId === user?.companyId
                            )
                            return {
                                savedId: v.id,
                                name: v.name,
                                tussCode: v.tussCode || '',
                                active: v.active,
                                sortOrder: v.sortOrder ?? 0,
                                price: formatCentsToDisplay(cp?.price),
                                sellingPrice: formatCentsToDisplay(
                                    cp?.sellingPrice
                                ),
                                markupPercentage:
                                    cp?.markupPercentage?.toString() || '',
                                maxDiscount: cp?.maxDiscount?.toString() || '',
                            }
                        })
                    )
                }

                // Carregar materiais médicos vinculados
                if (catalog.medicalSupplies?.length) {
                    setCatalogMedicalSupplies(catalog.medicalSupplies)
                }

                // Carregar exames relacionados
                if (catalog.catalogExams?.length) {
                    setCatalogExams(catalog.catalogExams)
                }

                // Carregar componentes do pacote
                if (catalog.category === 'PACKAGE') {
                    const pkgRes = await getCatalogPackageItems(catalog.id)
                    if (pkgRes.success) {
                        setPackageItems(pkgRes.result || [])
                    }
                }
            } else {
                toast.error(
                    response.error || 'Erro ao carregar item do catálogo'
                )
                navigate('/registrations/catalog')
            }
        } catch {
            toast.error('Erro ao carregar item do catálogo')
            navigate('/registrations/catalog')
        } finally {
            setLoadingData(false)
        }
    }

    // ==================== Type dropdown ====================

    const filteredTypes = catalogTypes.filter(t =>
        t.name.toLowerCase().includes(typeInput.toLowerCase().trim())
    )

    const typeInputMatchesExisting = catalogTypes.some(
        t => t.name.toLowerCase() === typeInput.toLowerCase().trim()
    )

    const handleSelectType = (type: CatalogType) => {
        setFormData(prev => ({ ...prev, typeId: type.id }))
        setTypeInput(type.name)
        setShowTypeDropdown(false)
    }

    const handleCreateType = async () => {
        if (!user?.organizationId || !typeInput.trim()) return
        try {
            const response = await createCatalogType({
                name: typeInput.trim(),
                category,
                organizationId: user.organizationId,
            })
            if (response.success) {
                const newType = response.result
                setCatalogTypes(prev =>
                    [...prev, newType].sort((a, b) =>
                        a.name.localeCompare(b.name)
                    )
                )
                setFormData(prev => ({ ...prev, typeId: newType.id }))
                setTypeInput(newType.name)
                setShowTypeDropdown(false)
                toast.success('Tipo criado com sucesso')
            } else {
                toast.error(response.error || 'Erro ao criar tipo')
            }
        } catch {
            toast.error('Erro ao criar tipo')
        }
    }

    const handleClearType = () => {
        setFormData(prev => ({ ...prev, typeId: null }))
        setTypeInput('')
    }

    // ==================== Specialty dropdown ====================

    const filteredSpecialties = specialties.filter(
        s =>
            !formData.specialtyIds.includes(s.id) &&
            s.name.toLowerCase().includes(specialtySearch.toLowerCase().trim())
    )

    const specialtySearchMatchesExisting = specialties.some(
        s => s.name.toLowerCase() === specialtySearch.toLowerCase().trim()
    )

    const selectedSpecialties = specialties.filter(s =>
        formData.specialtyIds.includes(s.id)
    )

    const handleSelectSpecialty = (specialty: Specialty) => {
        if (!formData.specialtyIds.includes(specialty.id)) {
            setFormData(prev => ({
                ...prev,
                specialtyIds: [...prev.specialtyIds, specialty.id],
            }))
        }
        setSpecialtySearch('')
        setShowSpecialtyDropdown(false)
    }

    const handleRemoveSpecialty = (specialtyId: number) => {
        setFormData(prev => ({
            ...prev,
            specialtyIds: prev.specialtyIds.filter(id => id !== specialtyId),
        }))
    }

    const handleCreateSpecialty = async () => {
        if (!user?.organizationId || !specialtySearch.trim()) return
        try {
            const response = await createSpecialty({
                name: specialtySearch.trim(),
                organizationId: user.organizationId,
            })
            if (response.success) {
                const newSpecialty = response.result
                setSpecialties(prev =>
                    [...prev, newSpecialty].sort((a, b) =>
                        a.name.localeCompare(b.name)
                    )
                )
                setFormData(prev => ({
                    ...prev,
                    specialtyIds: [...prev.specialtyIds, newSpecialty.id],
                }))
                setSpecialtySearch('')
                setShowSpecialtyDropdown(false)
                toast.success('Especialidade criada com sucesso')
            } else {
                toast.error(response.error || 'Erro ao criar especialidade')
            }
        } catch {
            toast.error('Erro ao criar especialidade')
        }
    }

    // ==================== Parceiros ====================

    const handleAddPartnerFromSelector = (
        partnerId: number,
        partner: Partner
    ) => {
        if (partnerPricings.some(p => p.partnerId === partnerId)) {
            toast.error('Parceiro já adicionado')
            return
        }

        setPartnerPricings(prev => [
            ...prev,
            {
                partnerId,
                partnerName: partner.name || partner.tradingName || '',
                partnerLocation: [partner.city, partner.state]
                    .filter(Boolean)
                    .join(' - '),
                price: '',
                sellingPrice: '',
                markupPercentage: '',
                maxDiscount: '',
                observations: '',
                active: true,
            },
        ])
    }

    const handleRemovePartner = async (index: number) => {
        const item = partnerPricings[index]
        if (isEdit && item.savedId && id) {
            setRemovingItem(true)
            try {
                const response = await removeCatalogPartner(
                    parseInt(id),
                    item.partnerId
                )
                if (!response.success) {
                    toast.error(response.error || 'Erro ao remover parceiro')
                    return
                }
                toast.success('Parceiro removido com sucesso')
            } catch {
                toast.error('Erro ao remover parceiro')
                return
            } finally {
                setRemovingItem(false)
            }
        }
        setPartnerPricings(prev => prev.filter((_, i) => i !== index))
        setConfirmRemove(null)
    }

    const handleSavePartner = async (index: number) => {
        if (!id) return
        const item = partnerPricings[index]
        // No PATCH o partnerId vai na URL: o UpdateCatalogPartnerDto o omite e o
        // ValidationPipe roda com forbidNonWhitelisted.
        const data: UpdateCatalogPartnerData = {
            price: item.price ? parseCurrencyToCents(item.price) : undefined,
            sellingPrice: item.sellingPrice
                ? parseCurrencyToCents(item.sellingPrice)
                : undefined,
            markupPercentage: item.markupPercentage
                ? parseFloat(item.markupPercentage)
                : undefined,
            maxDiscount: item.maxDiscount
                ? parseFloat(item.maxDiscount)
                : undefined,
            observations: item.observations || undefined,
            active: item.active,
        }

        try {
            if (item.savedId) {
                const response = await updateCatalogPartner(
                    parseInt(id),
                    item.partnerId,
                    data
                )
                if (response.success) {
                    toast.success('Parceiro atualizado')
                } else {
                    toast.error(response.error || 'Erro ao atualizar parceiro')
                }
            } else {
                const response = await addCatalogPartner(parseInt(id), {
                    ...data,
                    partnerId: item.partnerId,
                })
                if (response.success) {
                    const updated = [...partnerPricings]
                    updated[index] = {
                        ...updated[index],
                        savedId: response.result.id,
                    }
                    setPartnerPricings(updated)
                    toast.success('Parceiro adicionado')
                } else {
                    toast.error(response.error || 'Erro ao adicionar parceiro')
                }
            }
        } catch {
            toast.error('Erro ao salvar parceiro')
        }
    }

    // ==================== Profissionais ====================

    const handleAddProfessionalFromSelector = (
        professionalId: number,
        professional: Professional
    ) => {
        if (
            professionalPricings.some(p => p.professionalId === professionalId)
        ) {
            toast.error('Profissional já adicionado')
            return
        }

        setProfessionalPricings(prev => [
            ...prev,
            {
                professionalId,
                professionalName: professional.name,
                price: '',
                sellingPrice: '',
                markupPercentage: '',
                maxDiscount: '',
                active: true,
            },
        ])
    }

    const handleRemoveProfessional = async (index: number) => {
        const item = professionalPricings[index]
        if (isEdit && item.savedId && id) {
            setRemovingItem(true)
            try {
                const response = await removeCatalogProfessional(
                    parseInt(id),
                    item.professionalId
                )
                if (!response.success) {
                    toast.error(
                        response.error || 'Erro ao remover profissional'
                    )
                    return
                }
                toast.success('Profissional removido com sucesso')
            } catch {
                toast.error('Erro ao remover profissional')
                return
            } finally {
                setRemovingItem(false)
            }
        }
        setProfessionalPricings(prev => prev.filter((_, i) => i !== index))
        setConfirmRemove(null)
    }

    /**
     * "Remover todos" das variações. A variação nasce herdando os parceiros e os
     * profissionais do pai, e quase sempre o operador quer zerar essa herança e
     * escolher só os que valem para aquela variação — um a um seria dezenas de cliques.
     *
     * Só aparece no modal (`isModal`): na página do item pai, apagar todos os vínculos
     * de uma vez é destrutivo demais para caber atrás de um clique.
     *
     * Em edição, remove no servidor **um a um** (não há endpoint de lote) e em sequência,
     * mantendo no estado apenas os que falharam — assim a tela reflete o que de fato
     * sobrou no banco, e o operador pode tentar de novo só neles.
     */
    const handleRemoveAllPartners = async () => {
        if (!isEdit || !id) {
            setPartnerPricings([])
            setConfirmRemove(null)
            return
        }

        setRemovingItem(true)
        const catalogId = parseInt(id)
        const failed: typeof partnerPricings = []
        try {
            for (const item of partnerPricings) {
                if (!item.savedId) continue // nunca foi ao servidor
                try {
                    const response = await removeCatalogPartner(
                        catalogId,
                        item.partnerId
                    )
                    if (!response.success) failed.push(item)
                } catch {
                    failed.push(item)
                }
            }
            setPartnerPricings(failed)
            if (failed.length === 0) {
                toast.success('Parceiros removidos com sucesso')
            } else {
                toast.error(
                    `${failed.length} parceiro(s) não puderam ser removidos`
                )
            }
        } finally {
            setRemovingItem(false)
            setConfirmRemove(null)
        }
    }

    const handleRemoveAllProfessionals = async () => {
        if (!isEdit || !id) {
            setProfessionalPricings([])
            setConfirmRemove(null)
            return
        }

        setRemovingItem(true)
        const catalogId = parseInt(id)
        const failed: typeof professionalPricings = []
        try {
            for (const item of professionalPricings) {
                if (!item.savedId) continue
                try {
                    const response = await removeCatalogProfessional(
                        catalogId,
                        item.professionalId
                    )
                    if (!response.success) failed.push(item)
                } catch {
                    failed.push(item)
                }
            }
            setProfessionalPricings(failed)
            if (failed.length === 0) {
                toast.success('Profissionais removidos com sucesso')
            } else {
                toast.error(
                    `${failed.length} profissional(is) não puderam ser removidos`
                )
            }
        } finally {
            setRemovingItem(false)
            setConfirmRemove(null)
        }
    }

    const handleSaveProfessional = async (index: number) => {
        if (!id) return
        const item = professionalPricings[index]
        // No PATCH o professionalId vai na URL: o UpdateCatalogProfessionalDto o omite e o
        // ValidationPipe roda com forbidNonWhitelisted.
        const data: UpdateCatalogProfessionalData = {
            price: item.price ? parseCurrencyToCents(item.price) : undefined,
            sellingPrice: item.sellingPrice
                ? parseCurrencyToCents(item.sellingPrice)
                : undefined,
            markupPercentage: item.markupPercentage
                ? parseFloat(item.markupPercentage)
                : undefined,
            maxDiscount: item.maxDiscount
                ? parseFloat(item.maxDiscount)
                : undefined,
            active: item.active,
        }

        try {
            if (item.savedId) {
                const response = await updateCatalogProfessional(
                    parseInt(id),
                    item.professionalId,
                    data
                )
                if (response.success) {
                    toast.success('Profissional atualizado')
                } else {
                    toast.error(
                        response.error || 'Erro ao atualizar profissional'
                    )
                }
            } else {
                const response = await addCatalogProfessional(parseInt(id), {
                    ...data,
                    professionalId: item.professionalId,
                })
                if (response.success) {
                    const updated = [...professionalPricings]
                    updated[index] = {
                        ...updated[index],
                        savedId: response.result.id,
                    }
                    setProfessionalPricings(updated)
                    toast.success('Profissional adicionado')
                } else {
                    toast.error(
                        response.error || 'Erro ao adicionar profissional'
                    )
                }
            }
        } catch {
            toast.error('Erro ao salvar profissional')
        }
    }

    // ==================== Submit ====================

    /**
     * "Apenas orçamento" grava na hora quando o item já existe — não espera o botão
     * Atualizar. É um bloqueio de agendamento: deixá-lo pendente de um submit que o
     * operador pode nunca dar faria o item continuar agendável sem que ninguém veja.
     *
     * Em modo de criação não há o que salvar ainda; o valor vai junto no submit.
     * Se a gravação falhar, o checkbox volta ao estado anterior — a UI nunca mostra
     * um bloqueio que o banco não tem.
     */
    const handleBudgetOnlyChange = async (checked: boolean) => {
        const previous = formData.budgetOnly
        setFormData(prev => ({ ...prev, budgetOnly: checked }))

        if (!isEdit || !id) return

        setSavingBudgetOnly(true)
        try {
            const response = await updateCatalog(Number(id), {
                budgetOnly: checked,
            })
            if (response.success) {
                toast.success(
                    checked
                        ? 'Item marcado como apenas orçamento'
                        : 'Item liberado para agendamento'
                )
            } else {
                setFormData(prev => ({ ...prev, budgetOnly: previous }))
                toast.error(response.error || 'Erro ao salvar')
            }
        } catch {
            setFormData(prev => ({ ...prev, budgetOnly: previous }))
            toast.error('Erro ao salvar')
        } finally {
            setSavingBudgetOnly(false)
        }
    }

    /**
     * Seleção de TUSS no modo criação (itens normais, não pacote/variação): grava
     * o código/nome e, se já existir um item com esse TUSS na organização, entra em
     * MODO DE EDIÇÃO do item existente (evita o erro de duplicidade). O load do item
     * é disparado pelo effect que observa `id`/`editingExistingId`.
     */
    const handleTussSelected = async (
        code: string | null,
        tuss?: { term?: string } | null
    ) => {
        // Com código: o nome passa a ser o termo oficial. Sem código (o usuário limpou): PRESERVA o
        // nome digitado — agora ele é editável (TUSS é opcional), então apagá-lo perderia o texto.
        setFormData(prev => ({
            ...prev,
            tussCode: code || '',
            name: tuss?.term ?? prev.name,
        }))
        if (
            !code ||
            isEdit ||
            editingExistingId != null ||
            isVariationMode ||
            isPackage ||
            !user?.companyId
        ) {
            return
        }
        try {
            const res = await getCatalogByTuss(user.companyId, code)
            if (res.success && res.result) {
                setDuplicateNotice(true)
                setEditingExistingId(res.result.id)
            }
        } catch {
            // Falha no lookup não bloqueia o cadastro: o backend ainda barra
            // duplicidade no submit.
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        // Lido antes do primeiro `await`: o botão de variação zera o ref logo depois
        // do `requestSubmit`, que só executa a parte síncrona deste handler.
        const openVariationAfterSave = openVariationAfterSaveRef.current

        // O clique no botão tira o foco do campo e dispara a gravação daquele componente
        // — que a navegação seguinte cancelaria. Gravar aqui, e esperar, é o que faz o
        // custo digitado sobreviver ao "Salvar e sair".
        await flushPendingItemPatches()

        if (isPackage) {
            if (!formData.name.trim()) {
                toast.error('Informe o nome do pacote')
                return
            }
            // Componentes NÃO são obrigatórios: o pacote pode ser auto-contido — o nome descreve
            // o conjunto ("10 Sessões de Drenagens + Tapping 1 Área") e o preço fica no próprio
            // pacote. É o caso dos combos legados, cujos itens não são vendidos separadamente.
            // Ver docs/catalog-packages.md §3.2-b.
        } else if (isVariationMode) {
            // Variação: nome é texto livre (ex.: "Com contraste"); TUSS opcional.
            if (!formData.name.trim()) {
                toast.error('Informe o nome da variação')
                return
            }
        } else if (!formData.tussCode.trim() && !formData.name.trim()) {
            // TUSS é opcional: o item precisa de um código OU de um nome livre.
            // Fora da saúde não há campo de código na tela, então citá-lo aqui
            // mandaria o operador procurar algo que não existe.
            toast.error(
                isHealth && !isProduct
                    ? 'Informe o código TUSS ou um nome para o item'
                    : isProduct
                      ? 'Informe um nome para o produto'
                      : 'Informe um nome para o item'
            )
            return
        }

        if (
            (category === 'CONSULTATION' || category === 'TELECONSULTATION') &&
            formData.specialtyIds.length === 0
        ) {
            toast.error(
                'Pelo menos uma especialidade é obrigatória para esta categoria'
            )
            return
        }

        if (!user?.companyId) {
            toast.error('Empresa não identificada')
            return
        }

        if (showVariationsSection && variations.some(v => !v.name.trim())) {
            toast.error('Informe o nome de todas as variações')
            return
        }

        if (catalogPricingMode === 'item_only') {
            const priceCents = parseCurrencyToCents(formData.price)
            const sellingPriceCents = parseCurrencyToCents(
                formData.sellingPrice
            )
            if (priceCents <= 0 && sellingPriceCents <= 0) {
                toast.error(
                    'Quando o modo de preço está configurado como "preço base do item", é obrigatório definir o Preço de Custo ou o Preço de Venda'
                )
                return
            }
        }

        setLoading(true)
        try {
            const data: any = {
                companyId: user.companyId,
                // Mesmo preço nas demais filiais. Só vai quando há mais de uma — em
                // organização de filial única a flag não significa nada.
                ...(availableCompanies.length > 1 && applyPriceToAllCompanies
                    ? { applyPriceToAllCompanies: true }
                    : {}),
                category,
                name: formData.name.trim(),
                // Variação-como-item: cria como filha do pai (herda categoria, TUSS
                // opcional). Ver docs/catalog-variation-as-item-redesign.md.
                ...(isVariationMode ? { parentId: props.parentId } : {}),
                // Pacote não tem TUSS próprio (nome comercial livre).
                tussCode: isPackage
                    ? undefined
                    : formData.tussCode.trim() || undefined,
                description: formData.description.trim() || undefined,
                typeId: formData.typeId || undefined,
                price: formData.price
                    ? parseCurrencyToCents(formData.price)
                    : 0,
                /**
                 * CAMPO VAZIO É `null`, NÃO `undefined`.
                 *
                 * O update só grava o que veio (`sellingPrice !== undefined`), então
                 * omitir o campo vazio deixava o valor ANTIGO no banco: apagar o preço
                 * próprio da variação era impossível pela tela, e o salvamento ainda
                 * morria em "Informe também o preço de custo" — o backend comparava o
                 * custo zerado que ia no corpo contra a venda antiga que ficara gravada
                 * (`assertItemPriceIsComplete` valida o estado RESULTANTE).
                 *
                 * Zerar preço próprio é gesto legítimo: numa intermediadora o preço mora
                 * no vínculo do parceiro, e a filial não tem valor seu a declarar.
                 */
                sellingPrice: formData.sellingPrice
                    ? parseCurrencyToCents(formData.sellingPrice)
                    : null,
                // Markup sem preço é resíduo do valor anterior: sai junto.
                markupPercentage: formData.markupPercentage
                    ? parseFloat(formData.markupPercentage)
                    : null,
                maxDiscount: formData.maxDiscount
                    ? parseFloat(formData.maxDiscount)
                    : 0,
                collectionMaterial:
                    formData.collectionMaterial.trim() || undefined,
                defaultDuration: formData.defaultDuration
                    ? parseInt(formData.defaultDuration)
                    : undefined,
                active: formData.active,
                budgetOnly: formData.budgetOnly,
                // Cirurgia só existe em procedimento; nas demais categorias o campo nem
                // aparece na tela e vai `false` para não guardar estado sem significado.
                isSurgical:
                    category === 'PROCEDURE' ? formData.isSurgical : false,
                // Pacote novo nasce fora do agendamento; os antigos, que já foram criados
                // como agendáveis, continuam como estão.
                // Pacote não é agendável: vende-se. O backend também recusa mudança em
                // pacote — aqui o campo simplesmente não vai, para a edição de um pacote
                // ANTIGO (agendável, 686 em produção) não o tirar da agenda sem querer.
                ...(isPackage
                    ? isEdit
                        ? {}
                        : { schedulable: false }
                    : { schedulable: formData.schedulable }),
            }

            // Especialidades: a seção aparece em TODA categoria que não seja
            // mercadoria (a especialidade é de quem executa). Restringir o envio a
            // consulta/teleconsulta fazia a edição em procedimento, exame, pacote e
            // variação sair sem o campo — o operador mexia na tela e nada gravava,
            // sem erro nenhum. Vai sempre que a seção existe, inclusive vazio
            // (desvincular tudo é escolha legítima; o backend faz deleteMany).
            if (!isProduct) {
                data.specialtyIds = formData.specialtyIds
            }

            // Hospitais padrão (todas as categorias)
            data.hospitalIds = formData.hospitalIds
            if (category === 'EXAM') {
                data.instructions = formData.instructions.trim() || undefined
            }
            if (category === 'PROCEDURE') {
                data.preInstructions =
                    formData.preInstructions.trim() || undefined
                data.postInstructions =
                    formData.postInstructions.trim() || undefined
            }
            if (category === 'PROCEDURE' || category === 'EXAM') {
                data.anesthetistRecipientKind = anesthetistRecipientKind
                data.anesthetistRecipientId = anesthetistRecipientId
                data.anesthetistFee = formData.anesthetistFee
                    ? parseCurrencyToCents(formData.anesthetistFee)
                    : undefined
            }
            if (category === 'TELECONSULTATION') {
                data.maxTeleDuration = formData.maxTeleDuration
                    ? parseInt(formData.maxTeleDuration)
                    : undefined
                data.enableChat = formData.enableChat
                data.enableScreenShare = formData.enableScreenShare
                data.recordingEnabled = formData.recordingEnabled
            }

            if (category === 'PRODUCT') {
                data.supplierId = formData.supplierId ?? undefined
                data.productCategoryId = formData.productCategoryId ?? undefined
                data.productSubcategoryId =
                    formData.productSubcategoryId ?? undefined

                const num = (value: string) =>
                    value.trim() ? Number(value) : undefined
                const text = (value: string) => value.trim() || undefined

                data.productDetails = {
                    sku: text(formData.sku),
                    barcode: text(formData.barcode),
                    brand: text(formData.brand),
                    unit: formData.unit || undefined,
                    // Etiquetas chegam como texto separado por vírgula; vazias saem
                    // fora para não gravar [''] e sujar a busca.
                    tags: formData.productTags
                        .split(',')
                        .map(tag => tag.trim())
                        .filter(Boolean),
                    ncm: text(formData.ncm),
                    cest: text(formData.cest),
                    cfop: text(formData.cfop),
                    origin: formData.productOrigin || undefined,
                    weight: num(formData.weight),
                    width: num(formData.width),
                    height: num(formData.height),
                    depth: num(formData.depth),
                    warranty: text(formData.warranty),
                    // Linha sem nome ou sem valor não é característica: sai fora.
                    attributes: formData.attributes
                        .map(attr => ({
                            key: attr.key.trim(),
                            value: attr.value.trim(),
                            ...(isVariationMode && attr.color && /^#[0-9a-fA-F]{6}$/.test(attr.color)
                                ? { color: attr.color }
                                : {}),
                        }))
                        .filter(attr => attr.key && attr.value),
                }
                // Estoque é da FILIAL: o backend grava na empresa de `companyId`,
                // que já vai no payload.
                data.stock = {
                    stockEnabled: formData.stockEnabled,
                    stockQuantity: formData.stockEnabled
                        ? num(formData.stockQuantity)
                        : undefined,
                    minStock: formData.stockEnabled
                        ? num(formData.minStock)
                        : undefined,
                }
            }

            const response = isEdit
                ? await updateCatalog(parseInt(id!), data)
                : await createCatalog(data)

            if (response.success) {
                const catalogId = isEdit ? parseInt(id!) : response.result?.id
                if (catalogId) {
                    // Fotos/vídeos escolhidos antes de o item existir. Vão antes
                    // dos demais vínculos porque a navegação logo abaixo desmonta
                    // a tela — e com ela a seção que segura os arquivos.
                    if (!isEdit) {
                        await mediaSectionRef.current?.uploadPending(catalogId)
                    }

                    // Salvar materiais médicos novos (apenas na criação)
                    if (!isEdit && catalogMedicalSupplies.length > 0) {
                        await Promise.allSettled(
                            catalogMedicalSupplies.map(ms =>
                                addCatalogMedicalSupply(catalogId, {
                                    medicalSupplyId: ms.medicalSupplyId,
                                    quantity: ms.quantity,
                                    unitPrice: ms.unitPrice,
                                    notes: ms.notes || undefined,
                                })
                            )
                        )
                    }

                    // Salvar exames relacionados (apenas na criação)
                    if (!isEdit && catalogExams.length > 0) {
                        await Promise.allSettled(
                            catalogExams.map(ce =>
                                addCatalogExam(catalogId, {
                                    examCatalogId: ce.examCatalogId,
                                    quantity: ce.quantity,
                                    complimentary: ce.complimentary,
                                    notes: ce.notes || undefined,
                                })
                            )
                        )
                    }

                    // Salvar componentes do pacote (apenas na criação)
                    if (!isEdit && packageItems.length > 0) {
                        await Promise.allSettled(
                            packageItems.map((pi, i) =>
                                addCatalogPackageItem(catalogId, {
                                    // O alvo depende do TIPO: item, produto ou material —
                                    // hospital e anestesista não apontam para nada.
                                    kind: pi.kind,
                                    ...(pi.medicalSupplyId
                                        ? {
                                              medicalSupplyId:
                                                  pi.medicalSupplyId,
                                          }
                                        : pi.itemId
                                          ? { itemId: pi.itemId }
                                          : {}),
                                    amount: pi.amount ?? undefined,
                                    recipientKind:
                                        pi.recipientKind ?? undefined,
                                    recipientId: pi.recipientId ?? undefined,
                                    quantity: pi.quantity,
                                    complimentary: pi.complimentary,
                                    sortOrder: i,
                                    notes: pi.notes || undefined,
                                    // Execução definida antes de o pacote existir: vai
                                    // junto na criação do vínculo, senão se perderia.
                                    partnerId: pi.partnerId ?? undefined,
                                    professionalId:
                                        pi.professionalId ?? undefined,
                                    professionalAmount:
                                        pi.professionalAmount ?? undefined,
                                })
                            )
                        )
                    }

                    // Salvar parceiros: novos (add) e existentes (update)
                    await Promise.allSettled(
                        partnerPricings.map(pp => {
                            if (pp.savedId) {
                                return updateCatalogPartner(
                                    catalogId,
                                    pp.partnerId,
                                    {
                                        price: pp.price
                                            ? parseCurrencyToCents(pp.price)
                                            : undefined,
                                        sellingPrice: pp.sellingPrice
                                            ? parseCurrencyToCents(
                                                  pp.sellingPrice
                                              )
                                            : undefined,
                                        markupPercentage: pp.markupPercentage
                                            ? parseFloat(pp.markupPercentage)
                                            : undefined,
                                        maxDiscount: pp.maxDiscount
                                            ? parseFloat(pp.maxDiscount)
                                            : undefined,
                                        observations:
                                            pp.observations || undefined,
                                        active: pp.active,
                                    }
                                )
                            }
                            return addCatalogPartner(catalogId, {
                                partnerId: pp.partnerId,
                                price: pp.price
                                    ? parseCurrencyToCents(pp.price)
                                    : undefined,
                                sellingPrice: pp.sellingPrice
                                    ? parseCurrencyToCents(pp.sellingPrice)
                                    : undefined,
                                markupPercentage: pp.markupPercentage
                                    ? parseFloat(pp.markupPercentage)
                                    : undefined,
                                maxDiscount: pp.maxDiscount
                                    ? parseFloat(pp.maxDiscount)
                                    : undefined,
                                observations: pp.observations || undefined,
                                active: pp.active,
                            })
                        })
                    )

                    // Salvar profissionais: novos (add) e existentes (update)
                    await Promise.allSettled(
                        professionalPricings.map(pp => {
                            if (pp.savedId) {
                                return updateCatalogProfessional(
                                    catalogId,
                                    pp.professionalId,
                                    {
                                        price: pp.price
                                            ? parseCurrencyToCents(pp.price)
                                            : undefined,
                                        sellingPrice: pp.sellingPrice
                                            ? parseCurrencyToCents(
                                                  pp.sellingPrice
                                              )
                                            : undefined,
                                        markupPercentage: pp.markupPercentage
                                            ? parseFloat(pp.markupPercentage)
                                            : undefined,
                                        maxDiscount: pp.maxDiscount
                                            ? parseFloat(pp.maxDiscount)
                                            : undefined,
                                        active: pp.active,
                                    }
                                )
                            }
                            return addCatalogProfessional(catalogId, {
                                professionalId: pp.professionalId,
                                price: pp.price
                                    ? parseCurrencyToCents(pp.price)
                                    : undefined,
                                sellingPrice: pp.sellingPrice
                                    ? parseCurrencyToCents(pp.sellingPrice)
                                    : undefined,
                                markupPercentage: pp.markupPercentage
                                    ? parseFloat(pp.markupPercentage)
                                    : undefined,
                                maxDiscount: pp.maxDiscount
                                    ? parseFloat(pp.maxDiscount)
                                    : undefined,
                                active: pp.active,
                            })
                        })
                    )

                    // Lojas em que a mercadoria aparece. Vale na criação E na edição: é
                    // o `syncProductStores` que substitui a lista inteira, e desmarcar
                    // todas é uma escolha legítima (tirar da vitrine).
                    // A variação aparece dentro do produto, nunca sozinha na vitrine.
                    if (isProduct && !isVariationMode && user?.companyId) {
                        await syncProductStores(
                            catalogId,
                            selectedStoreIds,
                            user.companyId
                        ).catch(() => undefined)
                    }

                    // Variações NÃO são salvas aqui: são itens filhos gerenciados
                    // pelo modal (createCatalog com parentId + parceiro/profissional).
                }

                toast.success(
                    isVariationMode
                        ? isEdit
                            ? 'Variação atualizada com sucesso'
                            : 'Variação criada com sucesso'
                        : isPackage && !isEdit
                          ? 'Pacote criado. Agora dá para vendê-lo aqui mesmo.'
                          : isEdit
                            ? 'Item do catálogo atualizado com sucesso'
                            : 'Item do catálogo criado com sucesso'
                )
                // Modo modal: devolve o controle ao chamador (fecha o modal e
                // recarrega a lista de variações do pai). Página: navega de volta.
                if (isModal && props.onSuccess) props.onSuccess(catalogId)
                // Salvo pelo "Adicionar variação": fica no item, agora em edição, com o
                // modal da variação aberto. `replace` para o "voltar" do navegador não
                // cair de novo no formulário de item novo.
                else if (openVariationAfterSave && catalogId)
                    navigate(`/registrations/catalog/${catalogId}`, {
                        replace: true,
                        state: { openVariation: true },
                    })
                // Pacote recém-criado continua ABERTO, agora em edição. Vender exige o
                // pacote gravado (os componentes e o preço moram no banco, e a venda
                // chama POST /orders com o id dele), então o bloco de venda só
                // existe na edição. Mandar para a lista obrigaria o operador a procurar
                // o que acabou de criar para poder vender.
                else if (isPackage && !isEdit && catalogId)
                    navigate(`/registrations/catalog/${catalogId}`)
                else navigate('/registrations/catalog')
            } else {
                toast.error(response.error || 'Erro ao salvar item do catálogo')
            }
        } catch {
            toast.error('Erro ao salvar item do catálogo')
        } finally {
            setLoading(false)
        }
    }

    // ==================== Condições de categoria ====================

    const isPackage = category === 'PACKAGE'

    // Detector de pacote: o operador costuma cadastrar bundles como item comum, escrevendo
    // "A + B", "Pacote X" ou "Combo Y" no nome. Foi assim que nasceram os ~1.000 combos legados
    // que hoje precisam ser reclassificados à mão. Aqui avisamos na hora — e o clique já move o
    // nome digitado para o nome do pacote. Só sugere quando NÃO é pacote/variação.
    const looksLikePackage =
        !isPackage &&
        !isVariationMode &&
        /(\+|\bpacotes?\b|\bcombos?\b)/i.test(formData.name || '')

    const switchToPackage = () => {
        const nome = (formData.name || '').trim()
        setCategory('PACKAGE')
        // Sem TUSS: pacote não tem código próprio. O nome digitado vira o nome do pacote.
        setFormData(prev => ({ ...prev, name: nome, tussCode: '' }))
    }

    // Fotos escolhidas antes de o item existir: a seção as retém e o submit as
    // despacha assim que o id nasce (ver `uploadPending`).
    const mediaSectionRef = useRef<CatalogMediaSectionHandle>(null)

    const showExamFields = category === 'EXAM'
    /** Mercadoria: ganha identificação, fiscal, logística e estoque. */
    const isProduct = category === 'PRODUCT'
    const showOrderFields = category === 'PROCEDURE'
    const showTeleFields = category === 'TELECONSULTATION'
    // Pacote não tem materiais/variações próprios (os componentes carregam isso).
    const showMedicalSuppliesSection =
        category === 'PROCEDURE' || category === 'EXAM'
    const showVariationsSection =
        !isVariationMode &&
        (category === 'EXAM' ||
            category === 'PROCEDURE' ||
            category === 'CONSULTATION' ||
            // Mercadoria vive de variação: uma camisa tem cor e tamanho, e cada
            // combinação é um item filho com preço, SKU e estoque próprios.
            category === 'PRODUCT')
    const showPackageItemsSection = isPackage && !isVariationMode

    // Recompõe o custo do pacote sempre que a execução ou a quantidade de um componente
    // muda. O cálculo é do backend (`computePackageCost`) para não haver duas fórmulas de
    // custo divergindo — é o mesmo número que o repasse vai pagar.
    /**
     * Custo do componente NESTA filial, já multiplicado pela quantidade — o que a clínica
     * paga a quem executa. Espelha `computePackageCost` do backend; existe aqui só para o
     * operador ver linha a linha de onde vem o total (que é o do backend, não este).
     *
     * Cortesia ENTRA: ela tira o componente do preço cobrado, não do repasse.
     */
    const packageComponentCost = (pi: CatalogPackageItem): number => {
        const qty = pi.quantity || 1
        const kind = pi.kind ?? 'ITEM'

        // Mercadoria usa o mesmo preço por filial do serviço: `price` É o custo.
        if (kind === 'PRODUCT') {
            return (
                catalogCompanyPrice(
                    { companyPrices: pi.item?.companyPrices },
                    user?.companyId
                ).price * qty
            )
        }
        // Hospital e anestesista: o valor da linha É o custo. Material: o da linha, ou o do
        // cadastro quando a linha não define.
        if (kind === 'HOSPITAL' || kind === 'ANESTHETIST') {
            return (pi.amount ?? 0) * qty
        }
        if (kind === 'SUPPLY') {
            return (pi.amount ?? pi.medicalSupply?.costPrice ?? 0) * qty
        }
        const partnerCost = pi.partnerId
            ? ((pi.item?.partners || []).find(p => p.partnerId === pi.partnerId)
                  ?.price ?? 0)
            : 0
        return (partnerCost + (pi.professionalAmount ?? 0)) * qty
    }

    /**
     * Algum componente já diz quem executa?
     *
     * Havendo execução no componente, ela é a fonte — e as seções "Parceiros" e
     * "Profissionais" do próprio pacote saem da tela: manter as duas seria oferecer dois
     * lugares para dizer a mesma coisa, com respostas diferentes.
     *
     * Produto não conta: quem recebe por ele é o fornecedor do cadastro, nunca um parceiro.
     *
     * O pacote AUTO-CONTIDO (sem componentes) não entra aqui e continua exibindo as seções —
     * é nelas que mora o preço do bundle legado. Ver docs/catalog-packages.md §3.2.
     */
    const packageHasComponentExecution =
        isPackage &&
        packageItems.some(
            pi =>
                pi.kind !== 'PRODUCT' &&
                (pi.partnerId != null || pi.professionalId != null)
        )

    // Componente cuja execução está aberta no painel abaixo da tabela.
    const executionIndex =
        executionOpenIndex != null &&
        packageItems[executionOpenIndex] &&
        packageItems[executionOpenIndex].kind !== 'PRODUCT'
            ? executionOpenIndex
            : -1
    const executionComponent =
        executionIndex >= 0 ? packageItems[executionIndex] : null

    // ⚠️ Tudo o que ENTRA no custo tem de estar aqui: é esta assinatura que dispara o
    // recálculo. Sem `amount`, digitar o custo de um material/hospital/anestesista não
    // mexia no "Custo do pacote" — o valor ficava salvo no banco e a tela mostrava o
    // total antigo, o que derruba markup e lucro junto.
    const packageCostSignature = isPackage
        ? JSON.stringify(
              packageItems.map(pi => [
                  pi.id,
                  pi.kind ?? null,
                  pi.quantity,
                  pi.amount ?? null,
                  pi.medicalSupplyId ?? null,
                  pi.partnerId ?? null,
                  pi.professionalId ?? null,
                  pi.professionalAmount ?? null,
                  pi.itemId ?? null,
              ])
          )
        : ''

    useEffect(() => {
        if (!isPackage || !id || packageItems.length === 0) {
            setPackageComputedCost(null)
            return
        }
        refreshPackageCost()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPackage, id, packageCostSignature, refreshPackageCost])

    // Custo que só chegou agora (o parceiro acabou de ser escolhido) com a venda já
    // preenchida: o markup é dedutível dos dois, e deixá-lo em branco fazia parecer que
    // o campo tinha sido ignorado. Só preenche o que está VAZIO — markup digitado ou
    // gravado manda, e recalculá-lo aqui apagaria a intenção do operador.
    useEffect(() => {
        if (!isPackage || saleMarkup || !saleOverridePrice) return
        if (!packageComputedCost || packageComputedCost <= 0) return
        setSaleMarkup(
            recalcMarkupFromPrices(
                packageComputedCost,
                parseCurrencyToCents(saleOverridePrice)
            )
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packageComputedCost, saleOverridePrice, saleMarkup, isPackage])

    // ==================== Permissão ====================

    if (isEdit && !canUpdate) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <p className="text-base-content/60 text-sm">
                    Você não tem permissão para editar itens do catálogo.
                </p>
            </div>
        )
    }

    if (!isEdit && !canCreate) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <p className="text-base-content/60 text-sm">
                    Você não tem permissão para criar itens do catálogo.
                </p>
            </div>
        )
    }

    // ==================== Loading ====================

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando catálogo...
                </p>
            </div>
        )
    }

    // ==================== Render ====================

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() =>
                            isModal && props.onCancel
                                ? props.onCancel()
                                : navigate('/registrations/catalog')
                        }
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdCategory className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {/* Pacote tem nome próprio na tela: chamá-lo de "item do
                                catálogo" ao vender confunde quem veio para VENDER, não
                                para cadastrar. */}
                            {isVariationMode
                                ? isEdit
                                    ? 'Editar Variação'
                                    : 'Nova Variação'
                                : isPackage
                                  ? sellingMode
                                      ? 'Vender Pacote'
                                      : isEdit
                                        ? 'Editar Pacote'
                                        : 'Novo Pacote'
                                  : isEdit
                                    ? 'Editar Item do Catálogo'
                                    : 'Novo Item do Catálogo'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isVariationMode
                                ? 'A variação é um item filho com preço próprio (parceiro/profissional/filial)'
                                : isPackage
                                  ? sellingMode
                                      ? `Escolha o ${clientLabel}, ajuste as datas de cada componente e gere a cobrança`
                                      : isEdit
                                        ? 'Atualize os componentes e o preço do pacote'
                                        : 'Monte o pacote: componentes, custo e preço de venda'
                                  : isEdit
                                    ? 'Atualize os dados do item do catálogo'
                                    : 'Adicione um novo item ao catálogo'}
                        </p>
                    </div>
                </div>
            </div>

            <form
                ref={formRef}
                onSubmit={handleSubmit}
                className="flex flex-col gap-6"
            >
                {/* Duplicidade de TUSS: entramos em edição de um item que já existe. */}
                {duplicateNotice && (
                    <div className="alert alert-warning">
                        <MdInfo className="text-xl shrink-0" />
                        <div>
                            <p className="font-medium">
                                Este item já existe no catálogo, modo de edição
                                ativado
                            </p>
                        </div>
                    </div>
                )}

                {/* Seletor de Categoria */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Categoria
                    </h2>
                    <div className="flex flex-wrap gap-3">
                        {CATEGORIES.filter(
                            cat =>
                                isCategoryAllowed(cat) ||
                                (categoryLocked && cat === category)
                        ).map(cat => (
                            <label
                                key={cat}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-all ${
                                    category === cat
                                        ? 'border-secondary bg-secondary/10 text-secondary font-medium'
                                        : 'border-base-300 hover:border-base-400'
                                } ${categoryLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                                <input
                                    type="radio"
                                    name="category"
                                    value={cat}
                                    checked={category === cat}
                                    onChange={() => setCategory(cat)}
                                    disabled={categoryLocked}
                                    className="radio radio-secondary radio-sm"
                                />
                                <span>{categoryLabels[cat]}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Dados Gerais */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Dados Gerais
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* PACOTE e VARIAÇÃO: nome livre (variação = rótulo, ex.: "Com
                            contraste"; TUSS opcional). Demais: TUSS define o nome. */}
                        {isPackage || isVariationMode ? (
                            <div className="form-control md:col-span-2">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        {isVariationMode
                                            ? 'Nome da variação *'
                                            : 'Nome do pacote *'}
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    /*
                                     * Fora da saúde o placeholder DESCREVE o campo em
                                     * vez de dar exemplo: "com contraste" não diz nada
                                     * num salão, e um exemplo de salão não diria nada
                                     * numa oficina. Ver `ServiceLabels`.
                                     */
                                    placeholder={
                                        isVariationMode
                                            ? isProduct
                                                ? 'Ex.: Azul / P'
                                                : variationNamePlaceholder
                                            : packageNamePlaceholder
                                    }
                                    value={formData.name}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            name: e.target.value,
                                        }))
                                    }
                                />
                                {isVariationMode ? (
                                    <span className="label-text-alt mt-2 text-base-content/50">
                                        Texto livre (rótulo da variação, ex.:
                                        cor ou tamanho).
                                        {isHealth && !isProduct
                                            ? ' A variação herda o código TUSS do item pai — não tem código próprio.'
                                            : ''}
                                    </span>
                                ) : (
                                    <span className="label-text-alt mt-2 text-base-content/50">
                                        Nome comercial livre.
                                        {isHealth
                                            ? ' O pacote não tem TUSS próprio — o código fica em cada componente abaixo.'
                                            : ''}
                                    </span>
                                )}
                            </div>
                        ) : (
                            /* TUSS é OPCIONAL: há tenants que não faturam por convênio e não usam
                               a Tabela 22 (estética, odonto particular, fisioterapia). Com código,
                               o nome é derivado do termo oficial (e o campo fica somente-leitura,
                               para o nome nunca divergir do código). Sem código, nome é livre. */
                            <>
                                {isHealth && !isProduct && (
                                    <div className="form-control md:col-span-2">
                                        <TussSelector
                                            value={formData.tussCode || null}
                                            label="Código TUSS"
                                            placeholder="Buscar por código ou termo TUSS... (opcional)"
                                            disabled={duplicateNotice}
                                            onChange={(code, tuss) =>
                                                handleTussSelected(code, tuss)
                                            }
                                        />
                                        <span className="label-text-alt mt-2 text-base-content/50">
                                            Opcional. Ao selecionar um código, o
                                            nome do item passa a ser o termo
                                            oficial do TUSS.
                                        </span>
                                    </div>
                                )}

                                <div className="form-control md:col-span-2">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Nome do item *
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder={
                                            isProduct
                                                ? 'Nome do produto'
                                                : itemNamePlaceholder
                                        }
                                        value={formData.name}
                                        readOnly={!!formData.tussCode}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                name: e.target.value,
                                            }))
                                        }
                                    />
                                    <span className="label-text-alt mt-2 text-base-content/50">
                                        {formData.tussCode
                                            ? 'Definido pelo termo TUSS. Limpe o código acima para editar o nome livremente.'
                                            : isHealth && !isProduct
                                              ? 'Nome comercial livre (sem código TUSS).'
                                              : 'Nome comercial livre.'}
                                    </span>

                                    {/* Aviso instantâneo: o nome tem cara de bundle ("A + B",
                                        "Pacote", "Combo") mas a categoria não é Pacote. */}
                                    {looksLikePackage && (
                                        <div className="alert alert-warning mt-3 py-3">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5 shrink-0"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z"
                                                />
                                            </svg>
                                            <span className="text-sm">
                                                Parece que você está cadastrando
                                                um <strong>pacote</strong>.
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-warning"
                                                onClick={switchToPackage}
                                            >
                                                Mudar para pacote
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Tipo */}
                        <div className="form-control relative">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Tipo
                                </span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Digite para buscar ou criar um tipo..."
                                    value={typeInput}
                                    onChange={e => {
                                        setTypeInput(e.target.value)
                                        setShowTypeDropdown(true)
                                        if (!e.target.value.trim()) {
                                            setFormData(prev => ({
                                                ...prev,
                                                typeId: null,
                                            }))
                                        }
                                    }}
                                    onFocus={() => setShowTypeDropdown(true)}
                                    onBlur={() =>
                                        setTimeout(
                                            () => setShowTypeDropdown(false),
                                            200
                                        )
                                    }
                                />
                                {typeInput && (
                                    <button
                                        type="button"
                                        className="absolute z-10 right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs"
                                        onClick={handleClearType}
                                    >
                                        x
                                    </button>
                                )}
                            </div>
                            {showTypeDropdown && typeInput.trim() && (
                                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {filteredTypes.map(type => (
                                        <button
                                            key={type.id}
                                            type="button"
                                            className="w-full text-left px-4 py-2 hover:bg-base-200 text-sm"
                                            onMouseDown={() =>
                                                handleSelectType(type)
                                            }
                                        >
                                            {type.name}
                                        </button>
                                    ))}
                                    {!typeInputMatchesExisting &&
                                        typeInput.trim() && (
                                            <button
                                                type="button"
                                                className="w-full text-left px-4 py-2 hover:bg-base-200 text-sm text-secondary flex items-center gap-2 border-t border-base-300/50"
                                                onMouseDown={handleCreateType}
                                            >
                                                <MdAdd className="text-lg" />
                                                Criar tipo: &quot;
                                                {typeInput.trim()}&quot;
                                            </button>
                                        )}
                                    {filteredTypes.length === 0 &&
                                        typeInputMatchesExisting && (
                                            <div className="px-4 py-2 text-sm text-base-content/50">
                                                Nenhum resultado
                                            </div>
                                        )}
                                </div>
                            )}
                        </div>

                        {/* Descrição.
                            Mercadoria usa editor rich text: a descrição do produto é
                            renderizada como HTML na loja (`product-detail-client.tsx`),
                            e é ali que negrito, lista e link vendem o produto. Nas
                            demais categorias o campo é texto simples — ninguém
                            renderiza aquele HTML, e gravá-lo só encheria as telas
                            internas de tag. */}
                        <div className="form-control md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Descrição
                                </span>
                            </label>
                            {isProduct ? (
                                <RichTextEditor
                                    content={formData.description}
                                    onChange={html =>
                                        setFormData(prev => ({
                                            ...prev,
                                            description: html,
                                        }))
                                    }
                                    placeholder="Descreva o produto: material, medidas, o que acompanha..."
                                    minHeight="200px"
                                />
                            ) : (
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Descreva o item do catálogo..."
                                    rows={3}
                                    value={formData.description}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            description: e.target.value,
                                        }))
                                    }
                                />
                            )}
                        </div>

                        {/* Duração Padrão — mercadoria não é executada, não tem duração. */}
                        {!isProduct && (
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Duração Padrão
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={formData.defaultDuration}
                                onChange={e =>
                                    setFormData(prev => ({
                                        ...prev,
                                        defaultDuration: e.target.value,
                                    }))
                                }
                            >
                                <option value="">Selecione...</option>
                                {DEFAULT_DURATIONS.map(duration => (
                                    <option
                                        key={duration.value}
                                        value={duration.value}
                                    >
                                        {duration.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        )}

                        {/* Status */}
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Status
                                </span>
                            </label>
                            <label className="cursor-pointer flex items-center gap-3 mt-1">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary"
                                    checked={formData.active}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            active: e.target.checked,
                                        }))
                                    }
                                />
                                <span className="label-text">
                                    {formData.active ? 'Ativo' : 'Inativo'}
                                </span>
                            </label>
                        </div>

                        {/* Agendável — em edição, salva sozinho. Marcado = agendável;
                            desmarcado = apenas orçamento (budgetOnly). A UI mostra o
                            estado positivo (agendável) para não confundir o operador;
                            o dado persistido continua sendo budgetOnly (invertido).

                            Mercadoria não é agendável (o backend força `schedulable`
                            false), então o par de opções não aparece. */}
                        {!isProduct && (
                        <div className="form-control">
                            <label className="cursor-pointer flex items-center gap-3 mt-1">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-success"
                                    checked={!formData.budgetOnly}
                                    disabled={savingBudgetOnly}
                                    onChange={e =>
                                        handleBudgetOnlyChange(
                                            !e.target.checked
                                        )
                                    }
                                />
                                <span className="label-text flex items-center gap-2">
                                    {formData.budgetOnly
                                        ? 'Apenas orçamento'
                                        : 'Agendável diretamente'}
                                    {savingBudgetOnly && (
                                        <span className="loading loading-spinner loading-xs" />
                                    )}
                                </span>
                            </label>
                            <span className="text-xs text-base-content/60 mt-2">
                                {isEdit
                                    ? 'Defina se o item pode ser agendado diretamente ou apenas em orçamentos'
                                    : 'Defina se o item pode ser agendado diretamente ou apenas em orçamentos'}
                            </span>
                        </div>
                        )}

                        {/* Cirúrgico — só em procedimento, e só na saúde. A variação
                            herda do pai (o formulário dela não oferece o campo).
                            Diferente de "apenas orçamento", este é salvo no submit. */}
                        {isHealth &&
                            showOrderFields &&
                            !isVariationMode && (
                                <div className="form-control">
                                    <label className="cursor-pointer flex items-center gap-3 mt-1">
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-warning"
                                            checked={formData.isSurgical}
                                            onChange={e =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    isSurgical:
                                                        e.target.checked,
                                                }))
                                            }
                                        />
                                        <span className="label-text">
                                            {formData.isSurgical
                                                ? 'Procedimento cirúrgico'
                                                : 'Procedimento não cirúrgico'}
                                        </span>
                                    </label>
                                    <span className="text-xs text-base-content/60 mt-2">
                                        Defina se o procedimento é uma cirurgia
                                    </span>
                                </div>
                            )}
                    </div>
                </div>

                {/* Preços */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-1">
                        Preço próprio
                    </h2>
                    <div className="text-sm text-base-content/60 mb-4">
                        utilize somente quando tiver valores próprios sem
                        parceiros
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Preço de Custo (R$)
                                    {catalogPricingMode === 'item_only' &&
                                    !allowZeroCost
                                        ? ' *'
                                        : ''}
                                </span>
                            </label>
                            <div className="flex items-stretch gap-2">
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="R$ 0,00"
                                    value={formData.price}
                                    onChange={e =>
                                        handlePriceChange(e.target.value)
                                    }
                                />
                                {/* O custo é o campo que mais fica em branco aqui: o
                                    operador sabe o gasto do mês e o volume, mas não o
                                    valor por atendimento — e sem custo não há markup
                                    nem margem para conferir depois. */}
                                <button
                                    type="button"
                                    className="btn btn-outline btn-square"
                                    onClick={() => setShowCostCalculator(true)}
                                    title="Calcular o custo a partir das despesas do mês"
                                    aria-label="Calcular o custo"
                                >
                                    <MdCalculate className="text-lg" />
                                </button>
                            </div>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Preço de Venda (R$)
                                    {catalogPricingMode === 'item_only'
                                        ? ' *'
                                        : ''}
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="R$ 0,00"
                                value={formData.sellingPrice}
                                onChange={e =>
                                    handleSellingPriceChange(e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Markup (%)
                                </span>
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                className="input input-bordered w-full"
                                placeholder="0,00"
                                value={formData.markupPercentage || ''}
                                onChange={e =>
                                    handleMarkupChange(e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Desconto Máximo (%)
                                </span>
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                className="input input-bordered w-full"
                                placeholder="0"
                                value={formData.maxDiscount || ''}
                                onChange={e =>
                                    setFormData(prev => ({
                                        ...prev,
                                        maxDiscount: e.target.value,
                                    }))
                                }
                            />
                        </div>
                    </div>

                    {/* O preço é por filial, mas preço DIFERENTE por unidade quase não
                        existe na prática. Sem isto, o item nasce com preço só na filial em
                        que o operador está logado — e some (ou aparece a R$ 0,00) nas
                        outras. Estoque NÃO entra aqui: é mercadoria física de cada uma. */}
                    {availableCompanies.length > 1 && (
                        <div className="mt-6 border-t border-base-300/50 pt-4">
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary checkbox-sm mt-0.5"
                                    checked={applyPriceToAllCompanies}
                                    onChange={e =>
                                        setApplyPriceToAllCompanies(e.target.checked)
                                    }
                                />
                                <span>
                                    <span className="text-sm font-medium">
                                        Aplicar em todas as filiais
                                    </span>
                                    <span className="block text-xs text-base-content/60">
                                        Grava este mesmo preço nas{' '}
                                        {availableCompanies.length - 1} outras filiais,{' '}
                                        <strong>substituindo</strong> o que elas tiverem.
                                        Vale para este salvamento — filial criada depois não
                                        herda. O estoque continua sendo de cada unidade.
                                    </span>
                                </span>
                            </label>
                        </div>
                    )}
                </div>

                {/* Especialidade é do profissional que executa: mercadoria não tem. */}
                {!isProduct && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Especialidades
                        </h2>

                        {selectedSpecialties.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                                {selectedSpecialties.map(s => (
                                    <span
                                        key={s.id}
                                        className="badge badge-secondary gap-1 py-3 px-3"
                                    >
                                        {s.name}
                                        <button
                                            type="button"
                                            className="ml-1 text-primary-content/70 hover:text-primary-content"
                                            onClick={() =>
                                                handleRemoveSpecialty(s.id)
                                            }
                                        >
                                            x
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="form-control relative">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Adicionar Especialidade
                                </span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Digite para buscar ou criar uma especialidade..."
                                    value={specialtySearch}
                                    onChange={e => {
                                        setSpecialtySearch(e.target.value)
                                        setShowSpecialtyDropdown(true)
                                    }}
                                    onFocus={() => setShowSpecialtyDropdown(true)}
                                    onBlur={() =>
                                        setTimeout(
                                            () => setShowSpecialtyDropdown(false),
                                            200
                                        )
                                    }
                                />
                                {specialtySearch && (
                                    <button
                                        type="button"
                                        className="absolute z-10 right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs"
                                        onClick={() => setSpecialtySearch('')}
                                    >
                                        x
                                    </button>
                                )}
                            </div>
                            {showSpecialtyDropdown && specialtySearch.trim() && (
                                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {filteredSpecialties.map(specialty => (
                                        <button
                                            key={specialty.id}
                                            type="button"
                                            className="w-full text-left px-4 py-2 hover:bg-base-200 text-sm"
                                            onMouseDown={() =>
                                                handleSelectSpecialty(specialty)
                                            }
                                        >
                                            {specialty.name}
                                        </button>
                                    ))}
                                    {!specialtySearchMatchesExisting &&
                                        specialtySearch.trim() && (
                                            <button
                                                type="button"
                                                className="w-full text-left px-4 py-2 hover:bg-base-200 text-sm text-secondary flex items-center gap-2 border-t border-base-300/50"
                                                onMouseDown={handleCreateSpecialty}
                                            >
                                                <MdAdd className="text-lg" />
                                                Criar especialidade: &quot;
                                                {specialtySearch.trim()}&quot;
                                            </button>
                                        )}
                                    {filteredSpecialties.length === 0 &&
                                        specialtySearchMatchesExisting && (
                                            <div className="px-4 py-2 text-sm text-base-content/50">
                                                Nenhum resultado
                                            </div>
                                        )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Hospitais padrão (todas as categorias da saúde, menos mercadoria:
                    produto não é realizado em lugar nenhum). */}
                {isHealth && !isProduct && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-1">
                            Hospitais padrão
                        </h2>
                        <p className="text-sm text-base-content/60 mb-4">
                            Vincule os hospitais onde este item costuma ser
                            realizado. Eles aparecerão como sugestão ao criar um
                            agendamento.
                        </p>
                        <HospitalSearchSelector
                            multi
                            label=""
                            placeholder="Buscar e adicionar hospital..."
                            value={formData.hospitalIds}
                            onChange={ids =>
                                setFormData(prev => ({
                                    ...prev,
                                    hospitalIds: ids,
                                }))
                            }
                        />
                    </div>
                )}

                {/* ==================== PRODUTO ====================
                    Só a mercadoria tem identificação, fiscal, logística e estoque. Preço,
                    parceiros e profissionais NÃO ficam aqui: o produto usa o mesmo modelo
                    de preço do serviço (seções abaixo), e é isso que permite revenda,
                    dropshipping e produto de terceiro com margem própria. */}
                {isProduct && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Identificação e classificação
                        </h2>
                        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">SKU</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Código interno"
                                    value={formData.sku}
                                    onChange={e =>
                                        setFormData(prev => ({ ...prev, sku: e.target.value }))
                                    }
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Código de barras
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="EAN / GTIN"
                                    value={formData.barcode}
                                    onChange={e =>
                                        setFormData(prev => ({ ...prev, barcode: e.target.value }))
                                    }
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">Marca</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={formData.brand}
                                    onChange={e =>
                                        setFormData(prev => ({ ...prev, brand: e.target.value }))
                                    }
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Unidade de medida
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.unit}
                                    onChange={e =>
                                        setFormData(prev => ({ ...prev, unit: e.target.value }))
                                    }
                                >
                                    {PRODUCT_UNITS.map(u => (
                                        <option key={u.value} value={u.value}>
                                            {u.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-control md:col-span-2">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Etiquetas de busca
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="separadas por vírgula"
                                    value={formData.productTags}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            productTags: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>

                        <div className="divider" />

                        {/* A categoria do produto é a MESMA que a loja usa no menu, nas
                            promoções e nos cupons — por isso continua sendo a categoria
                            de produto, e não o "tipo" do catálogo (que agrupa serviço). */}
                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                            <ProductCategorySelector
                                label="Categoria (menu da loja)"
                                value={formData.productCategoryId}
                                onChange={id =>
                                    setFormData(prev => ({
                                        ...prev,
                                        productCategoryId: id,
                                        // Trocar a categoria invalida a subcategoria: ela
                                        // pertence à anterior.
                                        productSubcategoryId: null,
                                    }))
                                }
                            />
                            <ProductCategorySelector
                                label="Subcategoria"
                                parentId={formData.productCategoryId}
                                value={formData.productSubcategoryId}
                                onChange={id =>
                                    setFormData(prev => ({
                                        ...prev,
                                        productSubcategoryId: id,
                                    }))
                                }
                            />
                        </div>

                        <div className="divider" />

                        {/* Fornecedor: quem ENTREGA e recebe o repasse na baixa da
                            cobrança. Diferente do parceiro (que revende com preço
                            próprio) — juntos, são o desenho de dropshipping. */}
                        <SupplierSelector
                            label="Fornecedor (recebe o repasse)"
                            value={formData.supplierId}
                            onChange={id =>
                                setFormData(prev => ({ ...prev, supplierId: id }))
                            }
                        />
                    </div>
                )}

                {isProduct && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-1">
                            Fiscal, dimensões e estoque
                        </h2>
                        <p className="text-sm text-base-content/60 mb-4">
                            Fiscal alimenta a NF-e; peso e medidas alimentam o cálculo do
                            frete. O estoque é <strong>desta filial</strong> — cada unidade
                            tem o seu.
                        </p>

                        <div className="grid grid-cols-1 gap-5 md:grid-cols-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">NCM</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={formData.ncm}
                                    onChange={e =>
                                        setFormData(prev => ({ ...prev, ncm: e.target.value }))
                                    }
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">CEST</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={formData.cest}
                                    onChange={e =>
                                        setFormData(prev => ({ ...prev, cest: e.target.value }))
                                    }
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">CFOP</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={formData.cfop}
                                    onChange={e =>
                                        setFormData(prev => ({ ...prev, cfop: e.target.value }))
                                    }
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">Origem</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.productOrigin}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            productOrigin: e.target.value,
                                        }))
                                    }
                                >
                                    <option value="">Não informada</option>
                                    {PRODUCT_ORIGINS.map(o => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="divider" />

                        <div className="grid grid-cols-1 gap-5 md:grid-cols-4">
                            {PRODUCT_DIMENSIONS.map(dim => (
                                <div className="form-control" key={dim.field}>
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            {dim.label}
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        className="input input-bordered w-full"
                                        value={
                                            (formData as Record<string, any>)[dim.field] ?? ''
                                        }
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                [dim.field]: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="divider" />

                        <label className="label cursor-pointer justify-start gap-3">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-secondary"
                                checked={formData.stockEnabled}
                                onChange={e =>
                                    setFormData(prev => ({
                                        ...prev,
                                        stockEnabled: e.target.checked,
                                    }))
                                }
                            />
                            <div>
                                <span className="label-text font-medium">
                                    Controlar estoque nesta filial
                                </span>
                                <p className="text-base-content/50 text-xs">
                                    A venda não dá baixa automática — o estoque aqui é
                                    informativo e serve ao alerta de mínimo.
                                </p>
                            </div>
                        </label>

                        {formData.stockEnabled && (
                            <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Quantidade em estoque
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        className="input input-bordered w-full"
                                        value={formData.stockQuantity}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                stockQuantity: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Estoque mínimo (alerta)
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        className="input input-bordered w-full"
                                        value={formData.minStock}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                minStock: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                            </div>
                        )}

                        <div className="divider" />

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Garantia</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex.: 12 meses contra defeito de fabricação"
                                value={formData.warranty}
                                onChange={e =>
                                    setFormData(prev => ({
                                        ...prev,
                                        warranty: e.target.value,
                                    }))
                                }
                            />
                        </div>

                        <div className="divider" />

                        <div>
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <h3 className="font-medium text-base-content">
                                        {isVariationMode
                                            ? 'Opções desta variação'
                                            : 'Características'}
                                    </h3>
                                    <p className="text-base-content/50 text-xs mt-0.5">
                                        {isVariationMode
                                            ? 'Ex.: Cor = Azul e Tamanho = M. Com as mesmas opções em todas as variações, a loja mostra um seletor por opção. A bolinha ao lado dá a cor que a loja mostra na opção (opcional; marcar numa variação vale para todas com o mesmo valor).'
                                            : 'Aparecem na página do produto na loja. Ex.: Material = Algodão.'}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm gap-1 shrink-0"
                                    onClick={() =>
                                        setFormData(prev => ({
                                            ...prev,
                                            attributes: [
                                                ...prev.attributes,
                                                { key: '', value: '' },
                                            ],
                                        }))
                                    }
                                >
                                    <MdAdd className="text-lg" />
                                    Adicionar
                                </button>
                            </div>
                            {formData.attributes.length > 0 && (
                                <div className="mt-3 flex flex-col gap-2">
                                    {formData.attributes.map((attr, index) => (
                                        <div
                                            key={index}
                                            className={`grid gap-2 items-center ${isVariationMode ? 'grid-cols-[1fr_1fr_auto_auto]' : 'grid-cols-[1fr_1fr_auto]'}`}
                                        >
                                            <input
                                                type="text"
                                                className="input input-bordered input-sm w-full"
                                                placeholder={isVariationMode ? 'Ex.: Cor' : 'Ex.: Material'}
                                                value={attr.key}
                                                onChange={e =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        attributes: prev.attributes.map((item, i) =>
                                                            i === index
                                                                ? { ...item, key: e.target.value }
                                                                : item
                                                        ),
                                                    }))
                                                }
                                            />
                                            <input
                                                type="text"
                                                className="input input-bordered input-sm w-full"
                                                placeholder={isVariationMode ? 'Ex.: Azul' : 'Ex.: Algodão'}
                                                value={attr.value}
                                                onChange={e =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        attributes: prev.attributes.map((item, i) =>
                                                            i === index
                                                                ? { ...item, value: e.target.value }
                                                                : item
                                                        ),
                                                    }))
                                                }
                                            />
                                            {isVariationMode && (
                                                <div className="flex items-center gap-0.5">
                                                    <label
                                                        className="btn btn-ghost btn-sm btn-square"
                                                        title={attr.color ? `Cor na loja: ${attr.color}` : 'Cor na loja (opcional)'}
                                                    >
                                                        <span
                                                            className={`h-5 w-5 rounded-full border ${attr.color ? 'border-base-300' : 'border-dashed border-base-content/40'}`}
                                                            style={attr.color ? { backgroundColor: attr.color } : undefined}
                                                        />
                                                        <input
                                                            type="color"
                                                            className="sr-only"
                                                            value={attr.color || '#000000'}
                                                            onChange={e =>
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    attributes: prev.attributes.map((item, i) =>
                                                                        i === index
                                                                            ? { ...item, color: e.target.value }
                                                                            : item
                                                                    ),
                                                                }))
                                                            }
                                                        />
                                                    </label>
                                                    {attr.color && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-ghost btn-xs btn-square"
                                                            title="Tirar a cor"
                                                            onClick={() =>
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    attributes: prev.attributes.map((item, i) =>
                                                                        i === index ? { key: item.key, value: item.value } : item
                                                                    ),
                                                                }))
                                                            }
                                                        >
                                                            <MdClose />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm btn-square text-error"
                                                title="Remover"
                                                onClick={() =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        attributes: prev.attributes.filter(
                                                            (_, i) => i !== index
                                                        ),
                                                    }))
                                                }
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Produto com variações se vende pela variação: a promoção é dela. */}
                {isProduct &&
                    isEdit &&
                    id &&
                    user?.companyId &&
                    (isVariationMode || variations.length === 0) && (
                        <CatalogPromotionSection
                            key={`${id}-${savedCompanyPrice?.sellingPrice ?? 0}`}
                            catalogId={parseInt(id)}
                            companyId={user.companyId}
                            sellingPrice={savedCompanyPrice?.sellingPrice ?? 0}
                            initial={{
                                promotionalPrice: savedCompanyPrice?.promotionalPrice,
                                promotionalStart: savedCompanyPrice?.promotionalStart,
                                promotionalEnd: savedCompanyPrice?.promotionalEnd,
                            }}
                        />
                    )}

                {/* === Lojas online ===
                    A vitrine lista a partir de `StoreProduct`: sem loja marcada aqui, o
                    produto existe no catálogo e NÃO aparece em loja nenhuma. */}
                {isProduct && !isVariationMode && availableStores.length > 0 && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-1">
                            Lojas online
                        </h2>
                        <p className="text-sm text-base-content/60 mb-4">
                            Em quais lojas este produto aparece. Sem nenhuma marcada, ele
                            fica só no cadastro interno — não vai para a vitrine.
                        </p>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {availableStores.map(store => {
                                const checked = selectedStoreIds.includes(store.id)
                                return (
                                    <label
                                        key={store.id}
                                        className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                                            checked
                                                ? 'border-secondary bg-secondary/5'
                                                : 'border-base-300 hover:border-secondary/50'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-secondary checkbox-sm"
                                            checked={checked}
                                            onChange={e =>
                                                setSelectedStoreIds(prev =>
                                                    e.target.checked
                                                        ? [...prev, store.id]
                                                        : prev.filter(
                                                              sid => sid !== store.id
                                                          )
                                                )
                                            }
                                        />
                                        <div className="flex items-center gap-2 min-w-0">
                                            {store.logo ? (
                                                <img
                                                    src={store.logo}
                                                    alt={store.name}
                                                    className="h-8 w-8 rounded object-cover shrink-0"
                                                />
                                            ) : (
                                                <div className="h-8 w-8 rounded bg-base-200 flex items-center justify-center shrink-0">
                                                    <MdStorefront className="text-base-content/30" />
                                                </div>
                                            )}
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium truncate">
                                                    {store.name}
                                                </p>
                                                <p className="text-xs text-base-content/50 truncate">
                                                    {store.customDomain ||
                                                        `${store.subdomain}`}
                                                </p>
                                            </div>
                                        </div>
                                    </label>
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* Campos condicionais: Exame */}
                {showExamFields && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Instruções do {ExamLabel}
                        </h2>

                        {/* Material de coleta: o pedido impresso sai AGRUPADO por ele
                            ("Material: Sangue", depois "Material: Fezes"), porque é o
                            material que define o tubo no laboratório. Sem preencher, o
                            exame cai num grupo "Outros" no fim da folha. */}
                        {isHealth && (
                            <div className="form-control mb-6">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Material de coleta
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    list="collection-materials"
                                    className="input input-bordered w-full"
                                    placeholder="Ex.: Sangue"
                                    value={formData.collectionMaterial}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            collectionMaterial: e.target.value,
                                        }))
                                    }
                                />
                                {/* Sugestões, não lista fechada: a relação real cresce
                                    por especialidade e o médico precisa poder digitar
                                    o material que falta sem esperar um deploy. */}
                                <datalist id="collection-materials">
                                    {COLLECTION_MATERIALS.map(material => (
                                        <option key={material} value={material} />
                                    ))}
                                </datalist>
                                <span className="text-xs text-base-content/50 mt-1">
                                    Agrupa o pedido impresso. Deixe vazio nos exames
                                    que não coletam material (imagem, por exemplo).
                                </span>
                            </div>
                        )}

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Instruções
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder={
                                    isHealth
                                        ? 'Ex: Jejum de 12 horas, não ingerir bebidas alcoólicas...'
                                        : 'Ex.: o que precisa ser feito ou trazido antes do atendimento...'
                                }
                                rows={4}
                                value={formData.instructions}
                                onChange={e =>
                                    setFormData(prev => ({
                                        ...prev,
                                        instructions: e.target.value,
                                    }))
                                }
                            />
                        </div>
                    </div>
                )}

                {/* Campos condicionais: Procedimento */}
                {showOrderFields && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Instruções do {OrderLabel}
                        </h2>
                        <div className="grid grid-cols-1 gap-6">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Instruções Pré-{OrderLabel}
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder={
                                        isHealth
                                            ? 'Ex: Jejum de 8 horas, não ingerir bebidas alcoólicas...'
                                            : 'Ex.: o que o cliente precisa fazer ou trazer antes...'
                                    }
                                    rows={3}
                                    value={formData.preInstructions}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            preInstructions: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Instruções Pós-{OrderLabel}
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder={
                                        isHealth
                                            ? 'Ex: Repouso absoluto por 24 horas, aplicar gelo no local...'
                                            : 'Ex.: cuidados e recomendações depois do atendimento...'
                                    }
                                    rows={3}
                                    value={formData.postInstructions}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            postInstructions: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Campos condicionais: Teleconsulta */}
                {showTeleFields && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Configurações de Teleconsulta
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Duração Máxima (segundos)
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    className="input input-bordered w-full"
                                    placeholder="Ex: 3600"
                                    value={formData.maxTeleDuration}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            maxTeleDuration: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="flex flex-col gap-4 justify-center">
                                <label className="cursor-pointer flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary"
                                        checked={formData.enableChat}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                enableChat: e.target.checked,
                                            }))
                                        }
                                    />
                                    <span className="label-text font-medium">
                                        Habilitar chat
                                    </span>
                                </label>

                                <label className="cursor-pointer flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary"
                                        checked={formData.enableScreenShare}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                enableScreenShare:
                                                    e.target.checked,
                                            }))
                                        }
                                    />
                                    <span className="label-text font-medium">
                                        Habilitar compartilhamento de tela
                                    </span>
                                </label>

                                <label className="cursor-pointer flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary"
                                        checked={formData.recordingEnabled}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                recordingEnabled:
                                                    e.target.checked,
                                            }))
                                        }
                                    />
                                    <span className="label-text font-medium">
                                        Habilitar gravação
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                )}

                {/* Materiais e Anestesista (o anestesista só na saúde) */}
                {showMedicalSuppliesSection && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            {isHealth ? 'Materiais e Anestesista' : 'Materiais'}
                        </h2>

                        {/* Valor do Anestesista e quem recebe.
                            O destinatário aqui é PADRÃO: o agendamento herda e pode trocar.
                            Lá ele é obrigatório quando há taxa — é o que gera o repasse.
                            Ver docs/billing-system.md §7.

                            Só na saúde: fora dela não há anestesista, e o campo levaria o
                            operador a lançar uma taxa que nenhum atendimento explica. */}
                        {isHealth && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Valor do Anestesista (opcional)
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="R$ 0,00"
                                        value={formData.anesthetistFee}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                anesthetistFee: currencyFormat(
                                                    e.target.value,
                                                    true
                                                ),
                                            }))
                                        }
                                    />
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Quem recebe (padrão)
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={anesthetistRecipientKind ?? ''}
                                        onChange={e => {
                                            setAnesthetistRecipientKind(
                                                (e.target.value || null) as
                                                    | 'HOSPITAL'
                                                    | 'PROFESSIONAL'
                                                    | 'PARTNER'
                                                    | null
                                            )
                                            // Trocar o tipo invalida o destinatário anterior.
                                            setAnesthetistRecipientId(null)
                                        }}
                                    >
                                        <option value="">
                                            Definir no agendamento
                                        </option>
                                        <option value="HOSPITAL">
                                            Hospital
                                        </option>
                                        {canSeeProfessionals && (
                                            <option value="PROFESSIONAL">
                                                Profissional
                                            </option>
                                        )}
                                        {canSeePartners && (
                                            <option value="PARTNER">
                                                Parceiro
                                            </option>
                                        )}
                                    </select>
                                </div>

                                <div className="form-control">
                                    {anesthetistRecipientKind ===
                                        'HOSPITAL' && (
                                        <HospitalSearchSelector
                                            value={anesthetistRecipientId}
                                            label="Hospital"
                                            onChange={id =>
                                                setAnesthetistRecipientId(id)
                                            }
                                        />
                                    )}
                                    {anesthetistRecipientKind ===
                                        'PROFESSIONAL' && (
                                        <ProfessionalSearchSelector
                                            value={anesthetistRecipientId}
                                            label="Profissional"
                                            onChange={id =>
                                                setAnesthetistRecipientId(id)
                                            }
                                        />
                                    )}
                                    {anesthetistRecipientKind === 'PARTNER' && (
                                        <PartnerSelector
                                            value={anesthetistRecipientId}
                                            label="Parceiro"
                                            onChange={id =>
                                                setAnesthetistRecipientId(id)
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Busca de materiais */}
                        <div className="relative mb-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Adicionar Material
                                </span>
                            </label>
                            <div className="relative">
                                <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/50 text-lg pointer-events-none z-10" />
                                <input
                                    type="text"
                                    className="input input-bordered w-full pl-10"
                                    placeholder="Buscar material por nome..."
                                    value={medicalSupplySearch}
                                    onChange={async e => {
                                        setMedicalSupplySearch(e.target.value)
                                        if (
                                            e.target.value.trim().length >= 2 &&
                                            user?.organizationId
                                        ) {
                                            setLoadingMedicalSupplies(true)
                                            try {
                                                const res =
                                                    await searchMedicalSupplies(
                                                        user.organizationId,
                                                        e.target.value.trim()
                                                    )
                                                if (res.success) {
                                                    setMedicalSupplyResults(
                                                        res.result.filter(
                                                            ms =>
                                                                !catalogMedicalSupplies.some(
                                                                    cms =>
                                                                        cms.medicalSupplyId ===
                                                                        ms.id
                                                                )
                                                        )
                                                    )
                                                }
                                            } finally {
                                                setLoadingMedicalSupplies(false)
                                            }
                                            setShowMedicalSupplyDropdown(true)
                                        } else {
                                            setShowMedicalSupplyDropdown(false)
                                        }
                                    }}
                                    onFocus={() => {
                                        if (medicalSupplyResults.length > 0)
                                            setShowMedicalSupplyDropdown(true)
                                    }}
                                    onBlur={() =>
                                        setTimeout(
                                            () =>
                                                setShowMedicalSupplyDropdown(
                                                    false
                                                ),
                                            200
                                        )
                                    }
                                />
                            </div>
                            {showMedicalSupplyDropdown && (
                                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {loadingMedicalSupplies ? (
                                        <div className="px-4 py-3 text-sm text-base-content/50 text-center">
                                            <span className="loading loading-spinner loading-xs"></span>{' '}
                                            Buscando...
                                        </div>
                                    ) : medicalSupplyResults.length === 0 ? (
                                        <div className="px-4 py-3 text-sm text-base-content/50 text-center">
                                            Nenhum material encontrado
                                        </div>
                                    ) : (
                                        medicalSupplyResults.map(ms => (
                                            <button
                                                key={ms.id}
                                                type="button"
                                                className="w-full text-left px-4 py-2 hover:bg-base-200 text-sm flex items-center justify-between"
                                                onMouseDown={async () => {
                                                    const newItem: CatalogMedicalSupply =
                                                        {
                                                            id: 0,
                                                            catalogId: parseInt(
                                                                id || '0'
                                                            ),
                                                            medicalSupplyId:
                                                                ms.id,
                                                            medicalSupply: {
                                                                id: ms.id,
                                                                name: ms.name,
                                                                type: ms.type,
                                                                unit: ms.unit,
                                                                quantity:
                                                                    ms.quantity,
                                                                costPrice:
                                                                    ms.costPrice,
                                                                sellingPrice:
                                                                    ms.sellingPrice,
                                                            },
                                                            quantity: 1,
                                                            // Venda, não custo: este
                                                            // valor é copiado para o
                                                            // agendamento e entra no
                                                            // total do paciente.
                                                            unitPrice:
                                                                ms.sellingPrice ||
                                                                ms.costPrice ||
                                                                0,
                                                            notes: null,
                                                            createdAt: '',
                                                            updatedAt: '',
                                                        }

                                                    if (isEdit && id) {
                                                        const res =
                                                            await addCatalogMedicalSupply(
                                                                parseInt(id),
                                                                {
                                                                    medicalSupplyId:
                                                                        ms.id,
                                                                    quantity: 1,
                                                                    unitPrice:
                                                                        ms.sellingPrice ||
                                                                        ms.costPrice ||
                                                                        0,
                                                                }
                                                            )
                                                        if (res.success) {
                                                            setCatalogMedicalSupplies(
                                                                prev => [
                                                                    ...prev,
                                                                    res.result,
                                                                ]
                                                            )
                                                            toast.success(
                                                                'Material adicionado'
                                                            )
                                                        } else {
                                                            toast.error(
                                                                res.error ||
                                                                    'Erro ao adicionar material'
                                                            )
                                                        }
                                                    } else {
                                                        setCatalogMedicalSupplies(
                                                            prev => [
                                                                ...prev,
                                                                newItem,
                                                            ]
                                                        )
                                                    }
                                                    setMedicalSupplySearch('')
                                                    setShowMedicalSupplyDropdown(
                                                        false
                                                    )
                                                }}
                                            >
                                                <span>{ms.name}</span>
                                                <span className="badge badge-sm badge-ghost">
                                                    {
                                                        MEDICAL_SUPPLY_TYPE_LABELS[
                                                            ms.type
                                                        ]
                                                    }
                                                </span>
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Lista de materiais vinculados */}
                        {catalogMedicalSupplies.length === 0 ? (
                            <p className="text-sm text-base-content/50 text-center py-4">
                                Nenhum material vinculado
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Material</th>
                                            <th>Tipo</th>
                                            <th className="w-24">Qtd</th>
                                            <th className="w-36">
                                                Preço Unit.
                                            </th>
                                            <th className="w-28 text-right">
                                                Subtotal
                                            </th>
                                            <th className="w-12"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {catalogMedicalSupplies.map(
                                            (cms, idx) => (
                                                <tr key={cms.medicalSupplyId}>
                                                    <td className="font-medium">
                                                        {cms.medicalSupply.name}
                                                    </td>
                                                    <td>
                                                        <span className="badge badge-sm badge-ghost">
                                                            {MEDICAL_SUPPLY_TYPE_LABELS[
                                                                cms
                                                                    .medicalSupply
                                                                    .type as keyof typeof MEDICAL_SUPPLY_TYPE_LABELS
                                                            ] ||
                                                                cms
                                                                    .medicalSupply
                                                                    .type}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            className="input input-bordered input-sm w-20"
                                                            value={cms.quantity}
                                                            onChange={async e => {
                                                                const qty =
                                                                    parseInt(
                                                                        e.target
                                                                            .value
                                                                    ) || 1
                                                                setCatalogMedicalSupplies(
                                                                    prev =>
                                                                        prev.map(
                                                                            (
                                                                                item,
                                                                                i
                                                                            ) =>
                                                                                i ===
                                                                                idx
                                                                                    ? {
                                                                                          ...item,
                                                                                          quantity:
                                                                                              qty,
                                                                                      }
                                                                                    : item
                                                                        )
                                                                )
                                                                if (
                                                                    isEdit &&
                                                                    id &&
                                                                    cms.id
                                                                ) {
                                                                    await updateCatalogMedicalSupply(
                                                                        parseInt(
                                                                            id
                                                                        ),
                                                                        cms.medicalSupplyId,
                                                                        {
                                                                            quantity:
                                                                                qty,
                                                                        }
                                                                    )
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="input input-bordered input-sm w-32"
                                                            value={
                                                                currencyFormat(
                                                                    cms.unitPrice.toString(),
                                                                    true
                                                                ) ||
                                                                currencyFormat(
                                                                    '0'
                                                                )
                                                            }
                                                            onChange={async e => {
                                                                const formatted =
                                                                    currencyFormat(
                                                                        e.target
                                                                            .value,
                                                                        true
                                                                    )
                                                                const cents =
                                                                    parseCurrencyToCents(
                                                                        formatted ||
                                                                            '0'
                                                                    )
                                                                setCatalogMedicalSupplies(
                                                                    prev =>
                                                                        prev.map(
                                                                            (
                                                                                item,
                                                                                i
                                                                            ) =>
                                                                                i ===
                                                                                idx
                                                                                    ? {
                                                                                          ...item,
                                                                                          unitPrice:
                                                                                              cents,
                                                                                      }
                                                                                    : item
                                                                        )
                                                                )
                                                                if (
                                                                    isEdit &&
                                                                    id &&
                                                                    cms.id
                                                                ) {
                                                                    await updateCatalogMedicalSupply(
                                                                        parseInt(
                                                                            id
                                                                        ),
                                                                        cms.medicalSupplyId,
                                                                        {
                                                                            unitPrice:
                                                                                cents,
                                                                        }
                                                                    )
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="text-right font-medium">
                                                        {new Intl.NumberFormat(
                                                            'pt-BR',
                                                            {
                                                                style: 'currency',
                                                                currency: 'BRL',
                                                            }
                                                        ).format(
                                                            (cms.quantity *
                                                                cms.unitPrice) /
                                                                100
                                                        )}
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="btn btn-ghost btn-xs text-error"
                                                            onClick={async () => {
                                                                if (
                                                                    isEdit &&
                                                                    id &&
                                                                    cms.id
                                                                ) {
                                                                    const res =
                                                                        await removeCatalogMedicalSupply(
                                                                            parseInt(
                                                                                id
                                                                            ),
                                                                            cms.medicalSupplyId
                                                                        )
                                                                    if (
                                                                        !res.success
                                                                    ) {
                                                                        toast.error(
                                                                            'Erro ao remover material'
                                                                        )
                                                                        return
                                                                    }
                                                                }
                                                                setCatalogMedicalSupplies(
                                                                    prev =>
                                                                        prev.filter(
                                                                            (
                                                                                _,
                                                                                i
                                                                            ) =>
                                                                                i !==
                                                                                idx
                                                                        )
                                                                )
                                                            }}
                                                        >
                                                            <MdDelete className="text-base" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td
                                                colSpan={4}
                                                className="text-right font-semibold"
                                            >
                                                Total Materiais:
                                            </td>
                                            <td className="text-right font-bold text-secondary">
                                                {new Intl.NumberFormat(
                                                    'pt-BR',
                                                    {
                                                        style: 'currency',
                                                        currency: 'BRL',
                                                    }
                                                ).format(
                                                    catalogMedicalSupplies.reduce(
                                                        (sum, cms) =>
                                                            sum +
                                                            cms.quantity *
                                                                cms.unitPrice,
                                                        0
                                                    ) / 100
                                                )}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Componentes do Pacote - somente para PACKAGE */}
                {showPackageItemsSection && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <MdScience className="text-xl text-success" />
                            <h2 className="text-lg font-semibold text-base-content">
                                Componentes do Pacote
                            </h2>
                            {packageItems.length > 0 && (
                                <span className="badge badge-sm badge-success">
                                    {packageItems.length}
                                </span>
                            )}
                        </div>

                        <p className="text-sm text-base-content/60 mb-4">
                            Adicione {exams}, {consultations} ou {orders} —
                            ou venda apenas o item como um pacote único, com os
                            preços definidos abaixo em parceiros ou em preço
                            próprio. Os componentes são{' '}
                            <strong>opcionais</strong>: quando informados, cada
                            um entra na guia com o seu código, e o cliente paga
                            o preço único do pacote.
                        </p>

                        {/* Tipo do componente.
                            Um dropdown só, e não uma busca por tipo espalhada pela tela: o
                            pacote comporta tudo o que um orçamento cirúrgico tem, e cada
                            tipo vira LINHA da mesma lista. */}
                        <div className="form-control mb-4 max-w-xs">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Adicionar componente
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={componentKind}
                                onChange={e => {
                                    setComponentKind(
                                        e.target.value as ComponentKind
                                    )
                                    // Cada tipo tem o seu formulário: limpar evita levar
                                    // valor de um para o outro.
                                    setNewComponentAmount('')
                                    setNewComponentRecipientKind(null)
                                    setNewComponentRecipientId(null)
                                    setNewSupply(null)
                                }}
                            >
                                <option value="ITEM">Item do catálogo</option>
                                <option value="PRODUCT">Produto</option>
                                {/*
                                 * "(OPME)" é sigla da ANS — Órteses, Próteses e
                                 * Materiais Especiais. Não diz nada num salão, numa
                                 * academia ou numa oficina.
                                 */}
                                <option value="SUPPLY">
                                    {isHealth ? 'Material (OPME)' : 'Material'}
                                </option>
                                {/*
                                 * Hospital e anestesista são componentes de orçamento
                                 * cirúrgico. Fora da saúde não existem, e oferecê-los
                                 * no dropdown leva o operador a montar um pacote que
                                 * ele não consegue explicar ao cliente.
                                 */}
                                {isHealth && (
                                    <>
                                        <option value="HOSPITAL">
                                            Hospital
                                        </option>
                                        <option value="ANESTHETIST">
                                            Anestesista
                                        </option>
                                    </>
                                )}
                            </select>
                        </div>

                        {/* Busca de componentes de catálogo */}
                        {componentKind === 'ITEM' && (
                            <div className="relative mb-4">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Adicionar Componente
                                    </span>
                                </label>
                                <div className="relative">
                                    <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/50 text-lg pointer-events-none" />
                                    <input
                                        type="text"
                                        className="input input-bordered w-full pl-10"
                                        placeholder={`Buscar ${exam}, ${consultation} ou ${order} por nome...`}
                                        value={packageSearch}
                                        onChange={async e => {
                                            setPackageSearch(e.target.value)
                                            if (
                                                e.target.value.trim().length >=
                                                    2 &&
                                                user?.companyId
                                            ) {
                                                setLoadingPackageSearch(true)
                                                try {
                                                    const res =
                                                        await getCatalogsByCompany(
                                                            user.companyId,
                                                            {
                                                                search: e.target.value.trim(),
                                                                activeOnly: true,
                                                                isListing: true,
                                                            }
                                                        )
                                                    if (res.success) {
                                                        const existingIds =
                                                            new Set(
                                                                packageItems.map(
                                                                    pi =>
                                                                        pi.itemId
                                                                )
                                                            )
                                                        // ⚠️ NÃO filtrar por `tussCode`. O
                                                        // backend aceita componente sem TUSS
                                                        // desde 2026-07-11, justamente porque
                                                        // estética e fisioterapia (mastopexia,
                                                        // drenagem, peeling) NÃO têm código na
                                                        // Tabela 22 — o rol da ANS só cobre a
                                                        // medicina reparadora. O filtro antigo
                                                        // escondia todos os procedimentos
                                                        // estéticos da busca de componentes.
                                                        setPackageSearchResults(
                                                            (
                                                                res.result || []
                                                            ).filter(
                                                                c =>
                                                                    c.category !==
                                                                        'PACKAGE' &&
                                                                    !existingIds.has(
                                                                        c.id
                                                                    )
                                                            )
                                                        )
                                                    }
                                                } finally {
                                                    setLoadingPackageSearch(
                                                        false
                                                    )
                                                }
                                                setShowPackageDropdown(true)
                                            } else {
                                                setShowPackageDropdown(false)
                                            }
                                        }}
                                        onFocus={() => {
                                            if (packageSearchResults.length > 0)
                                                setShowPackageDropdown(true)
                                        }}
                                        onBlur={() =>
                                            setTimeout(
                                                () =>
                                                    setShowPackageDropdown(
                                                        false
                                                    ),
                                                200
                                            )
                                        }
                                    />
                                </div>
                                {showPackageDropdown && (
                                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                        {loadingPackageSearch ? (
                                            <div className="px-4 py-3 text-sm text-base-content/50 text-center">
                                                <span className="loading loading-spinner loading-xs"></span>{' '}
                                                Buscando...
                                            </div>
                                        ) : packageSearchResults.length ===
                                          0 ? (
                                            <div className="px-4 py-3 text-sm text-base-content/50 text-center">
                                                Nenhum exame, consulta ou
                                                procedimento encontrado
                                            </div>
                                        ) : (
                                            packageSearchResults.map(item => (
                                                <button
                                                    key={item.id}
                                                    type="button"
                                                    className="w-full text-left px-4 py-2 hover:bg-base-200 text-sm flex items-center justify-between"
                                                    onMouseDown={async () => {
                                                        if (isEdit && id) {
                                                            const res =
                                                                await addCatalogPackageItem(
                                                                    parseInt(
                                                                        id
                                                                    ),
                                                                    {
                                                                        itemId: item.id,
                                                                    }
                                                                )
                                                            if (res.success) {
                                                                setPackageItems(
                                                                    prev => [
                                                                        ...prev,
                                                                        res.result,
                                                                    ]
                                                                )
                                                                toast.success(
                                                                    'Componente adicionado'
                                                                )
                                                            } else {
                                                                toast.error(
                                                                    res.error ||
                                                                        'Erro ao adicionar componente'
                                                                )
                                                            }
                                                        } else {
                                                            const newItem: CatalogPackageItem =
                                                                {
                                                                    id: 0,
                                                                    packageId: 0,
                                                                    itemId: item.id,
                                                                    item: {
                                                                        id: item.id,
                                                                        name: item.name,
                                                                        category:
                                                                            item.category,
                                                                        tussCode:
                                                                            item.tussCode,
                                                                        companyPrices:
                                                                            item.companyPrices,
                                                                        // Quem executa este item.
                                                                        // Sem isto o seletor de
                                                                        // parceiro nasceria vazio
                                                                        // num pacote em criação.
                                                                        partners:
                                                                            (
                                                                                item.partners ||
                                                                                []
                                                                            ).map(
                                                                                cp => ({
                                                                                    partnerId:
                                                                                        cp.partnerId,
                                                                                    price: cp.price,
                                                                                    active: cp.active,
                                                                                    partner:
                                                                                        cp.partner
                                                                                            ? {
                                                                                                  id: cp
                                                                                                      .partner
                                                                                                      .id,
                                                                                                  name: cp
                                                                                                      .partner
                                                                                                      .name,
                                                                                                  tradingName:
                                                                                                      cp
                                                                                                          .partner
                                                                                                          .tradingName,
                                                                                              }
                                                                                            : null,
                                                                                })
                                                                            ),
                                                                        catalogType:
                                                                            item.catalogType,
                                                                    },
                                                                    quantity: 1,
                                                                    complimentary: false,
                                                                    sortOrder:
                                                                        packageItems.length,
                                                                    createdAt:
                                                                        '',
                                                                    updatedAt:
                                                                        '',
                                                                }
                                                            setPackageItems(
                                                                prev => [
                                                                    ...prev,
                                                                    newItem,
                                                                ]
                                                            )
                                                        }
                                                        setPackageSearch('')
                                                        setShowPackageDropdown(
                                                            false
                                                        )
                                                    }}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">
                                                            {item.name}
                                                        </span>
                                                        <span className="badge badge-xs badge-ghost">
                                                            {
                                                                categoryLabels[
                                                                    item
                                                                        .category
                                                                ]
                                                            }
                                                        </span>
                                                        {item.tussCode && (
                                                            <span className="text-xs text-base-content/50">
                                                                TUSS{' '}
                                                                {item.tussCode}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="text-xs font-semibold text-secondary">
                                                        {new Intl.NumberFormat(
                                                            'pt-BR',
                                                            {
                                                                style: 'currency',
                                                                currency: 'BRL',
                                                            }
                                                        ).format(
                                                            catalogBasePrice(
                                                                item,
                                                                user?.companyId
                                                            ) / 100
                                                        )}
                                                    </span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Busca de PRODUTOS */}
                        {componentKind === 'PRODUCT' && (
                            <div className="relative mb-4">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Adicionar Produto
                                    </span>
                                </label>
                                <div className="relative">
                                    <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/50 text-lg pointer-events-none" />
                                    <input
                                        type="text"
                                        className="input input-bordered w-full pl-10"
                                        placeholder="Buscar produto por nome..."
                                        value={packageProductSearch}
                                        onChange={async e => {
                                            setPackageProductSearch(
                                                e.target.value
                                            )
                                            if (
                                                e.target.value.trim().length >=
                                                    2 &&
                                                user?.companyId
                                            ) {
                                                setLoadingPackageProductSearch(
                                                    true
                                                )
                                                try {
                                                    // Mercadoria é item de catálogo
                                                    // (`category = PRODUCT`) desde a
                                                    // unificação.
                                                    const res =
                                                        await getCatalogsByCompany(
                                                            user.companyId,
                                                            {
                                                                category:
                                                                    'PRODUCT',
                                                                activeOnly: true,
                                                                isListing: true,
                                                                search: e.target.value.trim(),
                                                                limit: 20,
                                                            }
                                                        )
                                                    if (res.success) {
                                                        const alreadyAdded =
                                                            new Set(
                                                                packageItems
                                                                    .filter(
                                                                        pi =>
                                                                            pi.kind ===
                                                                            'PRODUCT'
                                                                    )
                                                                    .map(
                                                                        pi =>
                                                                            pi.itemId
                                                                    )
                                                                    .filter(
                                                                        Boolean
                                                                    )
                                                            )
                                                        setPackageProductResults(
                                                            (
                                                                res.result || []
                                                            ).filter(
                                                                p =>
                                                                    !alreadyAdded.has(
                                                                        p.id
                                                                    )
                                                            )
                                                        )
                                                    }
                                                } finally {
                                                    setLoadingPackageProductSearch(
                                                        false
                                                    )
                                                }
                                                setShowPackageProductDropdown(
                                                    true
                                                )
                                            } else {
                                                setShowPackageProductDropdown(
                                                    false
                                                )
                                            }
                                        }}
                                        onFocus={() => {
                                            if (
                                                packageProductResults.length > 0
                                            )
                                                setShowPackageProductDropdown(
                                                    true
                                                )
                                        }}
                                        onBlur={() =>
                                            setTimeout(
                                                () =>
                                                    setShowPackageProductDropdown(
                                                        false
                                                    ),
                                                200
                                            )
                                        }
                                    />
                                </div>
                                {showPackageProductDropdown && (
                                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                        {loadingPackageProductSearch ? (
                                            <div className="px-4 py-3 text-sm text-base-content/50 text-center">
                                                <span className="loading loading-spinner loading-xs"></span>{' '}
                                                Buscando...
                                            </div>
                                        ) : packageProductResults.length ===
                                          0 ? (
                                            <div className="px-4 py-3 text-sm text-base-content/50 text-center">
                                                Nenhum produto encontrado
                                            </div>
                                        ) : (
                                            packageProductResults.map(
                                                product => (
                                                    <button
                                                        key={product.id}
                                                        type="button"
                                                        className="w-full text-left px-4 py-2 hover:bg-base-200 text-sm flex items-center justify-between"
                                                        onMouseDown={async () => {
                                                            if (isEdit && id) {
                                                                const res =
                                                                    await addCatalogPackageItem(
                                                                        parseInt(
                                                                            id
                                                                        ),
                                                                        {
                                                                            // Mercadoria entra
                                                                            // pelo MESMO campo do
                                                                            // serviço; o `kind` é
                                                                            // que a distingue.
                                                                            itemId: product.id,
                                                                            kind: 'PRODUCT',
                                                                        }
                                                                    )
                                                                if (
                                                                    res.success
                                                                ) {
                                                                    setPackageItems(
                                                                        prev => [
                                                                            ...prev,
                                                                            res.result,
                                                                        ]
                                                                    )
                                                                    toast.success(
                                                                        'Produto adicionado ao pacote'
                                                                    )
                                                                } else {
                                                                    toast.error(
                                                                        res.error ||
                                                                            'Erro ao adicionar produto'
                                                                    )
                                                                }
                                                            } else {
                                                                // Na criação o vínculo ainda não
                                                                // existe no banco: fica no estado e
                                                                // é gravado no submit.
                                                                setPackageItems(
                                                                    prev => [
                                                                        ...prev,
                                                                        {
                                                                            id: 0,
                                                                            packageId: 0,
                                                                            kind: 'PRODUCT',
                                                                            itemId: product.id,
                                                                            item: {
                                                                                id: product.id,
                                                                                name: product.name,
                                                                                category:
                                                                                    'PRODUCT',
                                                                                // Sem isto a linha
                                                                                // nasceria sem preço
                                                                                // até salvar e recarregar.
                                                                                companyPrices:
                                                                                    product.companyPrices,
                                                                            },
                                                                            quantity: 1,
                                                                            complimentary: false,
                                                                            sortOrder:
                                                                                prev.length,
                                                                            createdAt:
                                                                                '',
                                                                            updatedAt:
                                                                                '',
                                                                        },
                                                                    ]
                                                                )
                                                            }
                                                            setPackageProductSearch(
                                                                ''
                                                            )
                                                            setShowPackageProductDropdown(
                                                                false
                                                            )
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <span className="font-medium truncate">
                                                                {product.name}
                                                            </span>
                                                            <span className="badge badge-xs badge-info">
                                                                Produto
                                                            </span>
                                                        </div>
                                                        {/* Preço DESTA filial, igual ao dropdown de
                                                    item de catálogo — o operador escolhe o
                                                    componente sabendo quanto ele custa. */}
                                                        <span className="text-xs font-semibold text-secondary whitespace-nowrap">
                                                            {new Intl.NumberFormat(
                                                                'pt-BR',
                                                                {
                                                                    style: 'currency',
                                                                    currency:
                                                                        'BRL',
                                                                }
                                                            ).format(
                                                                catalogBasePrice(
                                                                    product,
                                                                    user?.companyId
                                                                ) / 100
                                                            )}
                                                        </span>
                                                    </button>
                                                )
                                            )
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Material (OPME) — o valor é o CUSTO: quem recebe é o fornecedor
                            do cadastro ou o hospital, quando o material sai de lá. */}
                        {componentKind === 'SUPPLY' && (
                            <div className="rounded-lg border border-base-300 p-4 mb-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Material
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                className="input input-bordered w-full"
                                                placeholder="Buscar material por nome..."
                                                value={
                                                    newSupply?.name ??
                                                    medicalSupplySearch
                                                }
                                                onChange={async e => {
                                                    setNewSupply(null)
                                                    setMedicalSupplySearch(
                                                        e.target.value
                                                    )
                                                    if (
                                                        e.target.value.trim()
                                                            .length >= 2 &&
                                                        user?.organizationId
                                                    ) {
                                                        const res =
                                                            await searchMedicalSupplies(
                                                                user.organizationId,
                                                                e.target.value
                                                            )
                                                        if (res.success) {
                                                            setMedicalSupplyResults(
                                                                res.result || []
                                                            )
                                                            setShowMedicalSupplyDropdown(
                                                                true
                                                            )
                                                        }
                                                    } else {
                                                        setShowMedicalSupplyDropdown(
                                                            false
                                                        )
                                                    }
                                                }}
                                            />
                                            {showMedicalSupplyDropdown &&
                                                medicalSupplyResults.length >
                                                    0 && (
                                                    <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                                                        {medicalSupplyResults.map(
                                                            ms => (
                                                                <button
                                                                    key={ms.id}
                                                                    type="button"
                                                                    className="w-full text-left p-3 hover:bg-base-200 border-b border-base-300/50 last:border-b-0"
                                                                    onClick={() => {
                                                                        setNewSupply(
                                                                            {
                                                                                id: ms.id,
                                                                                name: ms.name,
                                                                                costPrice:
                                                                                    ms.costPrice ??
                                                                                    0,
                                                                            }
                                                                        )
                                                                        // O custo do cadastro
                                                                        // é só o padrão: o
                                                                        // preço do OPME é
                                                                        // negociado.
                                                                        setNewComponentAmount(
                                                                            formatCentsToDisplay(
                                                                                ms.costPrice
                                                                            )
                                                                        )
                                                                        setShowMedicalSupplyDropdown(
                                                                            false
                                                                        )
                                                                        setMedicalSupplySearch(
                                                                            ''
                                                                        )
                                                                    }}
                                                                >
                                                                    <span className="font-medium text-sm">
                                                                        {
                                                                            ms.name
                                                                        }
                                                                    </span>
                                                                    <span className="text-xs text-base-content/60 ml-2">
                                                                        {formatCentsToDisplay(
                                                                            ms.costPrice
                                                                        ) ||
                                                                            'sem custo'}
                                                                    </span>
                                                                </button>
                                                            )
                                                        )}
                                                    </div>
                                                )}
                                        </div>
                                    </div>

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Custo unitário
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="R$ 0,00"
                                            value={newComponentAmount}
                                            onChange={e =>
                                                setNewComponentAmount(
                                                    currencyFormat(
                                                        e.target.value,
                                                        true
                                                    )
                                                )
                                            }
                                        />
                                        <span className="text-xs text-base-content/50 mt-1">
                                            Vazio usa o custo do cadastro.
                                        </span>
                                    </div>

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Quem recebe
                                            </span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={
                                                newComponentRecipientKind ??
                                                'SUPPLIER'
                                            }
                                            onChange={e => {
                                                setNewComponentRecipientKind(
                                                    e.target.value as any
                                                )
                                                setNewComponentRecipientId(null)
                                            }}
                                        >
                                            <option value="SUPPLIER">
                                                Fornecedor
                                            </option>
                                            {/* Material que sai do hospital é caso
                                                clínico (OPME). */}
                                            {isHealth && (
                                                <option value="HOSPITAL">
                                                    Hospital
                                                </option>
                                            )}
                                            {canSeeProfessionals && (
                                                <option value="PROFESSIONAL">
                                                    Profissional
                                                </option>
                                            )}
                                            {canSeePartners && (
                                                <option value="PARTNER">
                                                    Parceiro
                                                </option>
                                            )}
                                        </select>
                                    </div>

                                    <div className="form-control">
                                        {newComponentRecipientKind ===
                                            'HOSPITAL' && (
                                            <HospitalSearchSelector
                                                value={newComponentRecipientId}
                                                label="Hospital"
                                                onChange={hospitalId =>
                                                    setNewComponentRecipientId(
                                                        hospitalId
                                                    )
                                                }
                                            />
                                        )}
                                        {newComponentRecipientKind ===
                                            'PROFESSIONAL' && (
                                            <ProfessionalSearchSelector
                                                value={newComponentRecipientId}
                                                label="Profissional"
                                                onChange={professionalId =>
                                                    setNewComponentRecipientId(
                                                        professionalId
                                                    )
                                                }
                                            />
                                        )}
                                        {newComponentRecipientKind ===
                                            'PARTNER' && (
                                            <PartnerSelector
                                                value={newComponentRecipientId}
                                                label="Parceiro"
                                                onChange={partnerId =>
                                                    setNewComponentRecipientId(
                                                        partnerId
                                                    )
                                                }
                                            />
                                        )}
                                        {(newComponentRecipientKind ??
                                            'SUPPLIER') === 'SUPPLIER' && (
                                            <>
                                                <SupplierSelector
                                                    value={
                                                        newComponentRecipientId
                                                    }
                                                    label="Fornecedor"
                                                    placeholder="Padrão do cadastro do material"
                                                    onChange={supplierId =>
                                                        setNewComponentRecipientId(
                                                            supplierId
                                                        )
                                                    }
                                                />
                                                <span className="text-xs text-base-content/50 mt-1">
                                                    Vazio usa o fornecedor do
                                                    material. Aqui é só o
                                                    padrão: o agendamento pode
                                                    trocar
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Digitar na busca NÃO escolhe o material: é preciso
                                    clicar num resultado. Sem este aviso, o operador
                                    digitava, clicava num botão desabilitado e ia embora
                                    achando que tinha adicionado — o componente nunca
                                    chegava a ser enviado. */}
                                {!newSupply ? (
                                    <p className="text-xs text-warning mt-3">
                                        Escolha um material na lista da busca
                                        para habilitar o botão.
                                    </p>
                                ) : (newComponentRecipientKind ??
                                      'SUPPLIER') !== 'SUPPLIER' &&
                                  !newComponentRecipientId ? (
                                    /* Só o fornecedor tem padrão de onde herdar. Nos demais,
                                       componente sem destinatário é custo vendido que nunca
                                       vira conta a pagar — o backend recusa. */
                                    <p className="text-xs text-warning mt-3">
                                        Escolha quem recebe pelo material para
                                        habilitar o botão.
                                    </p>
                                ) : (
                                    /* O botão diz "Adicionar ao pacote"; aqui fica O QUE
                                       vai entrar. Antes ele dizia "Adicionar material", o
                                       que se lê como cadastrar um material NOVO em vez de
                                       incluir no pacote o que já foi escolhido acima. */
                                    <p className="text-xs text-base-content/60 mt-3">
                                        Inclui <strong>{newSupply.name}</strong>{' '}
                                        como componente deste pacote.
                                    </p>
                                )}

                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm mt-2"
                                    disabled={
                                        !newSupply ||
                                        ((newComponentRecipientKind ??
                                            'SUPPLIER') !== 'SUPPLIER' &&
                                            !newComponentRecipientId)
                                    }
                                    onClick={() =>
                                        addPackageComponent({
                                            kind: 'SUPPLY',
                                            medicalSupplyId: newSupply!.id,
                                            label: newSupply!.name,
                                            amount: newComponentAmount
                                                ? parseCurrencyToCents(
                                                      newComponentAmount
                                                  )
                                                : undefined,
                                            recipientKind:
                                                newComponentRecipientKind ??
                                                'SUPPLIER',
                                            recipientId:
                                                newComponentRecipientId ??
                                                undefined,
                                        })
                                    }
                                >
                                    <MdAdd className="text-lg" />
                                    Adicionar ao pacote
                                </button>
                            </div>
                        )}

                        {/* Hospital — diária/taxa do hospital, como CUSTO do pacote.
                            `isHealth` é redundante com o dropdown (que já não oferece o
                            tipo fora da saúde) e está aqui de propósito: um pacote antigo
                            reaberto pode trazer `componentKind` gravado. */}
                        {isHealth && componentKind === 'HOSPITAL' && (
                            <div className="rounded-lg border border-base-300 p-4 mb-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="form-control">
                                        <HospitalSearchSelector
                                            value={newComponentRecipientId}
                                            label="Hospital"
                                            onChange={hospitalId =>
                                                setNewComponentRecipientId(
                                                    hospitalId
                                                )
                                            }
                                        />
                                    </div>
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Valor do hospital
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="R$ 0,00"
                                            value={newComponentAmount}
                                            onChange={e =>
                                                setNewComponentAmount(
                                                    currencyFormat(
                                                        e.target.value,
                                                        true
                                                    )
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                                {(!newComponentRecipientId ||
                                    !newComponentAmount) && (
                                    <p className="text-xs text-warning mt-3">
                                        Informe o hospital e o valor para
                                        habilitar o botão.
                                    </p>
                                )}

                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm mt-2"
                                    disabled={
                                        !newComponentRecipientId ||
                                        !newComponentAmount
                                    }
                                    onClick={() =>
                                        addPackageComponent({
                                            kind: 'HOSPITAL',
                                            label: 'Hospital',
                                            amount: parseCurrencyToCents(
                                                newComponentAmount
                                            ),
                                            recipientKind: 'HOSPITAL',
                                            recipientId:
                                                newComponentRecipientId!,
                                        })
                                    }
                                >
                                    <MdAdd className="text-lg" />
                                    Adicionar ao pacote
                                </button>
                            </div>
                        )}

                        {/* Anestesista — o destinatário pode ser hospital, profissional ou
                            parceiro. Ver docs/billing-system.md §7.1. Mesma guarda do
                            hospital acima. */}
                        {isHealth && componentKind === 'ANESTHETIST' && (
                            <div className="rounded-lg border border-base-300 p-4 mb-4">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Quem recebe
                                            </span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={
                                                newComponentRecipientKind ?? ''
                                            }
                                            onChange={e => {
                                                setNewComponentRecipientKind(
                                                    (e.target.value ||
                                                        null) as any
                                                )
                                                setNewComponentRecipientId(null)
                                            }}
                                        >
                                            <option value="">
                                                Selecione...
                                            </option>
                                            <option value="HOSPITAL">
                                                Hospital
                                            </option>
                                            {canSeeProfessionals && (
                                                <option value="PROFESSIONAL">
                                                    Profissional
                                                </option>
                                            )}
                                            {canSeePartners && (
                                                <option value="PARTNER">
                                                    Parceiro
                                                </option>
                                            )}
                                        </select>
                                    </div>

                                    <div className="form-control">
                                        {newComponentRecipientKind ===
                                            'HOSPITAL' && (
                                            <HospitalSearchSelector
                                                value={newComponentRecipientId}
                                                label="Hospital"
                                                onChange={id =>
                                                    setNewComponentRecipientId(
                                                        id
                                                    )
                                                }
                                            />
                                        )}
                                        {newComponentRecipientKind ===
                                            'PROFESSIONAL' && (
                                            <ProfessionalSearchSelector
                                                value={newComponentRecipientId}
                                                label="Profissional"
                                                onChange={id =>
                                                    setNewComponentRecipientId(
                                                        id
                                                    )
                                                }
                                            />
                                        )}
                                        {newComponentRecipientKind ===
                                            'PARTNER' && (
                                            <PartnerSelector
                                                value={newComponentRecipientId}
                                                label="Parceiro"
                                                onChange={id =>
                                                    setNewComponentRecipientId(
                                                        id
                                                    )
                                                }
                                            />
                                        )}
                                    </div>

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Valor do anestesista
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="R$ 0,00"
                                            value={newComponentAmount}
                                            onChange={e =>
                                                setNewComponentAmount(
                                                    currencyFormat(
                                                        e.target.value,
                                                        true
                                                    )
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                                {(!newComponentRecipientKind ||
                                    !newComponentRecipientId ||
                                    !newComponentAmount) && (
                                    <p className="text-xs text-warning mt-3">
                                        Informe quem recebe, o destinatário e o
                                        valor para habilitar o botão.
                                    </p>
                                )}

                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm mt-2"
                                    disabled={
                                        !newComponentRecipientKind ||
                                        !newComponentRecipientId ||
                                        !newComponentAmount
                                    }
                                    onClick={() =>
                                        addPackageComponent({
                                            kind: 'ANESTHETIST',
                                            label: 'Anestesista',
                                            amount: parseCurrencyToCents(
                                                newComponentAmount
                                            ),
                                            recipientKind:
                                                newComponentRecipientKind!,
                                            recipientId:
                                                newComponentRecipientId!,
                                        })
                                    }
                                >
                                    <MdAdd className="text-lg" />
                                    Adicionar ao pacote
                                </button>
                            </div>
                        )}

                        {/* Lista de componentes */}
                        {packageItems.length === 0 ? (
                            <p className="text-sm text-base-content/50 text-center py-4">
                                Nenhum componente adicionado — o pacote será
                                vendido como item único, pelo preço definido
                                abaixo.
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Componente</th>
                                            <th>Categoria</th>
                                            <th className="text-right">
                                                Preço ref.
                                            </th>
                                            <th className="w-20">Qtd</th>
                                            <th className="text-center">
                                                Cortesia
                                            </th>
                                            <th>Execução</th>
                                            {isEdit && (
                                                <th
                                                    title={`Quando este componente acontece. Só na venda: a data é do ${clientLabel}, não do modelo do pacote.`}
                                                >
                                                    Data
                                                </th>
                                            )}
                                            <th
                                                className="text-right"
                                                title={`O que a ${isHealth ? 'clínica' : 'empresa'} paga a quem executa, já multiplicado pela quantidade`}
                                            >
                                                Custo (repasse)
                                            </th>
                                            <th className="w-12"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {packageItems.map((pi, idx) => (
                                            <tr
                                                key={`${pi.id}-${pi.itemId ?? pi.medicalSupplyId ?? idx}`}
                                                className={
                                                    pi.complimentary
                                                        ? 'opacity-60'
                                                        : ''
                                                }
                                            >
                                                <td className="font-medium">
                                                    {pi.item?.name ??
                                                        pi.medicalSupply
                                                            ?.name ??
                                                        (pi.kind === 'HOSPITAL'
                                                            ? 'Hospital'
                                                            : pi.kind ===
                                                                'ANESTHETIST'
                                                              ? 'Anestesista'
                                                              : '-')}
                                                </td>
                                                <td>
                                                    {/* Serviço e mercadoria são ambos item
                                                        de catálogo: o selo vem da categoria.
                                                        Material, hospital e anestesista não
                                                        têm item — vêm do `kind`. */}
                                                    <span
                                                        className={`badge badge-sm ${
                                                            pi.kind ===
                                                            'PRODUCT'
                                                                ? 'badge-info'
                                                                : pi.item
                                                                  ? 'badge-ghost'
                                                                  : 'badge-warning'
                                                        }`}
                                                    >
                                                        {pi.item
                                                            ? categoryLabels[
                                                                  pi.item
                                                                      .category
                                                              ]
                                                            : COMPONENT_KIND_LABELS[
                                                                  pi.kind ??
                                                                      'ITEM'
                                                              ]}
                                                    </span>
                                                </td>

                                                <td
                                                    className={`text-right ${pi.complimentary ? 'line-through text-base-content/40' : 'font-semibold text-secondary'}`}
                                                >
                                                    {/* Preço de referência DESTA filial,
                                                        para o operador compor o preço do
                                                        pacote. */}
                                                    {/* Material, hospital e anestesista não
                                                        têm preço de venda: entram como
                                                        CUSTO do pacote. */}
                                                    {!pi.item ? (
                                                        <span className="text-base-content/40 font-normal">
                                                            —
                                                        </span>
                                                    ) : (
                                                        new Intl.NumberFormat(
                                                            'pt-BR',
                                                            {
                                                                style: 'currency',
                                                                currency: 'BRL',
                                                            }
                                                        ).format(
                                                            catalogBasePrice(
                                                                {
                                                                    companyPrices:
                                                                        pi.item
                                                                            .companyPrices,
                                                                } as Catalog,
                                                                user?.companyId
                                                            ) / 100
                                                        )
                                                    )}
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        className="input input-bordered input-sm w-16"
                                                        value={pi.quantity}
                                                        onChange={async e => {
                                                            const qty =
                                                                parseInt(
                                                                    e.target
                                                                        .value
                                                                ) || 1
                                                            setPackageItems(
                                                                prev =>
                                                                    prev.map(
                                                                        (
                                                                            it,
                                                                            i
                                                                        ) =>
                                                                            i ===
                                                                            idx
                                                                                ? {
                                                                                      ...it,
                                                                                      quantity:
                                                                                          qty,
                                                                                  }
                                                                                : it
                                                                    )
                                                            )
                                                            if (
                                                                isEdit &&
                                                                id &&
                                                                pi.id
                                                            ) {
                                                                // id da LINHA do vínculo:
                                                                // `itemId` é nulo quando o
                                                                // componente é produto.
                                                                await updateCatalogPackageItem(
                                                                    parseInt(
                                                                        id
                                                                    ),
                                                                    pi.id,
                                                                    {
                                                                        quantity:
                                                                            qty,
                                                                    }
                                                                )
                                                            }
                                                        }}
                                                    />
                                                </td>
                                                <td className="text-center">
                                                    <input
                                                        type="checkbox"
                                                        className="toggle toggle-xs toggle-success"
                                                        checked={
                                                            pi.complimentary
                                                        }
                                                        onChange={async () => {
                                                            const newVal =
                                                                !pi.complimentary
                                                            setPackageItems(
                                                                prev =>
                                                                    prev.map(
                                                                        (
                                                                            it,
                                                                            i
                                                                        ) =>
                                                                            i ===
                                                                            idx
                                                                                ? {
                                                                                      ...it,
                                                                                      complimentary:
                                                                                          newVal,
                                                                                  }
                                                                                : it
                                                                    )
                                                            )
                                                            if (
                                                                isEdit &&
                                                                id &&
                                                                pi.id
                                                            ) {
                                                                await updateCatalogPackageItem(
                                                                    parseInt(
                                                                        id
                                                                    ),
                                                                    pi.id,
                                                                    {
                                                                        complimentary:
                                                                            newVal,
                                                                    }
                                                                )
                                                            }
                                                        }}
                                                        title="Marcar como cortesia (entra na guia, não soma no preço). O repasse continua devido."
                                                    />
                                                </td>
                                                <td>
                                                    {/* Produto não tem parceiro nem
                                                        profissional: quem recebe é o
                                                        FORNECEDOR, que vem do cadastro do
                                                        produto e não se escolhe aqui. */}
                                                    {pi.kind === 'PRODUCT' ? (
                                                        // Produto não tem execução a
                                                        // escolher: quem recebe é o
                                                        // fornecedor do cadastro.
                                                        <span className="text-base-content/40">
                                                            —
                                                        </span>
                                                    ) : !pi.item ? (
                                                        // Material, hospital e anestesista
                                                        // já trazem o destinatário na
                                                        // própria linha.
                                                        <span className="text-xs text-base-content/60">
                                                            {pi.recipientKind
                                                                ? PAYOUT_RECIPIENT_LABELS[
                                                                      pi
                                                                          .recipientKind
                                                                  ]
                                                                : 'Fornecedor do cadastro'}
                                                        </span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn btn-ghost btn-xs gap-1"
                                                            title="Definir parceiro e profissional deste componente"
                                                            onClick={() =>
                                                                setExecutionOpenIndex(
                                                                    executionOpenIndex ===
                                                                        idx
                                                                        ? null
                                                                        : idx
                                                                )
                                                            }
                                                        >
                                                            <MdPerson className="text-base" />
                                                            {pi.partnerId ||
                                                            pi.professionalId ? (
                                                                <span className="text-xs">
                                                                    {[
                                                                        pi
                                                                            .partner
                                                                            ?.tradingName ||
                                                                            pi
                                                                                .partner
                                                                                ?.name,
                                                                        pi
                                                                            .professional
                                                                            ?.name,
                                                                    ]
                                                                        .filter(
                                                                            Boolean
                                                                        )
                                                                        .join(
                                                                            ' · '
                                                                        )}
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs text-base-content/50">
                                                                    Definir
                                                                </span>
                                                            )}
                                                        </button>
                                                    )}
                                                </td>
                                                {isEdit && (
                                                    <td>
                                                        {/* Só componente CLÍNICO tem data:
                                                            produto, material, hospital e
                                                            anestesista não se agendam. */}
                                                        {pi.item ? (
                                                            <div className="flex flex-col gap-1">
                                                                <input
                                                                    type={
                                                                        componentSchedules[
                                                                            pi
                                                                                .id
                                                                        ]
                                                                            ?.arrivalOrder
                                                                            ? 'date'
                                                                            : 'datetime-local'
                                                                    }
                                                                    className="input input-bordered input-xs w-48"
                                                                    value={
                                                                        componentSchedules[
                                                                            pi
                                                                                .id
                                                                        ]
                                                                            ?.scheduledAt ??
                                                                        ''
                                                                    }
                                                                    onChange={e =>
                                                                        setComponentSchedules(
                                                                            prev => ({
                                                                                ...prev,
                                                                                [pi.id]:
                                                                                    {
                                                                                        ...prev[
                                                                                            pi
                                                                                                .id
                                                                                        ],
                                                                                        scheduledAt:
                                                                                            e
                                                                                                .target
                                                                                                .value,
                                                                                    },
                                                                            })
                                                                        )
                                                                    }
                                                                />
                                                                <label className="label cursor-pointer justify-start gap-1 py-0">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="checkbox checkbox-xs"
                                                                        checked={
                                                                            !!componentSchedules[
                                                                                pi
                                                                                    .id
                                                                            ]
                                                                                ?.arrivalOrder
                                                                        }
                                                                        onChange={e =>
                                                                            setComponentSchedules(
                                                                                prev => ({
                                                                                    ...prev,
                                                                                    [pi.id]:
                                                                                        {
                                                                                            ...prev[
                                                                                                pi
                                                                                                    .id
                                                                                            ],
                                                                                            arrivalOrder:
                                                                                                e
                                                                                                    .target
                                                                                                    .checked,
                                                                                        },
                                                                                })
                                                                            )
                                                                        }
                                                                    />
                                                                    <span className="label-text text-xs">
                                                                        Ordem de
                                                                        chegada
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        ) : (
                                                            <span className="text-base-content/40">
                                                                —
                                                            </span>
                                                        )}
                                                    </td>
                                                )}
                                                <td className="text-right font-semibold">
                                                    {/* Material, hospital e anestesista têm
                                                        o valor NA LINHA: é editável aqui,
                                                        senão o operador teria de remover e
                                                        adicionar de novo para corrigir. */}
                                                    {pi.item ? null : (
                                                        <input
                                                            type="text"
                                                            className="input input-bordered input-xs w-28 text-right"
                                                            placeholder="R$ 0,00"
                                                            value={formatCentsToDisplay(
                                                                pi.amount
                                                            )}
                                                            onChange={e => {
                                                                const cents =
                                                                    parseCurrencyToCents(
                                                                        currencyFormat(
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    )
                                                                setPackageItems(
                                                                    prev =>
                                                                        prev.map(
                                                                            (
                                                                                it,
                                                                                i
                                                                            ) =>
                                                                                i ===
                                                                                idx
                                                                                    ? {
                                                                                          ...it,
                                                                                          amount: cents,
                                                                                      }
                                                                                    : it
                                                                        )
                                                                )
                                                                // Fica pendente até o
                                                                // blur gravar — ou até o
                                                                // submit gravar por ele.
                                                                if (pi.id) {
                                                                    pendingItemPatches.current.set(
                                                                        pi.id,
                                                                        {
                                                                            amount: cents,
                                                                        }
                                                                    )
                                                                }
                                                            }}
                                                            // Grava ao sair do campo: a cada
                                                            // tecla seria uma requisição por
                                                            // dígito.
                                                            onBlur={() =>
                                                                patchPackageItem(
                                                                    idx,
                                                                    {
                                                                        amount:
                                                                            pi.amount ??
                                                                            null,
                                                                    }
                                                                )
                                                            }
                                                        />
                                                    )}
                                                    {pi.item &&
                                                    packageComponentCost(pi) >
                                                        0 ? (
                                                        new Intl.NumberFormat(
                                                            'pt-BR',
                                                            {
                                                                style: 'currency',
                                                                currency: 'BRL',
                                                            }
                                                        ).format(
                                                            packageComponentCost(
                                                                pi
                                                            ) / 100
                                                        )
                                                    ) : pi.item ? (
                                                        <span className="text-base-content/40 font-normal">
                                                            —
                                                        </span>
                                                    ) : null}
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-xs text-error"
                                                        onClick={async () => {
                                                            if (
                                                                isEdit &&
                                                                id &&
                                                                pi.id
                                                            ) {
                                                                const res =
                                                                    await removeCatalogPackageItem(
                                                                        parseInt(
                                                                            id
                                                                        ),
                                                                        pi.id
                                                                    )
                                                                if (
                                                                    !res.success
                                                                ) {
                                                                    toast.error(
                                                                        'Erro ao remover componente'
                                                                    )
                                                                    return
                                                                }
                                                            }
                                                            setPackageItems(
                                                                prev =>
                                                                    prev.filter(
                                                                        (
                                                                            _,
                                                                            i
                                                                        ) =>
                                                                            i !==
                                                                            idx
                                                                    )
                                                            )
                                                        }}
                                                    >
                                                        <MdDelete className="text-base" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <th
                                                colSpan={6}
                                                className="text-right"
                                            >
                                                Custo do pacote
                                            </th>
                                            <th className="text-right text-secondary">
                                                {new Intl.NumberFormat(
                                                    'pt-BR',
                                                    {
                                                        style: 'currency',
                                                        currency: 'BRL',
                                                    }
                                                ).format(
                                                    (packageComputedCost ??
                                                        packageItems.reduce(
                                                            (sum, pi) =>
                                                                sum +
                                                                packageComponentCost(
                                                                    pi
                                                                ),
                                                            0
                                                        )) / 100
                                                )}
                                            </th>
                                            <th></th>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}

                        {/* A execução no componente esconde as seções de parceiro e
                            profissional do pacote, mas o preço que já estava lá CONTINUA
                            valendo no agendamento (`resolveEffectivePrice` prioriza o
                            vínculo do parceiro sobre o preço da filial). Esconder sem
                            avisar deixaria um preço ativo fora do alcance do operador. */}
                        {packageHasComponentExecution &&
                            (partnerPricings.length > 0 ||
                                professionalPricings.length > 0) && (
                                <div className="alert alert-warning mt-4 text-sm">
                                    <MdInfo className="text-lg" />
                                    <span>
                                        Este pacote ainda tem preço de venda
                                        cadastrado por parceiro ou profissional
                                        ({partnerPricings.length} parceiro(s),{' '}
                                        {professionalPricings.length}{' '}
                                        profissional(is)). Esse preço continua
                                        valendo no agendamento e se sobrepõe ao
                                        preço da filial. Para revê-lo, limpe a
                                        execução dos componentes.
                                    </span>
                                </div>
                            )}

                        {/* Execução do componente selecionado.
                            ⚠️ Fica FORA da tabela de propósito: a tabela vive dentro de um
                            `overflow-x-auto`, que recorta qualquer dropdown absoluto — os
                            seletores de parceiro e profissional ficavam escondidos sob a
                            borda do container. */}
                        {executionComponent && (
                            <div className="mt-4 rounded-lg border border-primary/30 bg-base-200/40 p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <h3 className="font-semibold text-base-content">
                                            Quem executa:{' '}
                                            {executionComponent.item?.name}
                                        </h3>
                                        <p className="text-xs text-base-content/60">
                                            Compõe o custo do pacote. Cortesia
                                            não isenta o repasse.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        onClick={() =>
                                            setExecutionOpenIndex(null)
                                        }
                                    >
                                        Fechar
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <PartnerSelector
                                            value={
                                                executionComponent.partnerId ??
                                                null
                                            }
                                            size="sm"
                                            label="Parceiro que executa"
                                            placeholder={
                                                isHealth
                                                    ? 'Nenhum (a clínica executa)'
                                                    : 'Nenhum (a empresa executa)'
                                            }
                                            /* A lista vem do vínculo do próprio item: só
                                               quem faz este exame/procedimento aparece, e
                                               é dela que sai o custo do repasse. Passar
                                               `items` (e não `allowedIds`) evita depender
                                               de uma busca que pode não trazer o parceiro. */
                                            items={
                                                (
                                                    executionComponent.item
                                                        ?.partners || []
                                                )
                                                    .filter(p => p.partner)
                                                    .map(
                                                        p =>
                                                            ({
                                                                id: p.partnerId,
                                                                name:
                                                                    p.partner
                                                                        ?.name ??
                                                                    '',
                                                                tradingName:
                                                                    p.partner
                                                                        ?.tradingName ??
                                                                    undefined,
                                                            }) as Partner
                                                    ) as Partner[]
                                            }
                                            priceLabelById={Object.fromEntries(
                                                (
                                                    executionComponent.item
                                                        ?.partners || []
                                                ).map(p => [
                                                    p.partnerId,
                                                    formatCentsToDisplay(
                                                        p.price
                                                    ) || 'Sem custo',
                                                ])
                                            )}
                                            onChange={(partnerId, partner) =>
                                                patchPackageItem(
                                                    executionIndex,
                                                    {
                                                        partnerId,
                                                        partner: partner
                                                            ? {
                                                                  id: partner.id,
                                                                  name: partner.name,
                                                                  tradingName:
                                                                      partner.tradingName,
                                                              }
                                                            : null,
                                                    }
                                                )
                                            }
                                        />
                                        <p className="text-xs text-base-content/50 mt-1">
                                            {(
                                                executionComponent.item
                                                    ?.partners || []
                                            ).length === 0
                                                ? 'Nenhum parceiro executa este item. Vincule um parceiro no cadastro do item para poder escolhê-lo aqui.'
                                                : 'Recebe o custo do vínculo com este item — não um valor digitado aqui.'}
                                        </p>
                                    </div>

                                    {/* Sem "Profissionais" em Gestão de entidades, não há quem escolher. */}
                                    {canSeeProfessionals && (
                                    <>
                                    <div>
                                        <ProfessionalSearchSelector
                                            value={
                                                executionComponent.professionalId ??
                                                null
                                            }
                                            size="sm"
                                            label="Profissional que executa"
                                            placeholder="Nenhum"
                                            onChange={(
                                                professionalId,
                                                professional
                                            ) =>
                                                patchPackageItem(
                                                    executionIndex,
                                                    {
                                                        professionalId,
                                                        professional:
                                                            professional
                                                                ? {
                                                                      id: professional.id,
                                                                      name: professional.name,
                                                                  }
                                                                : null,
                                                        // Sem profissional não há valor a repassar.
                                                        ...(professionalId
                                                            ? {}
                                                            : {
                                                                  professionalAmount:
                                                                      null,
                                                              }),
                                                    }
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Valor do profissional
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            className="input input-bordered input-sm w-full"
                                            placeholder="R$ 0,00"
                                            disabled={
                                                !executionComponent.professionalId
                                            }
                                            value={formatCentsToDisplay(
                                                executionComponent.professionalAmount
                                            )}
                                            onChange={e =>
                                                setPackageItems(prev =>
                                                    prev.map((it, i) =>
                                                        i === executionIndex
                                                            ? {
                                                                  ...it,
                                                                  professionalAmount:
                                                                      parseCurrencyToCents(
                                                                          currencyFormat(
                                                                              e
                                                                                  .target
                                                                                  .value
                                                                          )
                                                                      ),
                                                              }
                                                            : it
                                                    )
                                                )
                                            }
                                            /* Grava ao sair do campo: gravar a cada tecla
                                               mandaria uma requisição por dígito. */
                                            onBlur={() =>
                                                patchPackageItem(
                                                    executionIndex,
                                                    {
                                                        professionalAmount:
                                                            executionComponent.professionalAmount ??
                                                            null,
                                                    }
                                                )
                                            }
                                        />
                                        <span className="text-xs text-base-content/50 mt-1">
                                            Vai integralmente para o
                                            profissional, multiplicado pela
                                            quantidade.
                                        </span>
                                    </div>
                                    </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Exames Relacionados - somente para PROCEDURE na saúde */}
                {isHealth && category === 'PROCEDURE' && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <MdScience className="text-xl text-info" />
                            <h2 className="text-lg font-semibold text-base-content">
                                Exames Relacionados
                            </h2>
                            {catalogExams.length > 0 && (
                                <span className="badge badge-sm badge-info">
                                    {catalogExams.length}
                                </span>
                            )}
                        </div>

                        {/* Busca de exames */}
                        <div className="relative mb-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Adicionar Exame
                                </span>
                            </label>
                            <div className="relative">
                                <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/50 text-lg pointer-events-none z-10" />
                                <input
                                    type="text"
                                    className="input input-bordered w-full pl-10"
                                    placeholder="Buscar exame por nome..."
                                    value={examSearch}
                                    onChange={async e => {
                                        setExamSearch(e.target.value)
                                        if (
                                            e.target.value.trim().length >= 2 &&
                                            user?.companyId
                                        ) {
                                            setLoadingExamSearch(true)
                                            try {
                                                const res =
                                                    await getCatalogsByCompany(
                                                        user.companyId,
                                                        {
                                                            category: 'EXAM',
                                                            search: e.target.value.trim(),
                                                            activeOnly: true,
                                                        }
                                                    )
                                                if (res.success) {
                                                    const existingIds = new Set(
                                                        catalogExams.map(
                                                            ce =>
                                                                ce.examCatalogId
                                                        )
                                                    )
                                                    setExamSearchResults(
                                                        (
                                                            res.result || []
                                                        ).filter(
                                                            c =>
                                                                !existingIds.has(
                                                                    c.id
                                                                )
                                                        )
                                                    )
                                                }
                                            } finally {
                                                setLoadingExamSearch(false)
                                            }
                                            setShowExamDropdown(true)
                                        } else {
                                            setShowExamDropdown(false)
                                        }
                                    }}
                                    onFocus={() => {
                                        if (examSearchResults.length > 0)
                                            setShowExamDropdown(true)
                                    }}
                                    onBlur={() =>
                                        setTimeout(
                                            () => setShowExamDropdown(false),
                                            200
                                        )
                                    }
                                />
                            </div>
                            {showExamDropdown && (
                                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {loadingExamSearch ? (
                                        <div className="px-4 py-3 text-sm text-base-content/50 text-center">
                                            <span className="loading loading-spinner loading-xs"></span>{' '}
                                            Buscando...
                                        </div>
                                    ) : examSearchResults.length === 0 ? (
                                        <div className="px-4 py-3 text-sm text-base-content/50 text-center">
                                            Nenhum exame encontrado
                                        </div>
                                    ) : (
                                        examSearchResults.map(exam => (
                                            <button
                                                key={exam.id}
                                                type="button"
                                                className="w-full text-left px-4 py-2 hover:bg-base-200 text-sm flex items-center justify-between"
                                                onMouseDown={async () => {
                                                    if (isEdit && id) {
                                                        const res =
                                                            await addCatalogExam(
                                                                parseInt(id),
                                                                {
                                                                    examCatalogId:
                                                                        exam.id,
                                                                }
                                                            )
                                                        if (res.success) {
                                                            setCatalogExams(
                                                                prev => [
                                                                    ...prev,
                                                                    res.result,
                                                                ]
                                                            )
                                                            toast.success(
                                                                'Exame adicionado'
                                                            )
                                                        } else {
                                                            toast.error(
                                                                res.error ||
                                                                    'Erro ao adicionar exame'
                                                            )
                                                        }
                                                    } else {
                                                        // Modo criação: adicionar localmente (será salvo junto com o catálogo)
                                                        const newItem: CatalogExam =
                                                            {
                                                                id: 0,
                                                                catalogId: 0,
                                                                examCatalogId:
                                                                    exam.id,
                                                                examCatalog: {
                                                                    id: exam.id,
                                                                    name: exam.name,
                                                                    category:
                                                                        exam.category,
                                                                    price: catalogBasePrice(
                                                                        exam,
                                                                        user?.companyId
                                                                    ),
                                                                    catalogType:
                                                                        exam.catalogType,
                                                                },
                                                                quantity: 1,
                                                                complimentary: false,
                                                                createdAt: '',
                                                                updatedAt: '',
                                                            }
                                                        setCatalogExams(
                                                            prev => [
                                                                ...prev,
                                                                newItem,
                                                            ]
                                                        )
                                                    }
                                                    setExamSearch('')
                                                    setShowExamDropdown(false)
                                                }}
                                            >
                                                <div>
                                                    <span className="font-medium">
                                                        {exam.name}
                                                    </span>
                                                    {exam.catalogType && (
                                                        <span className="text-xs text-base-content/50 ml-2">
                                                            {
                                                                exam.catalogType
                                                                    .name
                                                            }
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="text-xs font-semibold text-secondary">
                                                    {new Intl.NumberFormat(
                                                        'pt-BR',
                                                        {
                                                            style: 'currency',
                                                            currency: 'BRL',
                                                        }
                                                    ).format(
                                                        catalogBasePrice(
                                                            exam,
                                                            user?.companyId
                                                        ) / 100
                                                    )}
                                                </span>
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Lista de exames vinculados */}
                        {catalogExams.length === 0 ? (
                            <p className="text-sm text-base-content/50 text-center py-4">
                                Nenhum {exam} vinculado a este {order}
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Exame</th>
                                            <th>Tipo</th>
                                            <th className="text-right">
                                                Preço
                                            </th>
                                            <th className="w-20">Qtd</th>
                                            <th className="text-center">
                                                Cortesia
                                            </th>
                                            <th className="w-12"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {catalogExams.map((ce, idx) => (
                                            <tr
                                                key={ce.examCatalogId}
                                                className={
                                                    ce.complimentary
                                                        ? 'opacity-60'
                                                        : ''
                                                }
                                            >
                                                <td className="font-medium">
                                                    {ce.examCatalog.name}
                                                </td>
                                                <td>
                                                    <span className="badge badge-sm badge-ghost">
                                                        {ce.examCatalog
                                                            .catalogType
                                                            ?.name || '-'}
                                                    </span>
                                                </td>
                                                <td
                                                    className={`text-right ${ce.complimentary ? 'line-through text-base-content/40' : 'font-semibold text-secondary'}`}
                                                >
                                                    {new Intl.NumberFormat(
                                                        'pt-BR',
                                                        {
                                                            style: 'currency',
                                                            currency: 'BRL',
                                                        }
                                                    ).format(
                                                        ((ce.examCatalog
                                                            .sellingPrice ??
                                                            ce.examCatalog
                                                                .price) ||
                                                            0) / 100
                                                    )}
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        className="input input-bordered input-sm w-16"
                                                        value={ce.quantity}
                                                        onChange={async e => {
                                                            const qty =
                                                                parseInt(
                                                                    e.target
                                                                        .value
                                                                ) || 1
                                                            setCatalogExams(
                                                                prev =>
                                                                    prev.map(
                                                                        (
                                                                            item,
                                                                            i
                                                                        ) =>
                                                                            i ===
                                                                            idx
                                                                                ? {
                                                                                      ...item,
                                                                                      quantity:
                                                                                          qty,
                                                                                  }
                                                                                : item
                                                                    )
                                                            )
                                                            if (
                                                                isEdit &&
                                                                id &&
                                                                ce.id
                                                            ) {
                                                                await updateCatalogExam(
                                                                    parseInt(
                                                                        id
                                                                    ),
                                                                    ce.examCatalogId,
                                                                    {
                                                                        quantity:
                                                                            qty,
                                                                    }
                                                                )
                                                            }
                                                        }}
                                                    />
                                                </td>
                                                <td className="text-center">
                                                    <input
                                                        type="checkbox"
                                                        className="toggle toggle-xs toggle-info"
                                                        checked={
                                                            ce.complimentary
                                                        }
                                                        onChange={async () => {
                                                            const newVal =
                                                                !ce.complimentary
                                                            setCatalogExams(
                                                                prev =>
                                                                    prev.map(
                                                                        (
                                                                            item,
                                                                            i
                                                                        ) =>
                                                                            i ===
                                                                            idx
                                                                                ? {
                                                                                      ...item,
                                                                                      complimentary:
                                                                                          newVal,
                                                                                  }
                                                                                : item
                                                                    )
                                                            )
                                                            if (
                                                                isEdit &&
                                                                id &&
                                                                ce.id
                                                            ) {
                                                                await updateCatalogExam(
                                                                    parseInt(
                                                                        id
                                                                    ),
                                                                    ce.examCatalogId,
                                                                    {
                                                                        complimentary:
                                                                            newVal,
                                                                    }
                                                                )
                                                            }
                                                        }}
                                                        title="Marcar como cortesia (sem custo)"
                                                    />
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-xs text-error"
                                                        onClick={async () => {
                                                            if (
                                                                isEdit &&
                                                                id &&
                                                                ce.id
                                                            ) {
                                                                const res =
                                                                    await removeCatalogExam(
                                                                        parseInt(
                                                                            id
                                                                        ),
                                                                        ce.examCatalogId
                                                                    )
                                                                if (
                                                                    !res.success
                                                                ) {
                                                                    toast.error(
                                                                        'Erro ao remover exame'
                                                                    )
                                                                    return
                                                                }
                                                            }
                                                            setCatalogExams(
                                                                prev =>
                                                                    prev.filter(
                                                                        (
                                                                            _,
                                                                            i
                                                                        ) =>
                                                                            i !==
                                                                            idx
                                                                    )
                                                            )
                                                        }}
                                                    >
                                                        <MdDelete className="text-base" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Parceiros.
                    ⚠️ Sai da tela quando algum componente já diz quem executa: nesse caso a
                    fonte é o componente, e manter esta seção seria oferecer dois lugares
                    para dizer a mesma coisa. O pacote auto-contido (sem componentes)
                    continua exibindo — é aqui que mora o preço do bundle legado.
                    Também sai para quem não tem "Parceiros" em Gestão de entidades,
                    salvo quando o item está sendo criado já preso a um parceiro. */}
                {!packageHasComponentExecution &&
                    (canSeePartners || lockPartnerSection) && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-base-content">
                                    {isPackage
                                        ? 'Parceiros — preço de venda do pacote'
                                        : 'Parceiros'}
                                </h2>
                                {isPackage && (
                                    <p className="text-sm text-base-content/60">
                                        Por quanto este parceiro vende o pacote.
                                        Para dizer quem executa cada item, use
                                        Componentes do Pacote.
                                    </p>
                                )}
                            </div>

                            {/* Só na variação: ela herda os vínculos do pai e costuma
                            precisar de uma lista enxuta. Travado (lockPartnerSection)
                            quando o item nasce atrelado ao parceiro da aba. */}
                            {isModal &&
                                !lockPartnerSection &&
                                partnerPricings.length > 0 && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-ghost text-error"
                                        disabled={removingItem}
                                        onClick={() =>
                                            setConfirmRemove({
                                                type: 'all-partners',
                                                index: -1,
                                                name: `${partnerPricings.length} parceiro(s)`,
                                            })
                                        }
                                    >
                                        Remover todos
                                    </button>
                                )}
                        </div>

                        {/* Adicionar parceiro — oculto quando a seção está travada
                        (já estamos no parceiro dono da aba). */}
                        {!lockPartnerSection && (
                            <div className="mb-4">
                                <PartnerSelector
                                    key={partnerPricings.length}
                                    value={null}
                                    onChange={(partnerId, partner) => {
                                        if (partnerId && partner) {
                                            handleAddPartnerFromSelector(
                                                partnerId,
                                                partner
                                            )
                                        }
                                    }}
                                    label="Adicionar Parceiro"
                                    placeholder="Buscar parceiro..."
                                />
                            </div>
                        )}

                        {/* Lista de parceiros */}
                        {partnerPricings.length === 0 ? (
                            <p className="text-sm text-base-content/50 text-center py-4">
                                Nenhum parceiro vinculado
                            </p>
                        ) : (
                            <div className="flex flex-col gap-4">
                                {partnerPricings.map((pp, index) => (
                                    <div
                                        key={pp.partnerId}
                                        className="border border-base-300 rounded-lg p-4"
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex flex-col">
                                                <span className="font-medium text-base-content">
                                                    {pp.partnerName}
                                                </span>
                                                {pp.partnerLocation && (
                                                    <span className="text-xs text-base-content/50 flex items-center gap-1">
                                                        <MdLocationOn className="text-sm shrink-0" />
                                                        {pp.partnerLocation}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {isEdit && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-sm gap-1"
                                                        onClick={() =>
                                                            handleSavePartner(
                                                                index
                                                            )
                                                        }
                                                    >
                                                        <MdSave className="text-base" />
                                                        Salvar
                                                    </button>
                                                )}
                                                {!lockPartnerSection && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-sm text-error"
                                                        onClick={() =>
                                                            setConfirmRemove({
                                                                type: 'partner',
                                                                index,
                                                                name: pp.partnerName,
                                                            })
                                                        }
                                                    >
                                                        <MdDelete className="text-lg" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                            <div className="form-control">
                                                <label className="label py-1">
                                                    <span className="label-text text-xs">
                                                        Preço de Custo
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered input-sm w-full"
                                                    placeholder="R$ 0,00"
                                                    value={pp.price}
                                                    onChange={e =>
                                                        handleSubPriceChange(
                                                            partnerPricings,
                                                            setPartnerPricings,
                                                            index,
                                                            'price',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="form-control">
                                                <label className="label py-1">
                                                    <span className="label-text text-xs">
                                                        Preço de Venda
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered input-sm w-full"
                                                    placeholder="R$ 0,00"
                                                    value={pp.sellingPrice}
                                                    onChange={e =>
                                                        handleSubPriceChange(
                                                            partnerPricings,
                                                            setPartnerPricings,
                                                            index,
                                                            'sellingPrice',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="form-control">
                                                <label className="label py-1">
                                                    <span className="label-text text-xs">
                                                        Markup (%)
                                                    </span>
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className="input input-bordered input-sm w-full"
                                                    placeholder="0,00"
                                                    value={
                                                        pp.markupPercentage ||
                                                        ''
                                                    }
                                                    onChange={e =>
                                                        handleSubPriceChange(
                                                            partnerPricings,
                                                            setPartnerPricings,
                                                            index,
                                                            'markupPercentage',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="form-control">
                                                <label className="label py-1">
                                                    <span className="label-text text-xs">
                                                        Desconto Máximo (%)
                                                    </span>
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    max="100"
                                                    className="input input-bordered input-sm w-full"
                                                    placeholder="0"
                                                    value={pp.maxDiscount || ''}
                                                    onChange={e => {
                                                        const updated = [
                                                            ...partnerPricings,
                                                        ]
                                                        updated[index] = {
                                                            ...updated[index],
                                                            maxDiscount:
                                                                e.target.value,
                                                        }
                                                        setPartnerPricings(
                                                            updated
                                                        )
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-control mt-3">
                                            <label className="label py-1">
                                                <span className="label-text text-xs">
                                                    Observações
                                                </span>
                                            </label>
                                            <textarea
                                                className="textarea textarea-bordered textarea-sm w-full"
                                                placeholder="Observações sobre o parceiro..."
                                                rows={2}
                                                value={pp.observations}
                                                onChange={e => {
                                                    const updated = [
                                                        ...partnerPricings,
                                                    ]
                                                    updated[index] = {
                                                        ...updated[index],
                                                        observations:
                                                            e.target.value,
                                                    }
                                                    setPartnerPricings(updated)
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Profissionais. Mesma regra da seção de parceiros: sai quando o
                    componente já diz quem executa. E sai para quem não tem
                    "Profissionais" em Gestão de entidades, salvo quando o item está
                    sendo criado já preso a um profissional. */}
                {!packageHasComponentExecution &&
                    (canSeeProfessionals || lockProfessionalSection) && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h2 className="text-lg font-semibold text-base-content">
                                    {isPackage
                                        ? 'Profissionais — preço de venda do pacote'
                                        : 'Profissionais'}
                                </h2>
                                {isPackage && (
                                    <p className="text-sm text-base-content/60">
                                        Por quanto o pacote é vendido com este
                                        profissional. O valor que ele recebe por
                                        componente está em Componentes do
                                        Pacote.
                                    </p>
                                )}
                            </div>
                            {isModal &&
                                !lockProfessionalSection &&
                                professionalPricings.length > 0 && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-ghost text-error"
                                        disabled={removingItem}
                                        onClick={() =>
                                            setConfirmRemove({
                                                type: 'all-professionals',
                                                index: -1,
                                                name: `${professionalPricings.length} profissional(is)`,
                                            })
                                        }
                                    >
                                        Remover todos
                                    </button>
                                )}
                        </div>

                        {/* Adicionar profissional — oculto quando a seção está travada. */}
                        {!lockProfessionalSection && (
                            <div className="mb-4">
                                <ProfessionalSearchSelector
                                    key={professionalPricings.length}
                                    value={null}
                                    onChange={(
                                        professionalId,
                                        professional
                                    ) => {
                                        if (professionalId && professional) {
                                            handleAddProfessionalFromSelector(
                                                professionalId,
                                                professional
                                            )
                                        }
                                    }}
                                    label="Adicionar Profissional"
                                    placeholder="Buscar profissional..."
                                />
                            </div>
                        )}

                        {/* Lista de profissionais */}
                        {professionalPricings.length === 0 ? (
                            <p className="text-sm text-base-content/50 text-center py-4">
                                Nenhum profissional vinculado
                            </p>
                        ) : (
                            <div className="flex flex-col gap-4">
                                {professionalPricings.map((pp, index) => (
                                    <div
                                        key={pp.professionalId}
                                        className="border border-base-300 rounded-lg p-4"
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="font-medium text-base-content">
                                                {pp.professionalName}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                {isEdit && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-sm gap-1"
                                                        onClick={() =>
                                                            handleSaveProfessional(
                                                                index
                                                            )
                                                        }
                                                    >
                                                        <MdSave className="text-base" />
                                                        Salvar
                                                    </button>
                                                )}
                                                {!lockProfessionalSection && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-sm text-error"
                                                        onClick={() =>
                                                            setConfirmRemove({
                                                                type: 'professional',
                                                                index,
                                                                name: pp.professionalName,
                                                            })
                                                        }
                                                    >
                                                        <MdDelete className="text-lg" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                            <div className="form-control">
                                                <label className="label py-1">
                                                    <span className="label-text text-xs">
                                                        Preço de Custo
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered input-sm w-full"
                                                    placeholder="R$ 0,00"
                                                    value={pp.price}
                                                    onChange={e =>
                                                        handleSubPriceChange(
                                                            professionalPricings,
                                                            setProfessionalPricings,
                                                            index,
                                                            'price',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="form-control">
                                                <label className="label py-1">
                                                    <span className="label-text text-xs">
                                                        Preço de Venda
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered input-sm w-full"
                                                    placeholder="R$ 0,00"
                                                    value={pp.sellingPrice}
                                                    onChange={e =>
                                                        handleSubPriceChange(
                                                            professionalPricings,
                                                            setProfessionalPricings,
                                                            index,
                                                            'sellingPrice',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="form-control">
                                                <label className="label py-1">
                                                    <span className="label-text text-xs">
                                                        Markup (%)
                                                    </span>
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className="input input-bordered input-sm w-full"
                                                    placeholder="0,00"
                                                    value={
                                                        pp.markupPercentage ||
                                                        ''
                                                    }
                                                    onChange={e =>
                                                        handleSubPriceChange(
                                                            professionalPricings,
                                                            setProfessionalPricings,
                                                            index,
                                                            'markupPercentage',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="form-control">
                                                <label className="label py-1">
                                                    <span className="label-text text-xs">
                                                        Desconto Máximo (%)
                                                    </span>
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    max="100"
                                                    className="input input-bordered input-sm w-full"
                                                    placeholder="0"
                                                    value={pp.maxDiscount || ''}
                                                    onChange={e => {
                                                        const updated = [
                                                            ...professionalPricings,
                                                        ]
                                                        updated[index] = {
                                                            ...updated[index],
                                                            maxDiscount:
                                                                e.target.value,
                                                        }
                                                        setProfessionalPricings(
                                                            updated
                                                        )
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Variações (novo modelo: filhas Catalog editadas via modal) */}
                {showVariationsSection && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-1">
                            <h2 className="text-lg font-semibold text-base-content">
                                Variações
                            </h2>
                            {/* Item novo em modal (aba de entidade) não ganha o botão: o
                                modal fecha ao salvar e não há página para onde ir. */}
                            {(isEdit || !isModal) && (
                                <div className="flex items-center gap-2">
                                    {isEdit && isProduct && user?.companyId && (
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-sm gap-1"
                                            onClick={() => setCombinationsModalOpen(true)}
                                        >
                                            <MdAutoAwesome className="text-lg" />
                                            Gerar combinações
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm gap-1"
                                        disabled={loading}
                                        title={
                                            isEdit
                                                ? undefined
                                                : 'Salva o item e abre o cadastro da variação'
                                        }
                                        onClick={() => {
                                            if (isEdit) {
                                                setEditingVariationId(null)
                                                setVariationModalOpen(true)
                                                return
                                            }
                                            openVariationAfterSaveRef.current = true
                                            formRef.current?.requestSubmit()
                                            // Formulário inválido não dispara o submit: o
                                            // ref não pode sobrar para o próximo "Salvar".
                                            openVariationAfterSaveRef.current = false
                                        }}
                                    >
                                        <MdAdd className="text-lg" />
                                        Adicionar variação
                                    </button>
                                </div>
                            )}
                        </div>
                        <p className="text-sm text-base-content/60 mb-4">
                            {isProduct ? (
                                <>
                                    Cada variação (cor, tamanho, sabor) é um
                                    item filho com preço, SKU e estoque
                                    próprios. Ao ser escolhida na venda,
                                    substitui/soma ao preço base do produto.
                                </>
                            ) : (
                                <>
                                    Cada variação{variationHint} é um item filho
                                    com preço próprio — inclusive por parceiro e
                                    profissional. Ao ser escolhida no
                                    agendamento, substitui/soma ao preço base do
                                    item.
                                </>
                            )}
                        </p>
                        {!isEdit ? (
                            <p className="text-sm text-base-content/50 text-center py-4">
                                Ao adicionar a primeira variação, o item é salvo
                                e o cadastro da variação abre em seguida.
                            </p>
                        ) : variations.length === 0 ? (
                            <p className="text-sm text-base-content/50 text-center py-4">
                                Nenhuma variação cadastrada
                            </p>
                        ) : (
                            <div className="flex flex-col gap-2">
                                {variations.map(v => (
                                    <div
                                        key={v.savedId}
                                        className="flex items-center justify-between border border-base-300 rounded-lg px-4 py-2 bg-base-200/30"
                                    >
                                        <div>
                                            <span className="font-medium">
                                                {v.name}
                                            </span>
                                            {v.tussCode && (
                                                <span className="ml-2 text-xs text-base-content/50">
                                                    TUSS {v.tussCode}
                                                </span>
                                            )}
                                            {!v.active && (
                                                <span className="ml-2 badge badge-ghost badge-sm">
                                                    inativa
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs"
                                                onClick={() => {
                                                    setEditingVariationId(
                                                        v.savedId ?? null
                                                    )
                                                    setVariationModalOpen(true)
                                                }}
                                            >
                                                Editar
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs text-error"
                                                onClick={async () => {
                                                    if (v.savedId && id) {
                                                        await removeCatalogVariation(
                                                            parseInt(id),
                                                            v.savedId
                                                        )
                                                        await reloadVariations()
                                                    }
                                                }}
                                            >
                                                Remover
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Na CRIAÇÃO não há o que vender: a venda chama POST /orders com o
                    id do pacote, e os componentes e o preço precisam estar gravados. Em
                    vez de esconder o bloco sem explicação, diz o que falta — e ao salvar
                    a tela continua aberta, já em edição, com o bloco de venda no lugar. */}
                {isPackage && !isEdit && (
                    <div className="bg-base-100 rounded-xl border border-secondary/30 shadow-sm p-6">
                        <div className="flex items-center gap-2 mb-1">
                            <MdSell className="text-xl text-secondary" />
                            <h2 className="text-lg font-semibold text-base-content">
                                Vender este pacote
                            </h2>
                        </div>
                        <p className="text-sm text-base-content/60">
                            Disponível assim que o pacote for salvo. Ao salvar,
                            você continua nesta tela e o bloco de venda aparece
                            aqui.
                        </p>
                    </div>
                )}

                {/* VENDA DO PACOTE.
                    O pacote não é agendado: é vendido aqui mesmo. Este bloco é o único que
                    grava fora do catálogo — tudo aqui vira o ATENDIMENTO do paciente, não o
                    modelo do pacote. Misturar os dois faria a data de um paciente virar
                    padrão para todo mundo que comprasse o pacote. */}
                {isPackage && isEdit && (
                    <div
                        id="sale-block"
                        className={`bg-base-100 rounded-xl border shadow-sm p-6 ${
                            sellingMode
                                ? 'border-secondary ring-2 ring-secondary/20'
                                : 'border-secondary/30'
                        }`}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <MdSell className="text-xl text-secondary" />
                            <h2 className="text-lg font-semibold text-base-content">
                                Vender este pacote
                            </h2>
                        </div>
                        <p className="text-sm text-base-content/60 mb-4">
                            Gera o orçamento do {clientLabel} e leva para o
                            atendimento, onde se aprova — com cobrança, ou em
                            modo flex. A data de cada componente é definida na
                            lista de componentes acima.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="form-control md:col-span-2">
                                <ClientSelector
                                    value={saleClientId}
                                    label={ClientLabel}
                                    onChange={clientId =>
                                        setSaleClientId(clientId)
                                    }
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Desconto (%)
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    className="input input-bordered w-full"
                                    placeholder="0"
                                    value={saleDiscount}
                                    onChange={e =>
                                        setSaleDiscount(e.target.value)
                                    }
                                />
                            </div>

                            {/* Custo, venda, markup e lucro — o mesmo trio do orçamento de
                                procedimento. Sem eles o operador vende no escuro: o custo
                                do pacote é a soma dos repasses, e vender abaixo dele dá
                                prejuízo sem nenhum aviso. */}
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Custo (componentes)
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered input-disabled w-full bg-base-200"
                                    readOnly
                                    value={formatCentsToDisplay(
                                        packageComputedCost
                                    )}
                                />
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Valor de venda
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="R$ 0,00"
                                    value={saleOverridePrice}
                                    onChange={e => {
                                        const formatted = currencyFormat(
                                            e.target.value,
                                            true
                                        )
                                        setSaleOverridePrice(formatted)
                                        // Grava no PREÇO DO PACOTE: é o que o botão
                                        // "Salvar e sair" persiste. Sem isto o valor só
                                        // existia no estado da tela e sumia ao recarregar.
                                        setFormData(prev => ({
                                            ...prev,
                                            sellingPrice: formatted,
                                        }))
                                        // Markup acompanha a venda: são o mesmo número
                                        // dito de duas formas.
                                        setSaleMarkup(
                                            recalcMarkupFromPrices(
                                                packageComputedCost ?? 0,
                                                parseCurrencyToCents(formatted)
                                            )
                                        )
                                    }}
                                />
                                <span className="text-xs text-base-content/50 mt-1">
                                    É o valor FINAL: o desconto não é reaplicado
                                    sobre ele.
                                </span>
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Markup (%)
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="input input-bordered w-full"
                                    placeholder="0"
                                    value={saleMarkup}
                                    onChange={e => {
                                        setSaleMarkup(e.target.value)
                                        // O markup é gravado SEMPRE. Antes ele só entrava
                                        // no formulário quando havia custo composto — e
                                        // pacote sem custo nos componentes (o caso comum
                                        // de quem ainda não preencheu) perdia o valor
                                        // digitado sem nenhum aviso.
                                        setFormData(prev => ({
                                            ...prev,
                                            markupPercentage: e.target.value,
                                        }))
                                        const m = parseFloat(e.target.value)
                                        if (!isNaN(m) && packageComputedCost) {
                                            // Com custo conhecido, a venda acompanha o
                                            // markup: são o mesmo número dito de dois
                                            // jeitos.
                                            const venda =
                                                recalcSellingPriceFromMarkup(
                                                    packageComputedCost,
                                                    m
                                                )
                                            setSaleOverridePrice(venda)
                                            setFormData(prev => ({
                                                ...prev,
                                                sellingPrice: venda,
                                            }))
                                        }
                                    }}
                                />
                                <span className="text-xs text-base-content/50 mt-1">
                                    Venda = custo + markup.
                                </span>
                            </div>

                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Lucro
                                    </span>
                                </label>
                                {(() => {
                                    const venda = parseCurrencyToCents(
                                        saleOverridePrice || '0'
                                    )
                                    const lucro =
                                        venda - (packageComputedCost ?? 0)
                                    return (
                                        <input
                                            type="text"
                                            readOnly
                                            className={`input input-bordered input-disabled w-full ${
                                                lucro < 0
                                                    ? 'text-error'
                                                    : 'text-success'
                                            }`}
                                            value={
                                                venda > 0
                                                    ? new Intl.NumberFormat(
                                                          'pt-BR',
                                                          {
                                                              style: 'currency',
                                                              currency: 'BRL',
                                                          }
                                                      ).format(lucro / 100)
                                                    : ''
                                            }
                                        />
                                    )
                                })()}
                                {/* Percentual do lucro é a MARGEM (sobre a venda). O
                                    campo "Markup (%)" acima é sobre o CUSTO — mostrar
                                    só ele fazia ler 100% de markup como 100% de lucro,
                                    quando o lucro real ali é metade da venda. */}
                                {(() => {
                                    const venda = parseCurrencyToCents(
                                        saleOverridePrice || '0'
                                    )
                                    const margem = profitMargin(
                                        venda,
                                        venda - (packageComputedCost ?? 0)
                                    )
                                    return margem != null ? (
                                        <span className="text-xs text-base-content/50 mt-1">
                                            {margem}% de margem sobre a venda
                                        </span>
                                    ) : null
                                })()}
                            </div>

                            {/* Margem negativa é o erro que mais custa: o pacote sai mais
                                barato do que os repasses que ele mesmo gera. */}
                            {packageComputedCost != null &&
                                packageComputedCost > 0 &&
                                parseCurrencyToCents(saleOverridePrice || '0') <
                                    packageComputedCost && (
                                    <div className="md:col-span-3 alert alert-warning text-sm">
                                        <MdInfo className="text-lg" />
                                        <span>
                                            O valor de venda está abaixo do
                                            custo dos componentes.
                                        </span>
                                    </div>
                                )}
                        </div>

                        {/* Requisitos da venda, ditos ANTES de o operador tentar: botão
                            desabilitado sem explicação é o que faz o operador achar que a
                            tela travou. */}
                        {(() => {
                            const faltando: string[] = []
                            if (!saleClientId) faltando.push(`o ${clientLabel}`)
                            if (
                                parseCurrencyToCents(
                                    saleOverridePrice || '0'
                                ) <= 0
                            )
                                faltando.push('o valor de venda')
                            if (packageItems.length === 0)
                                faltando.push('ao menos um componente')
                            // Componente clínico sem executante = serviço prestado sem
                            // ninguém a receber. O backend recusa; aqui o operador vê
                            // QUAL componente falta, antes de tentar.
                            const semExecutante = packageItems
                                .filter(
                                    pi =>
                                        (pi.kind ?? 'ITEM') === 'ITEM' &&
                                        !pi.partnerId &&
                                        !pi.professionalId
                                )
                                .map(pi => pi.item?.name ?? 'componente')
                            if (semExecutante.length > 0)
                                faltando.push(
                                    `quem executa ${semExecutante.join(', ')}`
                                )
                            if (faltando.length === 0) return null
                            return (
                                <p className="text-sm text-warning mt-4">
                                    Para finalizar a venda, informe{' '}
                                    {faltando.join(', ')}.
                                </p>
                            )
                        })()}

                        <div className="flex justify-end gap-3 mt-4">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() =>
                                    isModal && props.onCancel
                                        ? props.onCancel()
                                        : navigate('/registrations/catalog')
                                }
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-secondary gap-2"
                                disabled={loading}
                                title="Grava o pacote e volta — dá para continuar depois"
                            >
                                <MdSave className="text-lg" />
                                Salvar e sair
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={
                                    !saleClientId ||
                                    parseCurrencyToCents(
                                        saleOverridePrice || '0'
                                    ) <= 0 ||
                                    packageItems.length === 0 ||
                                    // Mesma regra do backend: componente clínico sem
                                    // executante não pode ser vendido.
                                    packageItems.some(
                                        pi =>
                                            (pi.kind ?? 'ITEM') === 'ITEM' &&
                                            !pi.partnerId &&
                                            !pi.professionalId
                                    ) ||
                                    selling
                                }
                                onClick={handleSellPackage}
                            >
                                {selling ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    <MdSell className="text-lg" />
                                )}
                                Gerar orçamento
                            </button>
                        </div>
                    </div>
                )}

                {/* Fotos e vídeos. Recolhida por padrão e por último: é opcional, e a
                    maioria dos itens não tem galeria — abrir espaço para ela no meio do
                    formulário empurraria preço e parceiros para baixo sem necessidade. */}
                <CatalogMediaSection
                    ref={mediaSectionRef}
                    catalogId={isEdit && id ? parseInt(id) : null}
                    subjectLabel={isProduct ? 'produto' : 'item'}
                />

                {/* Botões de ação.
                    Somem quando o bloco de VENDA está na tela: ele já traz Cancelar,
                    Salvar e sair e Finalizar venda. Duas barras de ação na mesma página
                    fazem o operador perguntar qual das duas salva. */}
                {!(isPackage && isEdit) && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() =>
                                    isModal && props.onCancel
                                        ? props.onCancel()
                                        : navigate('/registrations/catalog')
                                }
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-secondary gap-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="loading loading-spinner loading-sm"></span>
                                        Salvando...
                                    </>
                                ) : (
                                    <>
                                        <MdSave className="text-xl" />
                                        {isEdit ? 'Atualizar' : 'Criar'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </form>

            {/*
                Modal de variação: reusa este form em modo variação-filha.

                Fica FORA do <form> acima de propósito. Aninhado ali dentro, o form do
                filho vira descendente do form do pai: o evento `submit` borbulha, e
                salvar a variação (ou qualquer botão sem `type` do filho) disparava o
                `handleSubmit` do pai — que atualizava o item pai inteiro e navegava
                para a listagem, fechando a tela.
            */}
            {combinationsModalOpen && id && user?.companyId && (
                <VariationCombinationsModal
                    parentId={parseInt(id)}
                    companyId={user.companyId}
                    existingNames={variations.map(v => v.name)}
                    onClose={() => setCombinationsModalOpen(false)}
                    onCreated={reloadVariations}
                />
            )}

            {variationModalOpen && id && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-4xl max-h-[90vh] overflow-y-auto">
                        <CatalogFormPage
                            parentId={parseInt(id)}
                            modalCategory={category}
                            modalId={editingVariationId ?? undefined}
                            onSuccess={async () => {
                                setVariationModalOpen(false)
                                await reloadVariations()
                            }}
                            onCancel={() => setVariationModalOpen(false)}
                        />
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setVariationModalOpen(false)}
                    />
                </div>
            )}

            {/* Modal de confirmação para remoção */}
            <ConfirmModal
                isOpen={!!confirmRemove}
                onClose={() => setConfirmRemove(null)}
                onConfirm={() => {
                    if (!confirmRemove) return
                    if (confirmRemove.type === 'partner') {
                        handleRemovePartner(confirmRemove.index)
                    } else if (confirmRemove.type === 'professional') {
                        handleRemoveProfessional(confirmRemove.index)
                    } else if (confirmRemove.type === 'all-partners') {
                        handleRemoveAllPartners()
                    } else if (confirmRemove.type === 'all-professionals') {
                        handleRemoveAllProfessionals()
                    } else {
                        // Variação: removida direto na lista (via modal). No-op aqui.
                        setConfirmRemove(null)
                    }
                }}
                title={
                    confirmRemove?.type === 'partner'
                        ? 'Remover Parceiro'
                        : confirmRemove?.type === 'professional'
                          ? 'Remover Profissional'
                          : confirmRemove?.type === 'all-partners'
                            ? 'Remover todos os parceiros'
                            : confirmRemove?.type === 'all-professionals'
                              ? 'Remover todos os profissionais'
                              : 'Remover Variação'
                }
                message={
                    confirmRemove?.type === 'all-partners' ||
                    confirmRemove?.type === 'all-professionals'
                        ? `Deseja remover ${confirmRemove.name} desta variação? A ação não pode ser desfeita.`
                        : `Deseja realmente remover "${confirmRemove?.name}"?`
                }
                loading={removingItem}
                confirmButtonText="Remover"
                confirmButtonClass="btn-error"
            />

            {showCostCalculator && (
                <CostCalculatorModal
                    volumeLabel={
                        category === 'CONSULTATION' || category === 'TELECONSULTATION'
                            ? 'consultas'
                            : category === 'EXAM'
                            ? 'exames'
                            : 'atendimentos'
                    }
                    onClose={() => setShowCostCalculator(false)}
                    onApply={cents =>
                        // Passa pelo `handlePriceChange` de propósito: é ele que
                        // recalcula venda e markup a partir do custo novo.
                        handlePriceChange(formatCentsToDisplay(cents))
                    }
                />
            )}
        </div>
    )
}
